package apap.oneverandah.i18n

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import apap.oneverandah.model.Language
import kotlinx.browser.window

class StudyTestStrings(
    val tab: String,
    val sceneLoading: String,
    val sceneError: String,
    val audioError: String,
    val retry: String,
    val listenHint: (name: String) -> String,
    val speakHint: String,
    val playLine: String,
    val beatProgress: (current: Int, total: Int) -> String,
    val outlineTitle: String,
    val start: String,
    val inputPlaceholder: String,
    val next: String,
    val finish: String,
    val scoring: String,
    val scoreError: String,
    val resultTitle: String,
    val gradeLabel: String,
    val listeningLabel: String,
    val speakingLabel: String,
    val retake: String,
    val levelBeginner: String,
    val levelElementary: String,
    val levelIntermediate: String,
    val levelAdvanced: String,
    val levelNative: String,
    val resultCheer: (level: Int) -> String,
)

/**
 * Bundle of every user-facing UI string (i.e. app "chrome": titles, buttons,
 * section headers). Catalog content (category/service names) is localized on
 * the data itself via [apap.oneverandah.model.LocalizedText].
 */
data class UiStrings(
    val appTitle: String,
    val serviceDetails: String,
    val homePrompt: String,
    val reviews: (count: Int) -> String,
    val sectionBookingType: String,
    val sectionDuration: String,
    val sectionTargetArea: String,
    val sectionComments: String,
    val sectionReviews: String,
    val minutes: (minutes: Int) -> String,
    val hours: (hours: String) -> String,
    val from: String,
    val total: String,
    val priceSummary: String,
    val tapToSelect: String,
    val bookThisService: String,
    val booking: String,
    val bookingSuccessTitle: String,
    val bookingSuccessMessage: String,
    val done: String,
    val back: String,
    val menu: String,
    val languageName: String,
    /** Aria-label / tooltip: switch to desktop layout. */
    val layoutSwitchToDesktop: String,
    /** Aria-label / tooltip: switch to mobile (phone frame) layout. */
    val layoutSwitchToMobile: String,
    val layoutFullscreen: String,
    val layoutExitFullscreen: String,
    // Navigation drawer
    val menuTitle: String,
    val menuHome: String,
    val menuAbout: String,
    val menuLanguage: String,
    val menuHelp: String,
    val menuForAdults: String,
    val menuForKids: String,
    val close: String,
    val bookingDialogTitle: String,
    val zaloLabel: String,
    val zaloPlaceholder: String,
    val zaloError: String,
    val messageLabel: String,
    val messagePlaceholder: String,
    val confirmBooking: String,
    val confirm: String,
    val male: String,
    val female: String,
    val viewFront: String,
    val viewBack: String,
    val aboutText: String,
    val aboutExclusive: String,
    val aboutTrusted: String,
    val aboutFree: String,
    val helpSuggestFeature: String,
    val helpSuggestFeatureSubtitle: String,
    val helpFeedback: String,
    val helpFeedbackSubtitle: String,
    val helpReportProblem: String,
    val helpReportProblemSubtitle: String,
    val helpSubmit: String,
    val helpPlaceholder: String,
    val helpErrorRequired: String,
    val helpSuccessTitle: String,
    val helpSuccessMessage: String,
    val helpListService: String,
    val helpListServiceSubtitle: String,
    val listServiceDialogTitle: String,
    val serviceDescriptionLabel: String,
    val serviceDescriptionPlaceholder: String,
    val serviceDescriptionError: String,
    val yourNameLabel: String,
    val yourSexLabel: String,
    val yourNamePlaceholder: String,
    val yourNameError: String,
    val blockAddressPlaceholder: String,
    val blockAddressError: String,
    val listServiceSuccessTitle: String,
    val listServiceSuccessMessage: String,
    val searchPlaceholder: String,
    val noResults: String,
    val providedBy: String,
    val providerBlockLabel: String,
    val providerPhoneLabel: String,
    val providerSexLabel: String,
    val providerAboutLabel: String,
    val providerSkillsLabel: String,
    val providerSkillLevelLabel: String,
    val providerServicesLabel: String,
    val providerAllServicesLabel: String,
    val providerDetails: String,
    val whenLabel: String,
    val whenNow: String,
    val whenToday: String,
    val whenTomorrow: String,
    val whenLater: String,
    // Offer toolbar / share
    val save: String,
    val saved: String,
    val share: String,
    val savedToast: String,
    val removedToast: String,
    val copyLink: String,
    val copied: String,
    val copiedToast: String,
    // Home "Pinned & Recent" section
    val pinnedRecentTitle: String,
    val popularTitle: String,
    val clearAll: String,
    val clearAllConfirm: String,
    val pinnedEmpty: String,
    /** Home browse toggle: services feed (default). */
    val homeViewServices: String,
    /** Home browse toggle: category grid (legacy layout). */
    val homeViewCategories: String,
    val homeLatestStories: String,
    val homeLatestStudySessions: String,
    val homeLatestChallenges: String,
    val homeLatestLocations: String,
    val homeLatestClubs: String,
    val homeLatestMeets: String,
    /** Opens the full list for a home "Latest..." preview. */
    val viewAll: String,
    /** Shown under the feed while the next batch is being revealed. */
    val homeFeedLoadingMore: String,
    val serviceTemporarilyUnavailable: String,
    // AI chat
    val chatTitle: String,
    val studyTitle: String,
    val studyPrompt: String,
    val phrasesTitle: String,
    val chatPlaceholder: String,
    val chatAiSender: String,
    val chatYouSender: String,
    val chatThinking: String,
    val chatError: String,
    val chatPlaySentence: String,
    val chatPauseSentence: String,
    val chatDefinition: String,
    val chatInContext: String,
    val chatResidentDetails: String,
    val chatSuggestions: List<String>,
    // Phrases / Earth-Mystical Parlance
    val phrasesBlurb: String,
    val phrasesAllLocations: String,
    val phrasesLoading: String,
    val phrasesEmpty: String,
    val phrasesShowVerse: String,
    val phrasesReact: String,
    // Admin portal
    val adminTitle: String,
    val adminPasswordTitle: String,
    val adminPasswordPlaceholder: String,
    val adminSignIn: String,
    val adminWrongPassword: String,
    val adminLockedHint: String,
    val adminPhrasesTab: String,
    val adminPhotosTab: String,
    val adminCharactersTab: String,
    val adminVoicesTab: String,
    val adminAddPhrase: String,
    val adminEditPhrase: String,
    val adminEdit: String,
    val adminDelete: String,
    val adminSave: String,
    val adminSaved: String,
    val adminError: String,
    val adminLogout: String,
    val adminPhotoName: String,
    val adminPhotoNamePlaceholder: String,
    val adminPhotoFile: String,
    val adminUploadPhoto: String,
    val adminUploading: String,
    val adminPhotosHint: String,
    val adminCharactersHint: String,
    val adminCharactersEmpty: String,
    val adminVoicesHint: String,
    val adminVoiceName: String,
    val adminVoiceNamePlaceholder: String,
    val adminVoiceLanguage: String,
    val adminVoiceLanguageAll: String,
    val adminVoiceFile: String,
    val adminUploadVoice: String,
    val adminVoiceActivate: String,
    val adminVoiceActive: String,
    val adminVoiceEmpty: String,
    val adminDeleteVoiceConfirm: String,
    val adminVoiceProviderId: String,
    val adminLocationKey: String,
    val adminLocationEn: String,
    val adminLocationVi: String,
    val adminIcon: String,
    val adminSpokenEn: String,
    val adminSpokenVi: String,
    val adminVerseEn: String,
    val adminVerseVi: String,
    val adminRawAskEn: String,
    val adminRawAskVi: String,
    val adminImageUrl: String,
    val adminApproved: String,
    val adminUnapproved: String,
    val adminDeletePhraseConfirm: String,
    val adminDeletePhotoConfirm: String,
    val adminDeleteCharacterConfirm: String,
    val adminCharacterStudyLocked: String,
    val adminInstructionsEn: String,
    val adminInstructionsVi: String,
    val adminGeneratePhoto: String,
    val adminGenerateCover: String,
    val adminGenerate: String,
    val adminGenerating: String,
    val adminGeneratePrompt: String,
    val adminGenerateHint: String,
    val adminGenerateNoPhotos: String,
    val adminSelectReferences: String,
    val adminCoverTitle: String,
    val adminCoverHint: String,
    val adminCoverPromptDefault: String,
    val adminPhrasePromptDefault: String,
    val phrasesSpokenIn: String,
    val phrasesHowToUse: String,
    val phrasesVerse: String,
    val phrasesOriginStory: String,
    val chatEmptyLead: String,
    val chatEmptyOrAsk: String,
    val chatExplorePhrases: String,
    val chatExplorePhrasesSub: String,
    val chatExploreStories: String,
    val chatExploreStoriesSub: String,
    val storiesTitle: String,
    val storiesComingSoon: String,
    val storiesComingSoonBody: String,
    val storiesShelfHint: String,
    val storiesEmpty: String,
    val storiesLoading: String,
    val storiesFeaturedTitle: String,
    val storiesFeaturedHint: String,
    val storiesFeaturedRole: String,
    val storiesFeaturedAbout: String,
    val storiesFeaturedVibe: String,
    val storiesFeaturedWant: String,
    val storiesFeaturedPhotos: String,
    val storiesFeaturedSubmit: String,
    val storiesFeaturedSuccess: String,
    val storiesSuggestTitle: String,
    val storiesSuggestHint: String,
    val storiesSuggestPlaceholder: String,
    val storiesSuggestSubmit: String,
    val storiesSuggestSuccess: String,
    val storiesSubmitError: String,
    val storiesLearningMode: String,
    val storiesContinue: String,
    val storiesReadAgain: String,
    val storiesPreviousPage: String,
    val storiesTheEnd: String,
    val storiesPageOf: (page: Int, total: Int) -> String,
    val storiesPages: (count: Int) -> String,
    val storiesStudyThisPage: String,
    val storiesPublished: String,
    val storiesDraft: String,
    val storiesOpens: (count: Int) -> String,
    val adminStoriesTab: String,
    val adminAddBook: String,
    val adminEditBook: String,
    val adminBookName: String,
    val adminBookCategory: String,
    val adminBookCategoryPlaceholder: String,
    val adminPublish: String,
    val adminUnpublish: String,
    val adminConfirmLanguage: String,
    val adminPublishing: String,
    val adminAddPage: String,
    val adminPageText: String,
    val adminPagePhoto: String,
    val adminSelectScenes: String,
    val adminSceneName: String,
    val adminUploadScene: String,
    val adminSelectCharacters: String,
    val adminUploadCharacter: String,
    val adminGenerateCharacter: String,
    val adminCharacterName: String,
    val adminCharacterPrompt: String,
    val adminNoBooks: String,
    val chatSavedTitle: String,
    val chatSavedEmpty: String,
    val chatSavedSend: String,
    val chatSavedEdit: String,
    val chatSavedRemove: String,
    val chatExploreSaved: String,
    val chatExploreSavedSub: String,
    val chatExploreStudy: String,
    val chatExploreStudySub: String,
    val chatStudySessionsTitle: String,
    val chatStudySessionsMine: String,
    val chatStudySessionsCommunity: String,
    val chatStudySessionsCommunityEmpty: String,
    val chatStudySessionsEmpty: String,
    val chatStudySessionsPlaceholder: String,
    val chatStudyExactToggle: String,
    val chatStudySessionsOpen: String,
    val chatStudySessionsRemove: String,
    val chatStudySessionsRename: String,
    val chatStudySessionsNamePlaceholder: String,
    val chatStudySessionsSaveName: String,
    val chatStudyAnotherLanguage: String,
    val chatStudyAnotherLanguagePlaceholder: String,
    val chatStudyAnotherLanguageConfirm: String,
    val chatStudySessionsError: String,
    val chatSaveMessage: String,
    val chatUnsaveMessage: String,
    val chatSavedError: String,
    val chatStudyMessage: String,
    val chatStudyTitle: String,
    val chatWatchTab: String,
    val chatWatchPlay: String,
    val chatWatchPause: String,
    val chatWatchPreparing: String,
    val chatWatchSeekLabel: String,
    val chatWatchFullscreen: String,
    val chatWatchExitFullscreen: String,
    val chatStoryTab: String,
    val chatStoryContinue: String,
    val chatStoryBack: String,
    val chatStudyTab: String,
    val chatWordsTab: String,
    val chatWordsWord: String,
    val chatWordsMeaning: String,
    val chatListenTab: String,
    val chatReadTab: String,
    val chatWriteTab: String,
    val chatExpressTab: String,
    val chatPlayTab: String,
    val chatTest: StudyTestStrings,
    val chatAllTab: String,
    val chatReadTapHint: String,
    val chatReadPlayText: String,
    val chatReadPauseText: String,
    val chatWriteHint: String,
    val chatWritePlaceholder: String,
    val chatWriteCorrect: String,
    val chatWriteChecking: String,
    val chatWriteFeedbackError: String,
    val chatExpressHint: String,
    val chatListenHint: String,
    val chatListenPlaceholder: String,
    val chatListenChecking: String,
    val chatExpressPlaceholder: String,
    val chatExpressCorrect: String,
    val chatExpressChecking: String,
    val chatExpressFeedbackError: String,
    val chatExpressLoading: String,
    val chatExpressGenerating: String,
    val chatExpressEmpty: String,
    val chatPlayHint: String,
    val chatPlayLoading: String,
    val chatPlayError: String,
    val chatPlayRetry: String,
    val chatPlayPlaceholder: String,
    val chatPlayYouLabel: String,
    val chatPlayOtherLabel: String,
    val chatPlaySending: String,
    val chatStudyLoading: String,
    val chatStudyCreationTitle: String,
    val chatStudyCreationText: String,
    val chatStudyCreationStructure: String,
    val chatStudyCreationDissections: String,
    val chatStudyCreationImages: String,
    val chatStudyCreationTest: String,
    val chatStudyCreationCount: (done: Int, total: Int) -> String,
    val chatStudyError: String,
    val chatStudyLiteral: String,
    val chatStudyRegister: String,
    val chatStudyContext: String,
    val chatStudySynonyms: String,
    val chatStudyExamples: String,
    val chatStudyPlaySentence: String,
    val chatStudyPauseSentence: String,
    val chatStudyTapHint: String,
    val chatStudyPreparingMeanings: String,
    val chatStudySentenceError: String,
    val chatStudyRetrySentence: String,
    val chatStudyMeaningLoading: String,
    val chatStudyPlayground: String,
    val chatStudyPlaygroundHint: String,
    val chatStudyPlaygroundPlaceholder: String,
    val chatStudyPlaygroundSubmit: String,
    val chatStudyPlaygroundNatural: String,
    val chatStudyPlaygroundUnnatural: String,
    val chatStudyPlaygroundError: String,
    val chatStudyPlaygroundImageLoading: String,
    val chatStudyPlaygroundImageError: String,
    val chatStudyPlaygroundImageAlt: String,
    val chatStudySentenceImageLoading: String,
    val chatStudySentenceImageError: String,
    val chatStudySentenceImageAlt: String,
    val chatStudyWordImageLoading: String,
    val chatStudyWordImageError: String,
    val chatStudyWordImageAlt: String,
    val challengesTitle: String,
    val challengesPrompt: String,
    val challengesStartNew: String,
    val challengesLoading: String,
    val challengesEmpty: String,
    val challengesInReview: String,
    val challengesProcessing: String,
    val challengesFailed: String,
    val challengesYourName: String,
    val challengesYourNamePlaceholder: String,
    val challengesPickVideo: String,
    val challengesRulesTitle: String,
    val challengesRulePlace: String,
    val challengesRuleFamily: String,
    val challengesRuleLength: String,
    val challengesSubmit: String,
    val challengesUploading: String,
    val challengesVote: String,
    val challengesVoted: String,
    val challengesVotes: String,
    val challengesUntitled: String,
    val challengesEnter: String,
    val challengesNext: String,
    val challengesPrevious: String,
    val challengesSubmitError: String,
    val challengesVoteError: String,
    val chatExploreChallenges: String,
    val chatExploreChallengesSub: String,
    val chatExploreMeets: String,
    val chatExploreMeetsSub: String,
    val adminChallengesTab: String,
    val adminPendingSubmissions: String,
    val adminPublishSubmission: String,
    val adminRenameChallenge: String,
    val adminDeleteChallenge: String,
    val adminDeleteSubmission: String,
    val adminNoChallenges: String,
    val adminNoPending: String,
    val meetsTitle: String,
    val meetsPrompt: String,
    val meetsStartNew: String,
    val meetsLoading: String,
    val meetsEmpty: String,
    val meetsInReview: String,
    val meetsYourName: String,
    val meetsYourNamePlaceholder: String,
    val meetsYourPhoto: String,
    val meetsPickPhoto: String,
    val meetsZaloHint: String,
    val meetsPitchLimit: String,
    val meetsPitchLimitHint: String,
    val meetsPitchLabel: String,
    val meetsPitchPlaceholder: String,
    val meetsSubmit: String,
    val meetsUploading: String,
    val meetsRemaining: (count: Int) -> String,
    val meetsPitchPerson: (name: String) -> String,
    val meetsPitchReplyLabel: String,
    val meetsPitchReplyPlaceholder: String,
    val meetsSendPitch: String,
    val meetsPitchSent: String,
    val meetsSubmitError: String,
    val meetsPitchError: String,
    val meetsNoRemaining: String,
    val meetsUntitled: String,
    val meetsCannotPitchOwn: String,
    val meetsAlreadyPitched: String,
    val adminMeetsTab: String,
    val adminPendingMeets: String,
    val adminPendingPitches: String,
    val adminApproveMeet: String,
    val adminCompletePitch: String,
    val adminResetRemaining: String,
    val adminResetRemainingHint: String,
    val adminDeleteMeet: String,
    val adminDeletePitch: String,
    val adminNoMeets: String,
    val adminNoPendingMeets: String,
    val adminNoPendingPitches: String,
    val adminMeetZalo: String,
    val adminMeetPosterZalo: String,
    val adminPitchReplyZalo: String,
    val locationsTitle: String,
    val locationsPrompt: String,
    val locationsSearchPlaceholder: String,
    val locationsAllCategories: String,
    val locationsLoading: String,
    val locationsEmpty: String,
    val locationsStatusPlanning: String,
    val locationsStatusComingSoon: String,
    val locationsStatusOpen: String,
    val locationsStatusClosed: String,
    val locationsClosesAt: (time: String) -> String,
    val locationsOpensAt: (time: String) -> String,
    val locationsOpensDayAt: (day: String, time: String) -> String,
    val locationsCommitFunds: String,
    val locationsContact: String,
    val locationsDirections: String,
    val locationsAmountLabel: String,
    val locationsAmountPlaceholder: String,
    val locationsMessageLabel: String,
    val locationsMessagePlaceholder: String,
    val locationsCommitSubmit: String,
    val locationsCommitSent: String,
    val locationsCommitError: String,
    val locationsContactSubmit: String,
    val locationsContactSent: String,
    val locationsContactError: String,
    val locationsContactPlaceholder: String,
    val locationsLikeError: String,
    val locationsSuggestTitle: String,
    val locationsSuggestHint: String,
    val locationsSuggestName: String,
    val locationsSuggestType: String,
    val locationsSuggestAddress: String,
    val locationsSuggestWhy: String,
    val locationsSuggestSubmit: String,
    val locationsSuggestSuccess: String,
    val locationsSuggestError: String,
    val locationsUntitled: String,
    val locationsDayMon: String,
    val locationsDayTue: String,
    val locationsDayWed: String,
    val locationsDayThu: String,
    val locationsDayFri: String,
    val locationsDaySat: String,
    val locationsDaySun: String,
    val chatExploreLocations: String,
    val chatExploreLocationsSub: String,
    val adminLocationsTab: String,
    val adminAddLocation: String,
    val adminEditLocation: String,
    val adminDeleteLocation: String,
    val adminNoLocations: String,
    val adminLocationName: String,
    val adminLocationType: String,
    val adminLocationCategory: String,
    val adminLocationAddress: String,
    val adminLocationStatus: String,
    val adminLocationHostZalo: String,
    val adminLocationMapsUrl: String,
    val adminLocationHighlights: String,
    val adminLocationPhotos: String,
    val adminLocationAddPhoto: String,
    val adminLocationSchedule: String,
    val adminLocationClosedDay: String,
    val adminLocationApplyBelow: String,
    val adminLocationContent: String,
    val adminLocationAddText: String,
    val adminLocationAddImage: String,
    val adminLocationCommitments: String,
    val adminLocationContacts: String,
    val clubsTitle: String,
    val clubsPrompt: String,
    val clubsSearchPlaceholder: String,
    val clubsAllCategories: String,
    val clubsLoading: String,
    val clubsEmpty: String,
    val clubsUntitled: String,
    val clubsCreateTitle: String,
    val clubsCreateHint: String,
    val clubsCreateName: String,
    val clubsCreateTagline: String,
    val clubsCreateDescription: String,
    val clubsCreateHostName: String,
    val clubsCreateSubmit: String,
    val clubsCreateSuccess: String,
    val clubsCreateError: String,
    val clubsJoin: String,
    val clubsJoinSubmit: String,
    val clubsJoinSent: String,
    val clubsJoinError: String,
    val clubsYourName: String,
    val clubsYourMessage: String,
    val clubsYourMessagePlaceholder: String,
    val clubsHostedBy: String,
    val clubsDetails: String,
    val adminClubsTab: String,
    val adminAddClub: String,
    val adminEditClub: String,
    val adminDeleteClub: String,
    val adminNoClubs: String,
    val adminClubName: String,
    val adminClubTagline: String,
    val adminClubDescription: String,
    val adminClubCategory: String,
    val adminClubActivities: String,
    val adminClubHostName: String,
    val adminClubHostZalo: String,
    val adminClubPhotos: String,
    val adminClubAddPhoto: String,
    val adminClubContent: String,
    val adminClubAddText: String,
    val adminClubAddImage: String,
    val adminClubJoinQuestions: String,
    val adminClubCreateRequests: String,
    val adminClubJoinRequests: String,
    val adminNoClubCreateRequests: String,
    val adminNoClubJoinRequests: String,
    val adminDismissClubRequest: String,
    val adminDismissClubJoin: String,
    val adminStatsTab: String,
    val adminStatsHint: String,
    val adminStatsLoading: String,
    val adminStats7Days: String,
    val adminStats30Days: String,
    val adminStatsVisitors: String,
    val adminStatsVisits: String,
    val adminStatsNewVisitors: String,
    val adminStatsToday: String,
    val adminStatsLast7Days: String,
    val adminStatsLast30Days: String,
    val adminStatsAllTime: String,
    val adminStatsTopScreens: String,
    val adminStatsLanguages: String,
    val adminStatsNoData: String,
    val adminStatsTimezone: String,
    val adminStatsSelected: String,
) {
    val chatTestTab: String get() = chatTest.tab
    val chatTestSceneLoading: String get() = chatTest.sceneLoading
    val chatTestSceneError: String get() = chatTest.sceneError
    val chatTestAudioError: String get() = chatTest.audioError
    val chatTestRetry: String get() = chatTest.retry
    val chatTestListenHint: (String) -> String get() = chatTest.listenHint
    val chatTestSpeakHint: String get() = chatTest.speakHint
    val chatTestPlayLine: String get() = chatTest.playLine
    val chatTestBeatProgress: (Int, Int) -> String get() = chatTest.beatProgress
    val chatTestOutlineTitle: String get() = chatTest.outlineTitle
    val chatTestStart: String get() = chatTest.start
    val chatTestInputPlaceholder: String get() = chatTest.inputPlaceholder
    val chatTestNext: String get() = chatTest.next
    val chatTestFinish: String get() = chatTest.finish
    val chatTestScoring: String get() = chatTest.scoring
    val chatTestScoreError: String get() = chatTest.scoreError
    val chatTestResultTitle: String get() = chatTest.resultTitle
    val chatTestGradeLabel: String get() = chatTest.gradeLabel
    val chatTestListeningLabel: String get() = chatTest.listeningLabel
    val chatTestSpeakingLabel: String get() = chatTest.speakingLabel
    val chatTestRetake: String get() = chatTest.retake
    val chatTestResultCheer: (Int) -> String get() = chatTest.resultCheer
    val chatTestLevelBeginner: String get() = chatTest.levelBeginner
    val chatTestLevelElementary: String get() = chatTest.levelElementary
    val chatTestLevelIntermediate: String get() = chatTest.levelIntermediate
    val chatTestLevelAdvanced: String get() = chatTest.levelAdvanced
    val chatTestLevelNative: String get() = chatTest.levelNative
}

private val English = UiStrings(
    appTitle = "One Verandah App",
    serviceDetails = "Offer details",
    homePrompt = "How can we help you today?",
    reviews = { count -> "($count reviews)" },
    sectionBookingType = "Booking Type",
    sectionDuration = "Duration",
    sectionTargetArea = "Target Area",
    sectionComments = "Comments",
    sectionReviews = "Reviews",
    minutes = { minutes -> "$minutes minutes" },
    hours = { hours -> "$hours hours" },
    from = "from",
    total = "Total",
    priceSummary = "Price summary",
    tapToSelect = "Tap the body to choose areas",
    bookThisService = "Book This Service",
    booking = "Booking…",
    bookingSuccessTitle = "Booking confirmed!",
    bookingSuccessMessage = "We've sent the details to your provider. See you soon!",
    done = "Done",
    back = "Back",
    menu = "Menu",
    languageName = "English",
    layoutSwitchToDesktop = "Desktop layout",
    layoutSwitchToMobile = "Mobile layout",
    layoutFullscreen = "Fullscreen",
    layoutExitFullscreen = "Exit fullscreen",
    menuTitle = "One Verandah App",
    menuHome = "Home",
    menuAbout = "About",
    menuLanguage = "Language",
    menuHelp = "Help & support",
    menuForAdults = "For adults",
    menuForKids = "For kids",
    close = "Close",
    bookingDialogTitle = "Confirm your booking",
    zaloLabel = "Zalo number",
    zaloPlaceholder = "Your phone number",
    zaloError = "Zalo number is required",
    messageLabel = "Additional comments",
    messagePlaceholder = "Optional message for the provider",
    confirmBooking = "Confirm Booking",
    confirm = "Confirm",
    male = "Boy",
    female = "Girl",
    viewFront = "Front",
    viewBack = "Back",
    aboutText = "Made by One Verandah residents, for One Verandah residents.",
    aboutExclusive = "Built exclusively for the One Verandah community",
    aboutTrusted = "Your safety matters—we put trusted, verified providers first",
    aboutFree = "Completely free to use, always",
    helpSuggestFeature = "Suggest a feature",
    helpSuggestFeatureSubtitle = "Tell us what you'd like to see next",
    helpFeedback = "Send us feedback",
    helpFeedbackSubtitle = "Share your thoughts to help us improve",
    helpReportProblem = "Report a problem",
    helpReportProblemSubtitle = "Let us know about any issues you've found",
    helpSubmit = "Submit",
    helpPlaceholder = "Type your message here…",
    helpErrorRequired = "Message is required",
    helpSuccessTitle = "Thank you!",
    helpSuccessMessage = "Your submission has been received. We appreciate your feedback!",
    helpListService = "List a service",
    helpListServiceSubtitle = "Offer your services to the community",
    listServiceDialogTitle = "List a service",
    serviceDescriptionLabel = "Describe the service",
    serviceDescriptionPlaceholder = "What services do you provide?",
    serviceDescriptionError = "Service description is required",
    yourNameLabel = "Your name",
    yourSexLabel = "Your sex",
    yourNamePlaceholder = "Enter your name",
    yourNameError = "Your name is required",
    blockAddressPlaceholder = "Enter your block and unit number (e.g. Block A - A.12.03)",
    blockAddressError = "Block/Address is required",
    listServiceSuccessTitle = "Service listing submitted!",
    listServiceSuccessMessage = "Your service listing details have been sent. The host will review and add it manually soon!",
    searchPlaceholder = "Search for services…",
    noResults = "Press enter for AI search.",
    providedBy = "Provided by",
    providerBlockLabel = "Block/Address",
    providerPhoneLabel = "Zalo Phone",
    providerSexLabel = "Sex",
    providerAboutLabel = "About",
    providerSkillsLabel = "Skills",
    providerSkillLevelLabel = "Lvl",
    providerServicesLabel = "Other services by",
    providerAllServicesLabel = "All services by",
    providerDetails = "Provider details",
    whenLabel = "When",
    whenNow = "Now",
    whenToday = "Today",
    whenTomorrow = "Tomorrow",
    whenLater = "Later",
    save = "Save",
    saved = "Saved",
    share = "Share",
    savedToast = "Saved to your Saved list",
    removedToast = "Removed from Saved",
    copyLink = "Copy Link",
    copied = "Copied!",
    copiedToast = "Link copied to clipboard",
    pinnedRecentTitle = "Saved & Recent",
    popularTitle = "Popular services",
    clearAll = "Clear All",
    clearAllConfirm = "Remove all saved services from this list?",
    pinnedEmpty = "Save services you love, or book one to save it here!",
    homeViewServices = "Services",
    homeViewCategories = "Categories",
    homeLatestStories = "Latest stories",
    homeLatestStudySessions = "Latest study sessions",
    homeLatestChallenges = "Latest challenges",
    homeLatestLocations = "Latest locations",
    homeLatestClubs = "Latest clubs",
    homeLatestMeets = "Latest meets",
    viewAll = "View all",
    homeFeedLoadingMore = "Loading more…",
    serviceTemporarilyUnavailable = "This service is temporarily unavailable",
    chatTitle = "Chat",
    studyTitle = "Study",
    studyPrompt = "Grind through language at your own pace!",
    phrasesTitle = "Phrases",
    chatPlaceholder = "Ask anything…",
    chatAiSender = "AI",
    chatYouSender = "You",
    chatThinking = "I'm thinking…",
    chatError = "Something went wrong. Please try again.",
    chatPlaySentence = "Play sentence",
    chatPauseSentence = "Pause sentence",
    chatDefinition = "Definition",
    chatInContext = "In this sentence",
    chatResidentDetails = "View resident details",
    chatSuggestions = listOf(
        "Whisper a gentle way to meet a neighbour at the infinity pool",
        "Teach me a luminous Vietnamese phrase for shared stillness",
        "What quiet drama can I try at One Verandah today?",
        "Help me turn a plain request into Earth-Mystic Parlance",
    ),
    phrasesBlurb = "Use Earth-Mystic Parlance invitations to enhance your community at One Verandah.",
    phrasesAllLocations = "All places",
    phrasesLoading = "Loading phrases…",
    phrasesEmpty = "No phrases yet. Check back soon.",
    phrasesShowVerse = "Tap to read the full verse",
    phrasesReact = "React",
    adminTitle = "Admin",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "Enter password",
    adminSignIn = "Unlock",
    adminWrongPassword = "That password doesn't open this door.",
    adminLockedHint = "Enter the admin password to manage phrases and reference photos.",
    adminPhrasesTab = "Phrases",
    adminPhotosTab = "Reference photos",
    adminCharactersTab = "Characters",
    adminVoicesTab = "Cloned voices",
    adminAddPhrase = "Add phrase",
    adminEditPhrase = "Edit phrase",
    adminEdit = "Edit",
    adminDelete = "Delete",
    adminSave = "Save",
    adminSaved = "Saved",
    adminError = "Something went wrong",
    adminLogout = "Lock admin",
    adminPhotoName = "Photo name",
    adminPhotoNamePlaceholder = "e.g. Infinity Pool Dusk",
    adminPhotoFile = "Photo file",
    adminUploadPhoto = "Upload photo",
    adminUploading = "Uploading…",
    adminPhotosHint = "Named reference photos power Qwen R2I scenes that are actually at the apartment.",
    adminCharactersHint = "Named character portraits used when generating story photos.",
    adminCharactersEmpty = "No characters yet.",
    adminVoicesHint = "Upload a short clean sample per language. One active voice per language (or All languages fallback).",
    adminVoiceName = "Voice name",
    adminVoiceNamePlaceholder = "e.g. warm-vi-companion",
    adminVoiceLanguage = "Language",
    adminVoiceLanguageAll = "All languages",
    adminVoiceFile = "Audio sample",
    adminUploadVoice = "Upload voice",
    adminVoiceActivate = "Use this voice",
    adminVoiceActive = "Active",
    adminVoiceEmpty = "No cloned voices yet. Upload a sample to replace the default TTS voice.",
    adminDeleteVoiceConfirm = "Delete this cloned voice?",
    adminVoiceProviderId = "Provider id",
    adminLocationKey = "Location key",
    adminLocationEn = "Location (English)",
    adminLocationVi = "Location (Vietnamese)",
    adminIcon = "Icon",
    adminSpokenEn = "Spoken (English)",
    adminSpokenVi = "Spoken (Vietnamese)",
    adminVerseEn = "Verse (English)",
    adminVerseVi = "Verse (Vietnamese)",
    adminRawAskEn = "Raw ask (English)",
    adminRawAskVi = "Raw ask (Vietnamese)",
    adminImageUrl = "Image URL",
    adminApproved = "Approved",
    adminUnapproved = "Unapproved",
    adminDeletePhraseConfirm = "Delete this phrase?",
    adminDeletePhotoConfirm = "Delete this reference photo?",
    adminDeleteCharacterConfirm = "Delete this character?",
    adminCharacterStudyLocked = "From a study session, so it can't be deleted.",
    adminInstructionsEn = "Instructions (English)",
    adminInstructionsVi = "Instructions (Vietnamese)",
    adminGeneratePhoto = "Generate photo",
    adminGenerateCover = "Generate cover",
    adminGenerate = "Generate",
    adminGenerating = "Generating…",
    adminGeneratePrompt = "Prompt",
    adminGenerateHint = "Pick apartment reference photos, write a prompt, and craft the scene with Qwen R2I.",
    adminGenerateNoPhotos = "Upload reference photos first so the scene can be grounded at One Verandah.",
    adminSelectReferences = "Reference photos",
    adminCoverTitle = "Phrasebook cover",
    adminCoverHint = "Generate a cover from a prompt and apartment reference photos.",
    adminCoverPromptDefault = "Dreamlike cinematic cover of One Verandah residents sharing gentle Earth-Mystic Parlance at golden hour, soft film grain, no text",
    adminPhrasePromptDefault = "Dreamlike cinematic photo at One Verandah, soft mystical light, residents in gentle conversation, no text",
    phrasesSpokenIn = "Spoken in Earth-Mystic Parlance",
    phrasesHowToUse = "How to use",
    phrasesVerse = "Verse",
    phrasesOriginStory = "They say the first phrase was overheard near the infinity pool — not spoken by any resident, but carried on the wind through the garden. It sounded like an old prayer from a future where people have remembered how to be gentle with one another. Those who heard it began to speak it themselves. More phrases bloomed. The building itself seemed to exhale them. And so the parlance was born — not invented, but uncovered.",
    chatEmptyLead = "A few luminous doors to open — or ask anything living in you.",
    chatEmptyOrAsk = "Or try a spark",
    chatExplorePhrases = "Earth-Mystic Phrases",
    chatExplorePhrasesSub = "Starter invitations in Lời Huyền Linh",
    chatExploreStories = "Mini Stories",
    chatExploreStoriesSub = "Bite-sized tales from the building",
    storiesTitle = "Mini Stories",
    storiesComingSoon = "Stories are gathering",
    storiesComingSoonBody = "Mini Stories are on their way. Meanwhile, wander the Earth-Mystic phrasebook and let a line open a door.",
    storiesShelfHint = "A fun way for neighbors to laugh together while learning each other's languages.",
    storiesEmpty = "No stories on the shelf yet. Check back after the next release.",
    storiesLoading = "Opening the shelf…",
    storiesFeaturedTitle = "Be featured in the next Mini Story",
    storiesFeaturedHint = "Tell us the role you want and send 1–3 photos of you.",
    storiesFeaturedRole = "Your desired role",
    storiesFeaturedAbout = "A little about you",
    storiesFeaturedVibe = "What you're like",
    storiesFeaturedWant = "What you want to do in the story",
    storiesFeaturedPhotos = "1 to 3 photos of you",
    storiesFeaturedSubmit = "Submit",
    storiesFeaturedSuccess = "Sent! We'll write you into a future story.",
    storiesSuggestTitle = "Suggest a story idea",
    storiesSuggestHint = "A seed is enough — a place, a feeling, a neighborly mishap.",
    storiesSuggestPlaceholder = "Your story idea",
    storiesSuggestSubmit = "Send idea",
    storiesSuggestSuccess = "Idea received. Thank you.",
    storiesSubmitError = "Couldn't send that just now. Please try again.",
    storiesLearningMode = "Enable Learning Mode",
    storiesContinue = "Continue",
    storiesReadAgain = "Read again",
    storiesPreviousPage = "Previous page",
    storiesTheEnd = "The end",
    storiesPageOf = { page, total -> "Page $page of $total" },
    storiesPages = { count -> if (count == 1) "1 page" else "$count pages" },
    storiesStudyThisPage = "Study this page",
    storiesPublished = "Published",
    storiesDraft = "Draft",
    storiesOpens = { count -> if (count == 1) "1 open" else "$count opens" },
    adminStoriesTab = "Stories",
    adminAddBook = "New book",
    adminEditBook = "Edit book",
    adminBookName = "Book name",
    adminBookCategory = "Category",
    adminBookCategoryPlaceholder = "Mystery, Bedtime, Social…",
    adminPublish = "Publish",
    adminUnpublish = "Unpublish",
    adminConfirmLanguage = "Confirm the book's language",
    adminPublishing = "Publishing…",
    adminAddPage = "Add page",
    adminPageText = "Page text",
    adminPagePhoto = "Page photo",
    adminSelectScenes = "Scenes",
    adminSceneName = "Scene name",
    adminUploadScene = "Upload scene",
    adminSelectCharacters = "Characters",
    adminUploadCharacter = "Upload character",
    adminGenerateCharacter = "Generate character",
    adminCharacterName = "Character name",
    adminCharacterPrompt = "Character prompt",
    adminNoBooks = "No books yet.",
    chatSavedTitle = "Saved messages",
    chatSavedEmpty = "Bookmark any message to find it here later on this device.",
    chatSavedSend = "Send",
    chatSavedEdit = "Edit",
    chatSavedRemove = "Remove",
    chatExploreSaved = "Saved messages",
    chatExploreSavedSub = "Bookmarks from this device",
    chatExploreStudy = "Study sessions",
    chatExploreStudySub = "Resume learning from this device",
    chatStudySessionsTitle = "Study sessions",
    chatStudySessionsMine = "My study sessions",
    chatStudySessionsCommunity = "From the community",
    chatStudySessionsCommunityEmpty = "No community sessions yet.",
    chatStudySessionsEmpty = "Start a session above, or open Study on any chat message.",
    chatStudySessionsPlaceholder = "Paste or type text to study…",
    chatStudyExactToggle = "Exactly this text",
    chatStudySessionsOpen = "Open",
    chatStudySessionsRemove = "Remove",
    chatStudySessionsRename = "Rename",
    chatStudySessionsNamePlaceholder = "Session name",
    chatStudySessionsSaveName = "Save name",
    chatStudyAnotherLanguage = "Study in another language",
    chatStudyAnotherLanguagePlaceholder = "Type a language",
    chatStudyAnotherLanguageConfirm = "Create session",
    chatStudySessionsError = "Couldn't update study sessions. Try again.",
    chatSaveMessage = "Save message",
    chatUnsaveMessage = "Remove saved message",
    chatSavedError = "Couldn't update saved messages. Try again.",
    chatStudyMessage = "Study message",
    chatStudyTitle = "Study session",
    chatWatchTab = "Watch",
    chatWatchPlay = "Play",
    chatWatchPause = "Pause",
    chatWatchPreparing = "Preparing audio…",
    chatWatchSeekLabel = "Scrub through the session",
    chatWatchFullscreen = "Fullscreen",
    chatWatchExitFullscreen = "Exit fullscreen",
    chatStoryTab = "Story",
    chatStoryContinue = "Continue",
    chatStoryBack = "Back",
    chatStudyTab = "Study",
    chatWordsTab = "Words",
    chatWordsWord = "Word",
    chatWordsMeaning = "Meaning",
    chatListenTab = "Listen",
    chatReadTab = "Read",
    chatWriteTab = "Write",
    chatExpressTab = "Express",
    chatPlayTab = "Play",
    chatTest = StudyTestStrings(
        tab = "Test",
        sceneLoading = "Building your test scene…",
        sceneError = "Couldn't create the test scene.",
        audioError = "Couldn't play the line. Try again.",
        retry = "Retry",
        listenHint = { name -> "Explain what $name said in your language." },
        speakHint = "Reply in the study language and fit the situation.",
        playLine = "Replay",
        beatProgress = { current, total -> "Round $current of $total" },
        outlineTitle = "What you'll do",
        start = "Start",
        inputPlaceholder = "Type or hold to speak…",
        next = "Next",
        finish = "Finish test",
        scoring = "Scoring your answers…",
        scoreError = "Couldn't score the test. Try again.",
        resultTitle = "Test result",
        gradeLabel = "Overall grade",
        listeningLabel = "Listening",
        speakingLabel = "Speaking",
        retake = "Retake",
        levelBeginner = "Beginner",
        levelElementary = "Elementary",
        levelIntermediate = "Intermediate",
        levelAdvanced = "Advanced",
        levelNative = "Native",
        resultCheer = { level ->
            when (level) {
                5 -> "You heard it and used it. That's the real thing."
                4 -> "Sharp listening, useful replies. You're close to the top."
                3 -> "You held the conversation. That's a real middle."
                2 -> "You caught pieces of the moment. A retake can lock them in."
                else -> "You found the thread. The next round will feel clearer."
            }
        },
    ),
    chatAllTab = "All",
    chatReadTapHint = "Tap a word to highlight every matching word in the text",
    chatReadPlayText = "Play full text",
    chatReadPauseText = "Pause full text",
    chatWriteHint = "Type or speak each sentence exactly as shown",
    chatWritePlaceholder = "Type the sentence…",
    chatWriteCorrect = "Correct!",
    chatWriteChecking = "Checking…",
    chatWriteFeedbackError = "Couldn't explain that attempt. Try again.",
    chatExpressHint = "Express each meaning in the language you're learning",
    chatListenHint = "Listen to each sentence and type its meaning in your language",
    chatListenPlaceholder = "Type the meaning of the sentence…",
    chatListenChecking = "Checking…",
    chatExpressPlaceholder = "Type your sentence…",
    chatExpressCorrect = "Nice!",
    chatExpressChecking = "Checking…",
    chatExpressFeedbackError = "Couldn't check that attempt. Try again.",
    chatExpressLoading = "Gathering practice sentences…",
    chatExpressGenerating = "Creating more sentences…",
    chatExpressEmpty = "No practice sentences yet. Wait for meanings to load.",
    chatPlayHint = "Pick a scene and practice a short conversation",
    chatPlayLoading = "Inventing scenarios…",
    chatPlayError = "Couldn't load play scenarios. Try again.",
    chatPlayRetry = "Retry",
    chatPlayPlaceholder = "Reply in the study language…",
    chatPlayYouLabel = "You",
    chatPlayOtherLabel = "Them",
    chatPlaySending = "Thinking…",
    chatStudyLoading = "Preparing your study session…",
    chatStudyCreationTitle = "Creating your study session",
    chatStudyCreationText = "Study text",
    chatStudyCreationStructure = "Session structure",
    chatStudyCreationDissections = "Word meanings",
    chatStudyCreationImages = "Images",
    chatStudyCreationTest = "Test",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "Couldn't start the study session. Try again.",
    chatStudyLiteral = "Literal meaning",
    chatStudyRegister = "Register",
    chatStudyContext = "In this context",
    chatStudySynonyms = "Near synonyms",
    chatStudyExamples = "Example sentences",
    chatStudyPlaySentence = "Play sentence",
    chatStudyPauseSentence = "Pause sentence",
    chatStudyTapHint = "Tap a word to dissect its meaning",
    chatStudyPreparingMeanings = "Loading word meanings…",
    chatStudySentenceError = "Couldn't load meanings for this sentence.",
    chatStudyRetrySentence = "Retry",
    chatStudyMeaningLoading = "Loading meaning…",
    chatStudyPlayground = "Try it yourself",
    chatStudyPlaygroundHint = "Write a sentence using this word to test if it feels natural.",
    chatStudyPlaygroundPlaceholder = "Type a sentence with this word…",
    chatStudyPlaygroundSubmit = "Check sentence",
    chatStudyPlaygroundNatural = "Natural",
    chatStudyPlaygroundUnnatural = "Not natural",
    chatStudyPlaygroundError = "Couldn't check that sentence. Try again.",
    chatStudyPlaygroundImageLoading = "Drawing a comic scene…",
    chatStudyPlaygroundImageError = "Couldn't generate the comic image.",
    chatStudyPlaygroundImageAlt = "Comic scene for this word",
    chatStudySentenceImageLoading = "Creating a scene…",
    chatStudySentenceImageError = "Couldn't create the scene image.",
    chatStudySentenceImageAlt = "Illustration for this sentence",
    chatStudyWordImageLoading = "Creating a word scene…",
    chatStudyWordImageError = "Couldn't create the word image.",
    chatStudyWordImageAlt = "Illustration for this word",
    challengesTitle = "Challenges",
    challengesPrompt = "Fun challenges with the neighborhood!",
    challengesStartNew = "Start a new challenge",
    challengesLoading = "Loading challenges…",
    challengesEmpty = "No public challenges yet. Start one for the neighborhood.",
    challengesInReview = "In review",
    challengesProcessing = "Processing your video…",
    challengesFailed = "Processing failed",
    challengesYourName = "Your name",
    challengesYourNamePlaceholder = "What should neighbors call you?",
    challengesPickVideo = "Choose a video (up to 5 minutes)",
    challengesRulesTitle = "Challenge rules",
    challengesRulePlace = "Your submission must take place at One Verandah",
    challengesRuleFamily = "Your submission should be family friendly",
    challengesRuleLength = "Your submission should be 2–4 minutes long",
    challengesSubmit = "Submit video",
    challengesUploading = "Processing & uploading",
    challengesVote = "Vote",
    challengesVoted = "Voted",
    challengesVotes = "votes",
    challengesUntitled = "New challenge",
    challengesEnter = "Enter this challenge",
    challengesNext = "Next submission",
    challengesPrevious = "Previous submission",
    challengesSubmitError = "Couldn't submit that video. Try again.",
    challengesVoteError = "Couldn't save that vote.",
    chatExploreChallenges = "Challenges",
    chatExploreChallengesSub = "Compete for the top spot in the neighborhood",
    chatExploreMeets = "Meet",
    chatExploreMeetsSub = "Coffee chats and new neighbors",
    adminChallengesTab = "Challenges",
    adminPendingSubmissions = "New submissions",
    adminPublishSubmission = "Publish",
    adminRenameChallenge = "Rename",
    adminDeleteChallenge = "Delete challenge",
    adminDeleteSubmission = "Delete submission",
    adminNoChallenges = "No challenges yet.",
    adminNoPending = "Nothing waiting for review.",
    meetsTitle = "Meet",
    meetsPrompt = "Coffee chats, collaborations, and new neighbors. Pitch someone you'd like to meet.",
    meetsStartNew = "Post a meet",
    meetsLoading = "Loading meets…",
    meetsEmpty = "No meets yet. Be the first to say hello.",
    meetsInReview = "Waiting for approval",
    meetsYourName = "Your name",
    meetsYourNamePlaceholder = "What should people call you?",
    meetsYourPhoto = "Your best photo",
    meetsPickPhoto = "Choose a photo",
    meetsZaloHint = "Visible to everyone while the meet is active",
    meetsPitchLimit = "How many people can pitch you?",
    meetsPitchLimitHint = "Each completed meet uses one",
    meetsPitchLabel = "Your pitch",
    meetsPitchPlaceholder = "What do you want to meet and talk about or do?",
    meetsSubmit = "Post meet",
    meetsUploading = "Uploading",
    meetsRemaining = { count -> if (count == 1) "1 pitch left" else "$count pitches left" },
    meetsPitchPerson = { name -> "Pitch $name" },
    meetsPitchReplyLabel = "Your pitch",
    meetsPitchReplyPlaceholder = "Why should you meet?",
    meetsSendPitch = "Send pitch",
    meetsPitchSent = "Pitch sent. We'll pass along your Zalo if it's a match.",
    meetsSubmitError = "Couldn't post that meet. Try again.",
    meetsPitchError = "Couldn't send that pitch. Try again.",
    meetsNoRemaining = "This meet is full",
    meetsUntitled = "New meet",
    meetsCannotPitchOwn = "This is your meet",
    meetsAlreadyPitched = "You already sent a pitch",
    adminMeetsTab = "Meet",
    adminPendingMeets = "New meets",
    adminPendingPitches = "New meet pitches",
    adminApproveMeet = "Approve",
    adminCompletePitch = "Mark completed",
    adminResetRemaining = "Reset remaining",
    adminResetRemainingHint = "How many pitches should remain?",
    adminDeleteMeet = "Delete meet",
    adminDeletePitch = "Delete pitch",
    adminNoMeets = "No meets yet.",
    adminNoPendingMeets = "No new meets.",
    adminNoPendingPitches = "No new pitches.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "Poster Zalo",
    adminPitchReplyZalo = "Pitcher Zalo",
    locationsTitle = "Locations",
    locationsPrompt = "Discover places nearby — cafes, studios, and spots still taking shape.",
    locationsSearchPlaceholder = "Search places",
    locationsAllCategories = "All",
    locationsLoading = "Loading places…",
    locationsEmpty = "No places yet. Check back soon.",
    locationsStatusPlanning = "In planning",
    locationsStatusComingSoon = "Coming soon",
    locationsStatusOpen = "Open",
    locationsStatusClosed = "Closed",
    locationsClosesAt = { time -> "Closes at $time" },
    locationsOpensAt = { time -> "Opens at $time" },
    locationsOpensDayAt = { day, time -> "Opens $day $time" },
    locationsCommitFunds = "Commit funds",
    locationsContact = "Contact",
    locationsDirections = "Directions",
    locationsAmountLabel = "Amount (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "Message",
    locationsMessagePlaceholder = "A note for the host",
    locationsCommitSubmit = "Commit",
    locationsCommitSent = "Thank you — we'll pass this along.",
    locationsCommitError = "Couldn't send that pledge. Try again.",
    locationsContactSubmit = "Send",
    locationsContactSent = "Sent. The host will reach out on Zalo.",
    locationsContactError = "Couldn't send that message. Try again.",
    locationsContactPlaceholder = "Say hello",
    locationsLikeError = "Couldn't save that like.",
    locationsSuggestTitle = "Suggest a location",
    locationsSuggestHint = "Know a cafe, studio, or neighborhood spot we should add?",
    locationsSuggestName = "Name of the place",
    locationsSuggestType = "What kind of place",
    locationsSuggestAddress = "Address or how to find it",
    locationsSuggestWhy = "Why neighbors should know it",
    locationsSuggestSubmit = "Submit",
    locationsSuggestSuccess = "Sent! We'll take a look.",
    locationsSuggestError = "Couldn't send that just now. Please try again.",
    locationsUntitled = "New place",
    locationsDayMon = "Mon",
    locationsDayTue = "Tue",
    locationsDayWed = "Wed",
    locationsDayThu = "Thu",
    locationsDayFri = "Fri",
    locationsDaySat = "Sat",
    locationsDaySun = "Sun",
    chatExploreLocations = "Locations",
    chatExploreLocationsSub = "Places nearby",
    adminLocationsTab = "Locations",
    adminAddLocation = "Add location",
    adminEditLocation = "Edit",
    adminDeleteLocation = "Delete location",
    adminNoLocations = "No locations yet.",
    adminLocationName = "Name",
    adminLocationType = "Type",
    adminLocationCategory = "Category",
    adminLocationAddress = "Address",
    adminLocationStatus = "Status",
    adminLocationHostZalo = "Host Zalo",
    adminLocationMapsUrl = "Maps URL",
    adminLocationHighlights = "Highlights",
    adminLocationPhotos = "Photos",
    adminLocationAddPhoto = "Add photos",
    adminLocationSchedule = "Weekly hours (UTC+7)",
    adminLocationClosedDay = "Closed",
    adminLocationApplyBelow = "Apply below",
    adminLocationContent = "Story",
    adminLocationAddText = "Add text",
    adminLocationAddImage = "Add image",
    adminLocationCommitments = "Fund pledges",
    adminLocationContacts = "Messages",
    clubsTitle = "Clubs",
    clubsPrompt = "Discover clubs — join one, or start your own.",
    clubsSearchPlaceholder = "Search clubs",
    clubsAllCategories = "All",
    clubsLoading = "Loading clubs…",
    clubsEmpty = "No clubs yet. Check back soon.",
    clubsUntitled = "New club",
    clubsCreateTitle = "Create a club",
    clubsCreateHint = "Enter a name, tagline, description, and how to reach you. Other details are added after we create the club.",
    clubsCreateName = "Club name",
    clubsCreateTagline = "Tagline",
    clubsCreateDescription = "Description",
    clubsCreateHostName = "Your name",
    clubsCreateSubmit = "Create a club",
    clubsCreateSuccess = "Club request sent. We'll be in touch.",
    clubsCreateError = "Couldn't send that club request. Try again.",
    clubsJoin = "Join club",
    clubsJoinSubmit = "Join club",
    clubsJoinSent = "Thanks! The host will reach out to you on Zalo!",
    clubsJoinError = "Couldn't send that join request. Try again.",
    clubsYourName = "Your name",
    clubsYourMessage = "Your message",
    clubsYourMessagePlaceholder = "Anything you'd like the host to know",
    clubsHostedBy = "Hosted by",
    clubsDetails = "Details",
    adminClubsTab = "Clubs",
    adminAddClub = "Add club",
    adminEditClub = "Edit club",
    adminDeleteClub = "Delete club",
    adminNoClubs = "No clubs yet.",
    adminClubName = "Name",
    adminClubTagline = "Tagline",
    adminClubDescription = "Description",
    adminClubCategory = "Category",
    adminClubActivities = "Activities",
    adminClubHostName = "Host name",
    adminClubHostZalo = "Host Zalo",
    adminClubPhotos = "Photos",
    adminClubAddPhoto = "Add photos",
    adminClubContent = "Story",
    adminClubAddText = "Add text",
    adminClubAddImage = "Add image",
    adminClubJoinQuestions = "Join questions",
    adminClubCreateRequests = "Create club requests",
    adminClubJoinRequests = "Join club requests",
    adminNoClubCreateRequests = "No create club requests.",
    adminNoClubJoinRequests = "No join club requests.",
    adminDismissClubRequest = "Dismiss",
    adminDismissClubJoin = "Dismiss",
    adminStatsTab = "Stats",
    adminStatsHint = "Unique visitors by day in Vietnam time (UTC+7). Bars are visitors; the line is visits.",
    adminStatsLoading = "Loading stats…",
    adminStats7Days = "7 days",
    adminStats30Days = "30 days",
    adminStatsVisitors = "Visitors",
    adminStatsVisits = "Visits",
    adminStatsNewVisitors = "New visitors",
    adminStatsToday = "Today",
    adminStatsLast7Days = "Last 7 days",
    adminStatsLast30Days = "Last 30 days",
    adminStatsAllTime = "All time",
    adminStatsTopScreens = "Top screens",
    adminStatsLanguages = "Languages",
    adminStatsNoData = "No visits recorded yet.",
    adminStatsTimezone = "Times in UTC+7",
    adminStatsSelected = "Selected day",
)

private val Vietnamese = UiStrings(
    appTitle = "Ứng dụng One Verandah",
    serviceDetails = "Chi tiết dịch vụ",
    homePrompt = "Chúng tôi có thể giúp gì cho bạn hôm nay?",
    reviews = { count -> "($count đánh giá)" },
    sectionBookingType = "Loại đặt lịch",
    sectionDuration = "Thời lượng",
    sectionTargetArea = "Vùng cần chăm sóc",
    sectionComments = "Ghi chú",
    sectionReviews = "Đánh giá",
    minutes = { minutes -> "$minutes phút" },
    hours = { hours -> "$hours giờ" },
    from = "từ",
    total = "Tổng cộng",
    priceSummary = "Tóm tắt giá",
    tapToSelect = "Chạm vào cơ thể để chọn vùng",
    bookThisService = "Đặt dịch vụ này",
    booking = "Đang đặt…",
    bookingSuccessTitle = "Đã xác nhận đặt lịch!",
    bookingSuccessMessage = "Chúng tôi đã gửi thông tin cho người cung cấp. Hẹn gặp lại bạn!",
    done = "Xong",
    back = "Quay lại",
    menu = "Menu",
    languageName = "Tiếng Việt",
    layoutSwitchToDesktop = "Giao diện máy tính",
    layoutSwitchToMobile = "Giao diện điện thoại",
    layoutFullscreen = "Toàn màn hình",
    layoutExitFullscreen = "Thoát toàn màn hình",
    menuTitle = "Ứng dụng One Verandah",
    menuHome = "Trang chủ",
    menuAbout = "Giới thiệu",
    menuLanguage = "Ngôn ngữ",
    menuHelp = "Trợ giúp & hỗ trợ",
    menuForAdults = "Dành cho người lớn",
    menuForKids = "Dành cho trẻ em",
    close = "Đóng",
    bookingDialogTitle = "Xác nhận đặt lịch",
    zaloLabel = "Số Zalo",
    zaloPlaceholder = "Số điện thoại của bạn",
    zaloError = "Vui lòng nhập số Zalo",
    messageLabel = "Bình luận thêm",
    messagePlaceholder = "Tin nhắn tùy chọn cho nhà cung cấp",
    confirmBooking = "Xác nhận đặt lịch",
    confirm = "Xác nhận",
    male = "Nam",
    female = "Nữ",
    viewFront = "Trước",
    viewBack = "Sau",
    aboutText = "Được thực hiện bởi cư dân One Verandah, dành cho cư dân One Verandah.",
    aboutExclusive = "Được xây dựng riêng cho cộng đồng One Verandah",
    aboutTrusted = "An toàn của bạn là trên hết—chúng tôi ưu tiên những nhà cung cấp đáng tin cậy, đã được xác minh",
    aboutFree = "Hoàn toàn miễn phí, mãi mãi",
    helpSuggestFeature = "Đề xuất tính năng",
    helpSuggestFeatureSubtitle = "Cho chúng tôi biết bạn muốn thấy gì tiếp theo",
    helpFeedback = "Gửi phản hồi cho chúng tôi",
    helpFeedbackSubtitle = "Chia sẻ ý kiến của bạn để giúp chúng tôi cải thiện",
    helpReportProblem = "Báo cáo sự cố",
    helpReportProblemSubtitle = "Cho chúng tôi biết về bất kỳ vấn đề nào bạn gặp phải",
    helpSubmit = "Gửi",
    helpPlaceholder = "Nhập tin nhắn của bạn tại đây…",
    helpErrorRequired = "Vui lòng nhập tin nhắn",
    helpSuccessTitle = "Cảm ơn bạn!",
    helpSuccessMessage = "Ý kiến của bạn đã được ghi nhận. Chúng tôi rất trân trọng phản hồi của bạn!",
    helpListService = "Đăng ký dịch vụ",
    helpListServiceSubtitle = "Cung cấp dịch vụ của bạn cho cộng đồng",
    listServiceDialogTitle = "Đăng ký dịch vụ",
    serviceDescriptionLabel = "Mô tả dịch vụ",
    serviceDescriptionPlaceholder = "Bạn cung cấp những dịch vụ gì?",
    serviceDescriptionError = "Vui lòng nhập mô tả dịch vụ",
    yourNameLabel = "Tên của bạn",
    yourSexLabel = "Giới tính của bạn",
    yourNamePlaceholder = "Nhập tên của bạn",
    yourNameError = "Vui lòng nhập tên",
    blockAddressPlaceholder = "Nhập số căn hộ và tòa (ví dụ: Block A - A.12.03)",
    blockAddressError = "Vui lòng nhập căn hộ/tòa",
    listServiceSuccessTitle = "Đã gửi thông tin dịch vụ!",
    listServiceSuccessMessage = "Thông tin dịch vụ của bạn đã được gửi. Người quản trị sẽ xem xét và tự tay thêm vào ứng dụng sớm!",
    searchPlaceholder = "Tìm kiếm dịch vụ…",
    noResults = "Nhấn Enter để trò chuyện với AI.",
    providedBy = "Được cung cấp bởi",
    providerBlockLabel = "Căn hộ/Tòa",
    providerPhoneLabel = "Số điện thoại Zalo",
    providerSexLabel = "Giới tính",
    providerAboutLabel = "Giới thiệu",
    providerSkillsLabel = "Kỹ năng",
    providerSkillLevelLabel = "Cấp",
    providerServicesLabel = "Dịch vụ khác bởi",
    providerAllServicesLabel = "Tất cả dịch vụ bởi",
    providerDetails = "Thông tin nhà cung cấp",
    whenLabel = "Khi nào",
    whenNow = "Ngay bây giờ",
    whenToday = "Hôm nay",
    whenTomorrow = "Ngày mai",
    whenLater = "Sau này",
    save = "Lưu",
    saved = "Đã lưu",
    share = "Chia sẻ",
    savedToast = "Đã lưu vào danh sách Đã lưu",
    removedToast = "Đã xóa khỏi danh sách Đã lưu",
    copyLink = "Sao chép liên kết",
    copied = "Đã sao chép!",
    copiedToast = "Đã sao chép liên kết",
    pinnedRecentTitle = "Đã lưu & gần đây",
    popularTitle = "Dịch vụ phổ biến",
    clearAll = "Xóa tất cả",
    clearAllConfirm = "Xóa tất cả dịch vụ đã lưu khỏi danh sách này?",
    pinnedEmpty = "Lưu các dịch vụ bạn thích, hoặc đặt một dịch vụ để lưu vào đây!",
    homeViewServices = "Dịch vụ",
    homeViewCategories = "Danh mục",
    homeLatestStories = "Truyện mới nhất",
    homeLatestStudySessions = "Phiên học mới nhất",
    homeLatestChallenges = "Thử thách mới nhất",
    homeLatestLocations = "Địa điểm mới nhất",
    homeLatestClubs = "Câu lạc bộ mới nhất",
    homeLatestMeets = "Gặp gỡ mới nhất",
    viewAll = "Xem tất cả",
    homeFeedLoadingMore = "Đang tải thêm…",
    serviceTemporarilyUnavailable = "Dịch vụ này tạm thời không khả dụng",
    chatTitle = "Trò chuyện",
    studyTitle = "Học",
    studyPrompt = "Mài giũa ngôn ngữ theo nhịp của bạn!",
    phrasesTitle = "Câu nói",
    chatPlaceholder = "Hỏi bất cứ điều gì…",
    chatAiSender = "AI",
    chatYouSender = "Bạn",
    chatThinking = "Tui đang suy nghĩ…",
    chatError = "Đã xảy ra lỗi. Vui lòng thử lại.",
    chatPlaySentence = "Phát cả câu",
    chatPauseSentence = "Tạm dừng câu",
    chatDefinition = "Định nghĩa",
    chatInContext = "Trong câu này",
    chatResidentDetails = "Xem thông tin cư dân",
    chatSuggestions = listOf(
        "Gợi một cách dịu dàng làm quen ở hồ vô cực",
        "Dạy tôi một câu tiếng Việt sáng về sự tĩnh lặng chung",
        "Hôm nay One Verandah có phép màu nào dịu dàng?",
        "Giúp tôi biến lời nhờ vả thành Lời Huyền Linh",
    ),
    phrasesBlurb = "Dùng những lời mời Lời Huyền Linh để nâng tầm cộng đồng của bạn tại One Verandah.",
    phrasesAllLocations = "Mọi nơi",
    phrasesLoading = "Đang tải câu nói…",
    phrasesEmpty = "Chưa có câu nói. Hãy quay lại sau.",
    phrasesShowVerse = "Chạm để đọc toàn bài",
    phrasesReact = "Cảm xúc",
    adminTitle = "Quản trị",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "Nhập mật khẩu",
    adminSignIn = "Mở khóa",
    adminWrongPassword = "Mật khẩu chưa đúng.",
    adminLockedHint = "Nhập mật khẩu quản trị để quản lý câu nói và ảnh tham chiếu.",
    adminPhrasesTab = "Câu nói",
    adminPhotosTab = "Ảnh tham chiếu",
    adminCharactersTab = "Nhân vật",
    adminVoicesTab = "Giọng nhân bản",
    adminAddPhrase = "Thêm câu nói",
    adminEditPhrase = "Sửa câu nói",
    adminEdit = "Sửa",
    adminDelete = "Xóa",
    adminSave = "Lưu",
    adminSaved = "Đã lưu",
    adminError = "Đã xảy ra lỗi",
    adminLogout = "Khóa quản trị",
    adminPhotoName = "Tên ảnh",
    adminPhotoNamePlaceholder = "vd. Infinity Pool Dusk",
    adminPhotoFile = "Tệp ảnh",
    adminUploadPhoto = "Tải ảnh lên",
    adminUploading = "Đang tải…",
    adminPhotosHint = "Ảnh tham chiếu có tên dùng cho Qwen R2I để tạo cảnh thật tại căn hộ.",
    adminCharactersHint = "Ảnh chân dung nhân vật dùng khi tạo ảnh truyện.",
    adminCharactersEmpty = "Chưa có nhân vật.",
    adminVoicesHint = "Tải mẫu giọng ngắn, rõ cho từng ngôn ngữ. Mỗi ngôn ngữ một giọng đang dùng (hoặc dự phòng Mọi ngôn ngữ).",
    adminVoiceName = "Tên giọng",
    adminVoiceNamePlaceholder = "vd. am-ap-vi",
    adminVoiceLanguage = "Ngôn ngữ",
    adminVoiceLanguageAll = "Mọi ngôn ngữ",
    adminVoiceFile = "Mẫu âm thanh",
    adminUploadVoice = "Tải giọng lên",
    adminVoiceActivate = "Dùng giọng này",
    adminVoiceActive = "Đang dùng",
    adminVoiceEmpty = "Chưa có giọng nhân bản. Tải mẫu để thay giọng TTS mặc định.",
    adminDeleteVoiceConfirm = "Xóa giọng nhân bản này?",
    adminVoiceProviderId = "Id nhà cung cấp",
    adminLocationKey = "Mã địa điểm",
    adminLocationEn = "Địa điểm (English)",
    adminLocationVi = "Địa điểm (Tiếng Việt)",
    adminIcon = "Biểu tượng",
    adminSpokenEn = "Câu nói (English)",
    adminSpokenVi = "Câu nói (Tiếng Việt)",
    adminVerseEn = "Bài thơ (English)",
    adminVerseVi = "Bài thơ (Tiếng Việt)",
    adminRawAskEn = "Ý gốc (English)",
    adminRawAskVi = "Ý gốc (Tiếng Việt)",
    adminImageUrl = "URL ảnh",
    adminApproved = "Đã duyệt",
    adminUnapproved = "Chưa duyệt",
    adminDeletePhraseConfirm = "Xóa câu nói này?",
    adminDeletePhotoConfirm = "Xóa ảnh tham chiếu này?",
    adminDeleteCharacterConfirm = "Xóa nhân vật này?",
    adminCharacterStudyLocked = "Từ phiên học, nên không thể xóa.",
    adminInstructionsEn = "Hướng dẫn (English)",
    adminInstructionsVi = "Hướng dẫn (Tiếng Việt)",
    adminGeneratePhoto = "Tạo ảnh",
    adminGenerateCover = "Tạo ảnh bìa",
    adminGenerate = "Tạo",
    adminGenerating = "Đang tạo…",
    adminGeneratePrompt = "Prompt",
    adminGenerateHint = "Chọn ảnh tham chiếu căn hộ, viết prompt, và tạo cảnh bằng Qwen R2I.",
    adminGenerateNoPhotos = "Hãy tải ảnh tham chiếu trước để cảnh bám sát One Verandah.",
    adminSelectReferences = "Ảnh tham chiếu",
    adminCoverTitle = "Ảnh bìa sách câu nói",
    adminCoverHint = "Tạo ảnh bìa từ prompt và ảnh tham chiếu căn hộ.",
    adminCoverPromptDefault = "Ảnh bìa điện ảnh mộng mơ về cư dân One Verandah chia sẻ Lời Huyền Linh lúc hoàng hôn, hạt phim mềm, không chữ",
    adminPhrasePromptDefault = "Ảnh điện ảnh mộng mơ tại One Verandah, ánh sáng huyền ảo, cư dân trò chuyện dịu dàng, không chữ",
    phrasesSpokenIn = "Lời Huyền Linh",
    phrasesHowToUse = "Cách dùng",
    phrasesVerse = "Bài thơ",
    phrasesOriginStory = "Người ta kể rằng câu nói đầu tiên được nghe thấy gần hồ vô cực — không phải do cư dân nào thốt ra, mà theo gió xuyên qua khu vườn. Nó như một lời cầu nguyện xưa từ một tương lai nơi con người đã nhớ cách dịu dàng với nhau. Ai nghe được cũng bắt đầu nói theo. Nhiều câu hơn nở ra. Tòa nhà dường như thở ra chúng. Và thế là ngôn ngữ ấy ra đời — không phải được sáng tạo, mà được khám phá.",
    chatEmptyLead = "Vài cánh cửa sáng để mở — hoặc hỏi bất cứ điều gì đang sống trong bạn.",
    chatEmptyOrAsk = "Hoặc thử một tia lửa",
    chatExplorePhrases = "Lời Huyền Linh",
    chatExplorePhrasesSub = "Câu mời khởi đầu bằng Earth-Mystic Parlance",
    chatExploreStories = "Truyện mini",
    chatExploreStoriesSub = "Câu chuyện ngắn trong tòa nhà",
    storiesTitle = "Truyện mini",
    storiesComingSoon = "Câu chuyện đang hội tụ",
    storiesComingSoonBody = "Mini Stories sắp tới. Trong lúc chờ, hãy dạo phrasebook Lời Huyền Linh và để một câu mở lối.",
    storiesShelfHint = "Cách vui để hàng xóm cười cùng nhau khi học ngôn ngữ của nhau.",
    storiesEmpty = "Kệ sách còn trống. Hãy quay lại sau lần ra mắt tới.",
    storiesLoading = "Đang mở kệ sách…",
    storiesFeaturedTitle = "Xuất hiện trong truyện mini tiếp theo",
    storiesFeaturedHint = "Cho chúng tôi biết vai bạn muốn và gửi 1–3 tấm ảnh của bạn.",
    storiesFeaturedRole = "Vai bạn muốn",
    storiesFeaturedAbout = "Một chút về bạn",
    storiesFeaturedVibe = "Bạn là người như thế nào",
    storiesFeaturedWant = "Bạn muốn làm gì trong câu chuyện",
    storiesFeaturedPhotos = "1 đến 3 ảnh của bạn",
    storiesFeaturedSubmit = "Gửi",
    storiesFeaturedSuccess = "Đã gửi! Chúng tôi sẽ viết bạn vào một câu chuyện sau.",
    storiesSuggestTitle = "Gợi ý ý tưởng truyện",
    storiesSuggestHint = "Một hạt giống là đủ — một nơi, một cảm giác, một chuyện dở khóc dở cười.",
    storiesSuggestPlaceholder = "Ý tưởng của bạn",
    storiesSuggestSubmit = "Gửi ý tưởng",
    storiesSuggestSuccess = "Đã nhận ý tưởng. Cảm ơn bạn.",
    storiesSubmitError = "Chưa gửi được. Hãy thử lại.",
    storiesLearningMode = "Bật chế độ học",
    storiesContinue = "Tiếp tục",
    storiesReadAgain = "Đọc lại",
    storiesPreviousPage = "Trang trước",
    storiesTheEnd = "Hết",
    storiesPageOf = { page, total -> "Trang $page / $total" },
    storiesPages = { count -> if (count == 1) "1 trang" else "$count trang" },
    storiesStudyThisPage = "Học trang này",
    storiesPublished = "Đã xuất bản",
    storiesDraft = "Bản nháp",
    storiesOpens = { count -> "$count lượt mở" },
    adminStoriesTab = "Truyện",
    adminAddBook = "Sách mới",
    adminEditBook = "Sửa sách",
    adminBookName = "Tên sách",
    adminBookCategory = "Thể loại",
    adminBookCategoryPlaceholder = "Trinh thám, trước ngủ, xã giao…",
    adminPublish = "Xuất bản",
    adminUnpublish = "Gỡ xuất bản",
    adminConfirmLanguage = "Xác nhận ngôn ngữ của sách",
    adminPublishing = "Đang xuất bản…",
    adminAddPage = "Thêm trang",
    adminPageText = "Nội dung trang",
    adminPagePhoto = "Ảnh trang",
    adminSelectScenes = "Bối cảnh",
    adminSceneName = "Tên bối cảnh",
    adminUploadScene = "Tải bối cảnh",
    adminSelectCharacters = "Nhân vật",
    adminUploadCharacter = "Tải nhân vật",
    adminGenerateCharacter = "Tạo nhân vật",
    adminCharacterName = "Tên nhân vật",
    adminCharacterPrompt = "Mô tả nhân vật",
    adminNoBooks = "Chưa có sách.",
    chatSavedTitle = "Tin nhắn đã lưu",
    chatSavedEmpty = "Đánh dấu bất kỳ tin nhắn nào để xem lại sau trên thiết bị này.",
    chatSavedSend = "Gửi",
    chatSavedEdit = "Sửa",
    chatSavedRemove = "Xóa",
    chatExploreSaved = "Tin nhắn đã lưu",
    chatExploreSavedSub = "Dấu trang trên thiết bị này",
    chatExploreStudy = "Phiên học",
    chatExploreStudySub = "Tiếp tục học trên thiết bị này",
    chatStudySessionsTitle = "Phiên học",
    chatStudySessionsMine = "Phiên học của tôi",
    chatStudySessionsCommunity = "Từ cộng đồng",
    chatStudySessionsCommunityEmpty = "Chưa có phiên học từ cộng đồng.",
    chatStudySessionsEmpty = "Bắt đầu phiên học ở trên, hoặc chạm Học trên tin nhắn bất kỳ.",
    chatStudySessionsPlaceholder = "Dán hoặc nhập văn bản để học…",
    chatStudyExactToggle = "Văn bản gốc",
    chatStudySessionsOpen = "Mở",
    chatStudySessionsRemove = "Xóa",
    chatStudySessionsRename = "Đổi tên",
    chatStudySessionsNamePlaceholder = "Tên phiên học",
    chatStudyAnotherLanguage = "Học bài này bằng ngôn ngữ khác",
    chatStudyAnotherLanguagePlaceholder = "Nhập tên ngôn ngữ",
    chatStudyAnotherLanguageConfirm = "Tạo phiên học",
    chatStudySessionsSaveName = "Lưu tên",
    chatStudySessionsError = "Không cập nhật được phiên học. Thử lại.",
    chatSaveMessage = "Lưu tin nhắn",
    chatUnsaveMessage = "Bỏ lưu tin nhắn",
    chatSavedError = "Không cập nhật được tin đã lưu. Thử lại.",
    chatStudyMessage = "Học tin nhắn",
    chatStudyTitle = "Phiên học",
    chatWatchTab = "Xem",
    chatWatchPlay = "Phát",
    chatWatchPause = "Tạm dừng",
    chatWatchPreparing = "Đang chuẩn bị âm thanh…",
    chatWatchSeekLabel = "Tua phiên học",
    chatWatchFullscreen = "Toàn màn hình",
    chatWatchExitFullscreen = "Thoát toàn màn hình",
    chatStoryTab = "Câu chuyện",
    chatStoryContinue = "Tiếp tục",
    chatStoryBack = "Quay lại",
    chatStudyTab = "Học",
    chatWordsTab = "Từ vựng",
    chatWordsWord = "Từ",
    chatWordsMeaning = "Nghĩa",
    chatListenTab = "Nghe",
    chatReadTab = "Đọc",
    chatWriteTab = "Viết",
    chatExpressTab = "Diễn đạt",
    chatPlayTab = "Chơi",
    chatTest = StudyTestStrings(
        tab = "Kiểm tra",
        sceneLoading = "Đang tạo tình huống kiểm tra…",
        sceneError = "Không thể tạo tình huống kiểm tra.",
        audioError = "Không thể phát câu nói. Hãy thử lại.",
        retry = "Thử lại",
        listenHint = { name -> "Giải thích $name vừa nói gì bằng ngôn ngữ của bạn." },
        speakHint = "Trả lời bằng ngôn ngữ đang học và phù hợp với tình huống.",
        playLine = "Phát lại",
        beatProgress = { current, total -> "Vòng $current / $total" },
        outlineTitle = "Bạn sẽ làm gì",
        start = "Bắt đầu",
        inputPlaceholder = "Nhập hoặc nhấn giữ để nói…",
        next = "Tiếp",
        finish = "Hoàn tất bài kiểm tra",
        scoring = "Đang chấm câu trả lời…",
        scoreError = "Không thể chấm bài kiểm tra. Hãy thử lại.",
        resultTitle = "Kết quả kiểm tra",
        gradeLabel = "Điểm tổng",
        listeningLabel = "Nghe hiểu",
        speakingLabel = "Nói",
        retake = "Làm lại",
        levelBeginner = "Mới bắt đầu",
        levelElementary = "Sơ cấp",
        levelIntermediate = "Trung cấp",
        levelAdvanced = "Nâng cao",
        levelNative = "Bản ngữ",
        resultCheer = { level ->
            when (level) {
                5 -> "Bạn nghe ra và dùng được. Đúng là chuyện thật."
                4 -> "Nghe sắc, trả lời đúng lúc. Gần đến đỉnh rồi."
                3 -> "Bạn giữ được cuộc nói chuyện. Đó là một kết quả thật."
                2 -> "Bạn bắt được một phần. Làm lại sẽ khóa chặt hơn."
                else -> "Bạn đã vào cuộc. Vòng sau sẽ rõ hơn."
            }
        },
    ),
    chatAllTab = "Tất cả",
    chatReadTapHint = "Chạm một từ để tô sáng mọi chỗ xuất hiện giống hệt trong văn bản",
    chatReadPlayText = "Phát toàn bộ văn bản",
    chatReadPauseText = "Tạm dừng toàn bộ văn bản",
    chatWriteHint = "Gõ hoặc nói chính xác từng câu như hiện trên màn hình",
    chatWritePlaceholder = "Nhập câu…",
    chatWriteCorrect = "Đúng rồi!",
    chatWriteChecking = "Đang kiểm tra…",
    chatWriteFeedbackError = "Không giải thích được lần thử đó. Hãy thử lại.",
    chatExpressHint = "Diễn đạt từng ý bằng ngôn ngữ bạn đang học",
    chatListenHint = "Nghe từng câu và nhập nghĩa bằng ngôn ngữ của bạn",
    chatListenPlaceholder = "Nhập nghĩa của câu…",
    chatListenChecking = "Đang kiểm tra…",
    chatExpressPlaceholder = "Nhập câu của bạn…",
    chatExpressCorrect = "Hay lắm!",
    chatExpressChecking = "Đang kiểm tra…",
    chatExpressFeedbackError = "Không kiểm tra được lần thử đó. Hãy thử lại.",
    chatExpressLoading = "Đang gom câu luyện tập…",
    chatExpressGenerating = "Đang tạo thêm câu…",
    chatExpressEmpty = "Chưa có câu luyện. Hãy đợi nghĩa từ tải xong.",
    chatPlayHint = "Chọn một tình huống và luyện hội thoại ngắn",
    chatPlayLoading = "Đang tạo tình huống…",
    chatPlayError = "Không tải được tình huống chơi. Thử lại.",
    chatPlayRetry = "Thử lại",
    chatPlayPlaceholder = "Trả lời bằng ngôn ngữ đang học…",
    chatPlayYouLabel = "Bạn",
    chatPlayOtherLabel = "Họ",
    chatPlaySending = "Đang suy nghĩ…",
    chatStudyLoading = "Đang chuẩn bị phiên học…",
    chatStudyCreationTitle = "Đang tạo phiên học",
    chatStudyCreationText = "Văn bản học",
    chatStudyCreationStructure = "Cấu trúc phiên học",
    chatStudyCreationDissections = "Nghĩa từng từ",
    chatStudyCreationImages = "Hình ảnh",
    chatStudyCreationTest = "Kiểm tra",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "Không mở được phiên học. Thử lại.",
    chatStudyLiteral = "Nghĩa đen",
    chatStudyRegister = "Thanh ngữ / phong cách",
    chatStudyContext = "Trong ngữ cảnh này",
    chatStudySynonyms = "Từ gần nghĩa",
    chatStudyExamples = "Câu ví dụ",
    chatStudyPlaySentence = "Phát câu",
    chatStudyPauseSentence = "Tạm dừng câu",
    chatStudyTapHint = "Chạm một từ để phân tích nghĩa",
    chatStudyPreparingMeanings = "Đang tải nghĩa từ…",
    chatStudySentenceError = "Không tải được nghĩa cho câu này.",
    chatStudyRetrySentence = "Thử lại",
    chatStudyMeaningLoading = "Đang tải nghĩa…",
    chatStudyPlayground = "Tự thử dùng từ",
    chatStudyPlaygroundHint = "Viết một câu dùng từ này để kiểm tra độ tự nhiên.",
    chatStudyPlaygroundPlaceholder = "Nhập một câu có từ này…",
    chatStudyPlaygroundSubmit = "Kiểm tra câu",
    chatStudyPlaygroundNatural = "Tự nhiên",
    chatStudyPlaygroundUnnatural = "Chưa tự nhiên",
    chatStudyPlaygroundError = "Không kiểm tra được câu này. Thử lại.",
    chatStudyPlaygroundImageLoading = "Đang vẽ cảnh truyện tranh…",
    chatStudyPlaygroundImageError = "Không tạo được ảnh truyện tranh.",
    chatStudyPlaygroundImageAlt = "Cảnh truyện tranh cho từ này",
    chatStudySentenceImageLoading = "Đang tạo cảnh…",
    chatStudySentenceImageError = "Không tạo được ảnh minh họa.",
    chatStudySentenceImageAlt = "Minh họa cho câu này",
    chatStudyWordImageLoading = "Đang tạo ảnh cho từ…",
    chatStudyWordImageError = "Không tạo được ảnh cho từ.",
    chatStudyWordImageAlt = "Minh họa cho từ này",
    challengesTitle = "Thử thách",
    challengesPrompt = "Thử thách vui cùng xóm!",
    challengesStartNew = "Bắt đầu thử thách mới",
    challengesLoading = "Đang tải thử thách…",
    challengesEmpty = "Chưa có thử thách công khai. Hãy bắt đầu một thử thách cho xóm.",
    challengesInReview = "Đang xét duyệt",
    challengesProcessing = "Đang xử lý video…",
    challengesFailed = "Xử lý thất bại",
    challengesYourName = "Tên của bạn",
    challengesYourNamePlaceholder = "Hàng xóm nên gọi bạn là gì?",
    challengesPickVideo = "Chọn video (tối đa 5 phút)",
    challengesRulesTitle = "Luật thử thách",
    challengesRulePlace = "Video phải được quay tại One Verandah",
    challengesRuleFamily = "Nội dung phải phù hợp gia đình",
    challengesRuleLength = "Video nên dài 2–4 phút",
    challengesSubmit = "Gửi video",
    challengesUploading = "Đang xử lý và tải lên",
    challengesVote = "Bình chọn",
    challengesVoted = "Đã chọn",
    challengesVotes = "phiếu",
    challengesUntitled = "Thử thách mới",
    challengesEnter = "Tham gia thử thách này",
    challengesNext = "Video tiếp theo",
    challengesPrevious = "Video trước",
    challengesSubmitError = "Không gửi được video. Thử lại nhé.",
    challengesVoteError = "Không lưu được phiếu bầu.",
    chatExploreChallenges = "Thử thách",
    chatExploreChallengesSub = "Cạnh tranh vị trí quán quân trong xóm",
    chatExploreMeets = "Gặp gỡ",
    chatExploreMeetsSub = "Cà phê và hàng xóm mới",
    adminChallengesTab = "Thử thách",
    adminPendingSubmissions = "Bài mới",
    adminPublishSubmission = "Xuất bản",
    adminRenameChallenge = "Đổi tên",
    adminDeleteChallenge = "Xóa thử thách",
    adminDeleteSubmission = "Xóa bài dự thi",
    adminNoChallenges = "Chưa có thử thách.",
    adminNoPending = "Không có bài chờ duyệt.",
    meetsTitle = "Gặp gỡ",
    meetsPrompt = "Cà phê, hợp tác, và hàng xóm mới. Gửi lời mời gặp người bạn muốn nói chuyện.",
    meetsStartNew = "Đăng gặp gỡ",
    meetsLoading = "Đang tải gặp gỡ…",
    meetsEmpty = "Chưa có gặp gỡ. Hãy là người đầu tiên chào.",
    meetsInReview = "Đang chờ duyệt",
    meetsYourName = "Tên của bạn",
    meetsYourNamePlaceholder = "Mọi người nên gọi bạn là gì?",
    meetsYourPhoto = "Ảnh đẹp nhất của bạn",
    meetsPickPhoto = "Chọn ảnh",
    meetsZaloHint = "Hiển thị với mọi người khi buổi gặp đang diễn ra",
    meetsPitchLimit = "Bao nhiêu người có thể gửi lời mời?",
    meetsPitchLimitHint = "Mỗi lần gặp hoàn tất sẽ trừ một",
    meetsPitchLabel = "Lời giới thiệu",
    meetsPitchPlaceholder = "Bạn muốn gặp để nói chuyện hoặc làm gì?",
    meetsSubmit = "Đăng gặp gỡ",
    meetsUploading = "Đang tải lên",
    meetsRemaining = { count -> if (count == 1) "Còn 1 lời mời" else "Còn $count lời mời" },
    meetsPitchPerson = { name -> "Gửi lời mời $name" },
    meetsPitchReplyLabel = "Lời mời của bạn",
    meetsPitchReplyPlaceholder = "Vì sao nên gặp nhau?",
    meetsSendPitch = "Gửi lời mời",
    meetsPitchSent = "Đã gửi. Chúng tôi sẽ chuyển Zalo nếu phù hợp.",
    meetsSubmitError = "Không đăng được. Thử lại nhé.",
    meetsPitchError = "Không gửi được lời mời. Thử lại nhé.",
    meetsNoRemaining = "Đã hết lượt gặp",
    meetsUntitled = "Gặp gỡ mới",
    meetsCannotPitchOwn = "Đây là gặp gỡ của bạn",
    meetsAlreadyPitched = "Bạn đã gửi lời mời",
    adminMeetsTab = "Gặp gỡ",
    adminPendingMeets = "Gặp gỡ mới",
    adminPendingPitches = "Lời mời mới",
    adminApproveMeet = "Duyệt",
    adminCompletePitch = "Đánh dấu xong",
    adminResetRemaining = "Đặt lại lượt",
    adminResetRemainingHint = "Còn bao nhiêu lượt gặp?",
    adminDeleteMeet = "Xóa gặp gỡ",
    adminDeletePitch = "Xóa lời mời",
    adminNoMeets = "Chưa có gặp gỡ.",
    adminNoPendingMeets = "Không có gặp gỡ mới.",
    adminNoPendingPitches = "Không có lời mời mới.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "Zalo người đăng",
    adminPitchReplyZalo = "Zalo người gửi",
    locationsTitle = "Địa điểm",
    locationsPrompt = "Khám phá quán, studio và những nơi đang thành hình quanh đây.",
    locationsSearchPlaceholder = "Tìm địa điểm",
    locationsAllCategories = "Tất cả",
    locationsLoading = "Đang tải địa điểm…",
    locationsEmpty = "Chưa có địa điểm. Quay lại sau nhé.",
    locationsStatusPlanning = "Đang gây quỹ",
    locationsStatusComingSoon = "Sắp mở",
    locationsStatusOpen = "Đang mở",
    locationsStatusClosed = "Đã đóng",
    locationsClosesAt = { time -> "Đóng lúc $time" },
    locationsOpensAt = { time -> "Mở lúc $time" },
    locationsOpensDayAt = { day, time -> "Mở $day $time" },
    locationsCommitFunds = "Cam kết góp quỹ",
    locationsContact = "Liên hệ",
    locationsDirections = "Chỉ đường",
    locationsAmountLabel = "Số tiền (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "Lời nhắn",
    locationsMessagePlaceholder = "Gửi host một lời nhắn",
    locationsCommitSubmit = "Cam kết",
    locationsCommitSent = "Cảm ơn bạn — chúng tôi sẽ chuyển tới host.",
    locationsCommitError = "Không gửi được. Thử lại nhé.",
    locationsContactSubmit = "Gửi",
    locationsContactSent = "Đã gửi. Host sẽ nhắn Zalo.",
    locationsContactError = "Không gửi được tin. Thử lại nhé.",
    locationsContactPlaceholder = "Xin chào",
    locationsLikeError = "Không thích được địa điểm này.",
    locationsSuggestTitle = "Gợi ý một địa điểm",
    locationsSuggestHint = "Bạn biết quán, studio hoặc góc phố nào chúng tôi nên thêm?",
    locationsSuggestName = "Tên địa điểm",
    locationsSuggestType = "Loại địa điểm",
    locationsSuggestAddress = "Địa chỉ hoặc cách tìm",
    locationsSuggestWhy = "Vì sao hàng xóm nên biết nơi này",
    locationsSuggestSubmit = "Gửi",
    locationsSuggestSuccess = "Đã gửi! Chúng tôi sẽ xem.",
    locationsSuggestError = "Chưa gửi được. Hãy thử lại.",
    locationsUntitled = "Địa điểm mới",
    locationsDayMon = "T2",
    locationsDayTue = "T3",
    locationsDayWed = "T4",
    locationsDayThu = "T5",
    locationsDayFri = "T6",
    locationsDaySat = "T7",
    locationsDaySun = "CN",
    chatExploreLocations = "Địa điểm",
    chatExploreLocationsSub = "Quanh đây",
    adminLocationsTab = "Địa điểm",
    adminAddLocation = "Thêm địa điểm",
    adminEditLocation = "Sửa",
    adminDeleteLocation = "Xóa địa điểm",
    adminNoLocations = "Chưa có địa điểm.",
    adminLocationName = "Tên",
    adminLocationType = "Loại",
    adminLocationCategory = "Danh mục",
    adminLocationAddress = "Địa chỉ",
    adminLocationStatus = "Trạng thái",
    adminLocationHostZalo = "Zalo host",
    adminLocationMapsUrl = "Link bản đồ",
    adminLocationHighlights = "Điểm nổi bật",
    adminLocationPhotos = "Ảnh",
    adminLocationAddPhoto = "Thêm ảnh",
    adminLocationSchedule = "Giờ mở cửa (UTC+7)",
    adminLocationClosedDay = "Đóng",
    adminLocationApplyBelow = "Áp dụng bên dưới",
    adminLocationContent = "Câu chuyện",
    adminLocationAddText = "Thêm chữ",
    adminLocationAddImage = "Thêm ảnh",
    adminLocationCommitments = "Cam kết góp quỹ",
    adminLocationContacts = "Tin nhắn",
    clubsTitle = "Câu lạc bộ",
    clubsPrompt = "Khám phá câu lạc bộ — tham gia hoặc tạo câu lạc bộ của bạn.",
    clubsSearchPlaceholder = "Tìm câu lạc bộ",
    clubsAllCategories = "Tất cả",
    clubsLoading = "Đang tải câu lạc bộ…",
    clubsEmpty = "Chưa có câu lạc bộ. Hãy quay lại sau.",
    clubsUntitled = "Câu lạc bộ mới",
    clubsCreateTitle = "Tạo câu lạc bộ",
    clubsCreateHint = "Nhập tên, slogan, mô tả và cách liên hệ. Chi tiết khác sẽ được thêm sau khi chúng tôi tạo câu lạc bộ.",
    clubsCreateName = "Tên câu lạc bộ",
    clubsCreateTagline = "Slogan",
    clubsCreateDescription = "Mô tả",
    clubsCreateHostName = "Tên của bạn",
    clubsCreateSubmit = "Tạo câu lạc bộ",
    clubsCreateSuccess = "Đã gửi yêu cầu tạo câu lạc bộ.",
    clubsCreateError = "Không gửi được yêu cầu. Thử lại.",
    clubsJoin = "Tham gia",
    clubsJoinSubmit = "Tham gia",
    clubsJoinSent = "Cảm ơn! Host sẽ liên hệ với bạn qua Zalo!",
    clubsJoinError = "Không gửi được yêu cầu tham gia. Thử lại.",
    clubsYourName = "Tên của bạn",
    clubsYourMessage = "Lời nhắn",
    clubsYourMessagePlaceholder = "Bạn muốn host biết điều gì?",
    clubsHostedBy = "Tổ chức bởi",
    clubsDetails = "Chi tiết",
    adminClubsTab = "CLB",
    adminAddClub = "Thêm câu lạc bộ",
    adminEditClub = "Sửa câu lạc bộ",
    adminDeleteClub = "Xóa câu lạc bộ",
    adminNoClubs = "Chưa có câu lạc bộ.",
    adminClubName = "Tên",
    adminClubTagline = "Slogan",
    adminClubDescription = "Mô tả",
    adminClubCategory = "Danh mục",
    adminClubActivities = "Hoạt động",
    adminClubHostName = "Tên host",
    adminClubHostZalo = "Zalo host",
    adminClubPhotos = "Ảnh",
    adminClubAddPhoto = "Thêm ảnh",
    adminClubContent = "Câu chuyện",
    adminClubAddText = "Thêm chữ",
    adminClubAddImage = "Thêm ảnh",
    adminClubJoinQuestions = "Câu hỏi tham gia",
    adminClubCreateRequests = "Yêu cầu tạo CLB",
    adminClubJoinRequests = "Yêu cầu tham gia",
    adminNoClubCreateRequests = "Không có yêu cầu tạo CLB.",
    adminNoClubJoinRequests = "Không có yêu cầu tham gia.",
    adminDismissClubRequest = "Bỏ qua",
    adminDismissClubJoin = "Bỏ qua",
    adminStatsTab = "Thống kê",
    adminStatsHint = "Lượt khách theo ngày theo giờ Việt Nam (UTC+7). Cột là khách; đường là lượt xem.",
    adminStatsLoading = "Đang tải thống kê…",
    adminStats7Days = "7 ngày",
    adminStats30Days = "30 ngày",
    adminStatsVisitors = "Khách",
    adminStatsVisits = "Lượt xem",
    adminStatsNewVisitors = "Khách mới",
    adminStatsToday = "Hôm nay",
    adminStatsLast7Days = "7 ngày qua",
    adminStatsLast30Days = "30 ngày qua",
    adminStatsAllTime = "Toàn thời gian",
    adminStatsTopScreens = "Màn hình hàng đầu",
    adminStatsLanguages = "Ngôn ngữ",
    adminStatsNoData = "Chưa có lượt truy cập.",
    adminStatsTimezone = "Giờ UTC+7",
    adminStatsSelected = "Ngày đã chọn",
)

private val Chinese = UiStrings(
    appTitle = "One Verandah 应用",
    serviceDetails = "服务详情",
    homePrompt = "我们今天能为您提供什么帮助？",
    reviews = { count -> "（$count 条评论）" },
    sectionBookingType = "预订类型",
    sectionDuration = "时长",
    sectionTargetArea = "目标区域",
    sectionComments = "备注",
    sectionReviews = "评论",
    minutes = { minutes -> "$minutes 分钟" },
    hours = { hours -> "$hours 小时" },
    from = "起",
    total = "总计",
    priceSummary = "价格摘要",
    tapToSelect = "点击身体选择区域",
    bookThisService = "预订此服务",
    booking = "预订中…",
    bookingSuccessTitle = "预订已确认！",
    bookingSuccessMessage = "我们已将详情发送给服务提供商。期待与您见面！",
    done = "完成",
    back = "返回",
    menu = "菜单",
    languageName = "中文",
    layoutSwitchToDesktop = "桌面布局",
    layoutSwitchToMobile = "手机布局",
    layoutFullscreen = "全屏",
    layoutExitFullscreen = "退出全屏",
    menuTitle = "One Verandah 应用",
    menuHome = "首页",
    menuAbout = "关于",
    menuLanguage = "语言",
    menuHelp = "帮助与支持",
    menuForAdults = "成人",
    menuForKids = "儿童",
    close = "关闭",
    bookingDialogTitle = "确认您的预订",
    zaloLabel = "Zalo 号码",
    zaloPlaceholder = "您的电话号码",
    zaloError = "请输入您的 Zalo 号码",
    messageLabel = "备注",
    messagePlaceholder = "给服务提供商的可选留言",
    confirmBooking = "确认预订",
    confirm = "确认",
    male = "男",
    female = "女",
    viewFront = "正面",
    viewBack = "背面",
    aboutText = "由 One Verandah 居民制作，服务于 One Verandah 居民。",
    aboutExclusive = "专为 One Verandah 社区打造",
    aboutTrusted = "您的安全至关重要——我们优先选择可信、经过验证的服务提供者",
    aboutFree = "始终完全免费使用",
    helpSuggestFeature = "建议新功能",
    helpSuggestFeatureSubtitle = "告诉我们您希望看到的内容",
    helpFeedback = "向我们发送反馈",
    helpFeedbackSubtitle = "分享您的想法，帮助我们改进",
    helpReportProblem = "报告问题",
    helpReportProblemSubtitle = "告诉我们您发现的任何问题",
    helpSubmit = "提交",
    helpPlaceholder = "在此输入您的留言…",
    helpErrorRequired = "内容不能为空",
    helpSuccessTitle = "谢谢您！",
    helpSuccessMessage = "我们已收到您的提交。感谢您的反馈！",
    helpListService = "发布服务",
    helpListServiceSubtitle = "向社区提供您的服务",
    listServiceDialogTitle = "发布服务",
    serviceDescriptionLabel = "描述服务",
    serviceDescriptionPlaceholder = "您提供什么服务？",
    serviceDescriptionError = "请输入服务描述",
    yourNameLabel = "您的姓名",
    yourSexLabel = "您的性别",
    yourNamePlaceholder = "请输入您的姓名",
    yourNameError = "请输入姓名",
    blockAddressPlaceholder = "输入楼栋和单元号 (例如 Block A - A.12.03)",
    blockAddressError = "楼栋/地址是必填项",
    listServiceSuccessTitle = "服务申请已提交！",
    listServiceSuccessMessage = "您的 service 上市详情已发送。平台管理员将很快进行审核并手动添加！",
    searchPlaceholder = "搜索服务…",
    noResults = "按 Enter 键进入 AI 聊天。",
    providedBy = "服务提供者",
    providerBlockLabel = "楼栋/地址",
    providerPhoneLabel = "Zalo 电话",
    providerSexLabel = "性别",
    providerAboutLabel = "简介",
    providerSkillsLabel = "技能",
    providerSkillLevelLabel = "等级",
    providerServicesLabel = "其他服务由",
    providerAllServicesLabel = "全部服务由",
    providerDetails = "服务提供者详情",
    whenLabel = "何时",
    whenNow = "现在",
    whenToday = "今天",
    whenTomorrow = "明天",
    whenLater = "稍后",
    save = "收藏",
    saved = "已收藏",
    share = "分享",
    savedToast = "已保存到您的收藏列表",
    removedToast = "已从收藏中移除",
    copyLink = "复制链接",
    copied = "已复制！",
    copiedToast = "链接已复制到剪贴板",
    pinnedRecentTitle = "收藏与最近",
    popularTitle = "热门服务",
    clearAll = "清除全部",
    clearAllConfirm = "从此列表中移除所有已保存的服务吗？",
    pinnedEmpty = "收藏您喜欢的服务，或预订一个将其收藏在这里！",
    homeViewServices = "服务",
    homeViewCategories = "分类",
    homeLatestStories = "最新故事",
    homeLatestStudySessions = "最新学习会话",
    homeLatestChallenges = "最新挑战",
    homeLatestLocations = "最新地点",
    homeLatestClubs = "最新俱乐部",
    homeLatestMeets = "最新见面",
    viewAll = "查看全部",
    homeFeedLoadingMore = "正在加载更多…",
    serviceTemporarilyUnavailable = "该服务暂时不可用",
    chatTitle = "聊天",
    studyTitle = "学习",
    studyPrompt = "按自己的节奏把语言啃下来！",
    phrasesTitle = "短语",
    chatPlaceholder = "问任何问题…",
    chatAiSender = "AI",
    chatYouSender = "您",
    chatThinking = "我在想…",
    chatError = "出现问题，请重试。",
    chatPlaySentence = "播放整句",
    chatPauseSentence = "暂停整句",
    chatDefinition = "定义",
    chatInContext = "在本句中的含义",
    chatResidentDetails = "查看居民详情",
    chatSuggestions = listOf(
        "分享一个实用的公寓养护技巧",
        "教我一句实用的越南语",
        "今天在 One Verandah 可以做什么？",
    ),
    phrasesBlurb = "使用地球秘语邀请，增强 One Verandah 的社区。",
    phrasesAllLocations = "全部地点",
    phrasesLoading = "正在加载短语…",
    phrasesEmpty = "暂无短语，请稍后再来。",
    phrasesShowVerse = "点按阅读完整诗句",
    phrasesReact = "回应",
    adminTitle = "管理",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "输入密码",
    adminSignIn = "解锁",
    adminWrongPassword = "密码不正确。",
    adminLockedHint = "输入管理密码以管理短语和参考照片。",
    adminPhrasesTab = "短语",
    adminPhotosTab = "参考照片",
    adminCharactersTab = "角色",
    adminVoicesTab = "克隆音色",
    adminAddPhrase = "添加短语",
    adminEditPhrase = "编辑短语",
    adminEdit = "编辑",
    adminDelete = "删除",
    adminSave = "保存",
    adminSaved = "已保存",
    adminError = "出了点问题",
    adminLogout = "锁定管理",
    adminPhotoName = "照片名称",
    adminPhotoNamePlaceholder = "例如 Infinity Pool Dusk",
    adminPhotoFile = "照片文件",
    adminUploadPhoto = "上传照片",
    adminUploading = "上传中…",
    adminPhotosHint = "命名参考照片用于 Qwen R2I，生成真实公寓场景。",
    adminCharactersHint = "生成故事照片时使用的角色肖像。",
    adminCharactersEmpty = "还没有角色。",
    adminVoicesHint = "按语言上传简短清晰的样本。每种语言一个启用音色（或“全部语言”回退）。",
    adminVoiceName = "音色名称",
    adminVoiceNamePlaceholder = "例如 warm-vi",
    adminVoiceLanguage = "语言",
    adminVoiceLanguageAll = "全部语言",
    adminVoiceFile = "音频样本",
    adminUploadVoice = "上传音色",
    adminVoiceActivate = "使用此音色",
    adminVoiceActive = "使用中",
    adminVoiceEmpty = "还没有克隆音色。上传样本以替换默认 TTS 音色。",
    adminDeleteVoiceConfirm = "删除此克隆音色？",
    adminVoiceProviderId = "提供方 ID",
    adminLocationKey = "地点键",
    adminLocationEn = "地点（英文）",
    adminLocationVi = "地点（越南文）",
    adminIcon = "图标",
    adminSpokenEn = "口语（英文）",
    adminSpokenVi = "口语（越南文）",
    adminVerseEn = "诗句（英文）",
    adminVerseVi = "诗句（越南文）",
    adminRawAskEn = "原意（英文）",
    adminRawAskVi = "原意（越南文）",
    adminImageUrl = "图片链接",
    adminApproved = "已通过",
    adminUnapproved = "未通过",
    adminDeletePhraseConfirm = "删除这条短语？",
    adminDeletePhotoConfirm = "删除这张参考照片？",
    adminDeleteCharacterConfirm = "删除这个角色？",
    adminCharacterStudyLocked = "来自学习会话，因此无法删除。",
    adminInstructionsEn = "使用说明（英文）",
    adminInstructionsVi = "使用说明（越南文）",
    adminGeneratePhoto = "生成照片",
    adminGenerateCover = "生成封面",
    adminGenerate = "生成",
    adminGenerating = "生成中…",
    adminGeneratePrompt = "提示词",
    adminGenerateHint = "选择公寓参考照片、写下提示词，用 Qwen R2I 生成场景。",
    adminGenerateNoPhotos = "请先上传参考照片，让场景真正落在 One Verandah。",
    adminSelectReferences = "参考照片",
    adminCoverTitle = "短语册封面",
    adminCoverHint = "用提示词和公寓参考照片生成封面。",
    adminCoverPromptDefault = "梦幻电影感封面：One Verandah 住户在金色时刻轻声分享地球秘语，柔和胶片颗粒，无文字",
    adminPhrasePromptDefault = "One Verandah 梦幻电影感照片，柔神秘光，住户轻柔交谈，无文字",
    phrasesSpokenIn = "以地球秘语说出",
    phrasesHowToUse = "如何使用",
    phrasesVerse = "诗句",
    phrasesOriginStory = "据说第一句秘语是在无边泳池边被听见的——不是哪位住户说出口，而是随风穿过花园传来。它听起来像来自未来的古老祈愿，在那里人们重新记得如何彼此温柔。听见的人开始跟着说。更多句子绽放。整栋楼仿佛把它们呼出。于是这种语言诞生了——不是被发明，而是被发现。",
    chatEmptyLead = "几扇发光的门可以推开——或问任何在你心里活着的事。",
    chatEmptyOrAsk = "或试一试火花",
    chatExplorePhrases = "地球秘语短语",
    chatExplorePhrasesSub = "Lời Huyền Linh 的温柔开场",
    chatExploreStories = "迷你故事",
    chatExploreStoriesSub = "来自这栋楼的短篇",
    storiesTitle = "迷你故事",
    storiesComingSoon = "故事正在汇聚",
    storiesComingSoonBody = "迷你故事即将到来。与此同时，逛逛地球秘语短语册，让一句开一扇门。",
    storiesShelfHint = "让邻居一起笑、一起学彼此的语言。",
    storiesEmpty = "书架上还没有故事。下次发布后再来看看。",
    storiesLoading = "正在打开书架…",
    storiesFeaturedTitle = "出现在下一则迷你故事里",
    storiesFeaturedHint = "告诉我们你想扮演的角色，并上传 1–3 张你的照片。",
    storiesFeaturedRole = "你想要的角色",
    storiesFeaturedAbout = "关于你",
    storiesFeaturedVibe = "你是什么样的人",
    storiesFeaturedWant = "你想在故事里做什么",
    storiesFeaturedPhotos = "1 到 3 张你的照片",
    storiesFeaturedSubmit = "提交",
    storiesFeaturedSuccess = "已送出！我们会把你写进之后的故事。",
    storiesSuggestTitle = "建议一个故事点子",
    storiesSuggestHint = "一颗种子就够——一个地方、一种感觉、一件邻里趣事。",
    storiesSuggestPlaceholder = "你的故事点子",
    storiesSuggestSubmit = "发送点子",
    storiesSuggestSuccess = "已收到点子，谢谢。",
    storiesSubmitError = "刚才没能发送，请再试一次。",
    storiesLearningMode = "开启学习模式",
    storiesContinue = "继续",
    storiesReadAgain = "再读一遍",
    storiesPreviousPage = "上一页",
    storiesTheEnd = "完",
    storiesPageOf = { page, total -> "第 $page / $total 页" },
    storiesPages = { count -> "$count 页" },
    storiesStudyThisPage = "学习这一页",
    storiesPublished = "已发布",
    storiesDraft = "草稿",
    storiesOpens = { count -> "${count} 次打开" },
    adminStoriesTab = "故事",
    adminAddBook = "新书",
    adminEditBook = "编辑书",
    adminBookName = "书名",
    adminBookCategory = "类别",
    adminBookCategoryPlaceholder = "悬疑、睡前、社交…",
    adminPublish = "发布",
    adminUnpublish = "取消发布",
    adminConfirmLanguage = "确认这本书的语言",
    adminPublishing = "正在发布…",
    adminAddPage = "添加页",
    adminPageText = "页文",
    adminPagePhoto = "页图",
    adminSelectScenes = "场景",
    adminSceneName = "场景名",
    adminUploadScene = "上传场景",
    adminSelectCharacters = "角色",
    adminUploadCharacter = "上传角色",
    adminGenerateCharacter = "生成角色",
    adminCharacterName = "角色名",
    adminCharacterPrompt = "角色描述",
    adminNoBooks = "还没有书。",
    chatSavedTitle = "已保存消息",
    chatSavedEmpty = "收藏任意消息，之后可在本设备查看。",
    chatSavedSend = "发送",
    chatSavedEdit = "编辑",
    chatSavedRemove = "移除",
    chatExploreSaved = "已保存消息",
    chatExploreSavedSub = "本设备上的收藏",
    chatExploreStudy = "学习会话",
    chatExploreStudySub = "在本设备继续学习",
    chatStudySessionsTitle = "学习会话",
    chatStudySessionsMine = "我的学习会话",
    chatStudySessionsCommunity = "来自社区",
    chatStudySessionsCommunityEmpty = "暂无社区学习会话。",
    chatStudySessionsEmpty = "在上方开始一个会话，或在任意聊天消息上点学习。",
    chatStudySessionsPlaceholder = "粘贴或输入要学习的文本…",
    chatStudyExactToggle = "原文",
    chatStudySessionsOpen = "打开",
    chatStudySessionsRemove = "移除",
    chatStudySessionsRename = "重命名",
    chatStudySessionsNamePlaceholder = "会话名称",
    chatStudyAnotherLanguage = "用另一种语言学习这一课",
    chatStudyAnotherLanguagePlaceholder = "输入语言",
    chatStudyAnotherLanguageConfirm = "创建会话",
    chatStudySessionsSaveName = "保存名称",
    chatStudySessionsError = "无法更新学习会话，请重试。",
    chatSaveMessage = "保存消息",
    chatUnsaveMessage = "取消保存",
    chatSavedError = "无法更新已保存消息，请重试。",
    chatStudyMessage = "学习消息",
    chatStudyTitle = "学习会话",
    chatWatchTab = "观看",
    chatWatchPlay = "播放",
    chatWatchPause = "暂停",
    chatWatchPreparing = "正在准备音频…",
    chatWatchSeekLabel = "拖动浏览会话",
    chatWatchFullscreen = "全屏",
    chatWatchExitFullscreen = "退出全屏",
    chatStoryTab = "故事",
    chatStoryContinue = "继续",
    chatStoryBack = "返回",
    chatStudyTab = "学习",
    chatWordsTab = "词汇",
    chatWordsWord = "词语",
    chatWordsMeaning = "含义",
    chatListenTab = "听力",
    chatReadTab = "阅读",
    chatWriteTab = "书写",
    chatExpressTab = "表达",
    chatPlayTab = "扮演",
    chatTest = StudyTestStrings(
        tab = "测试",
        sceneLoading = "正在生成测试场景…",
        sceneError = "无法生成测试场景。",
        audioError = "无法播放台词，请重试。",
        retry = "重试",
        listenHint = { name -> "用你的语言说明${name}说了什么。" },
        speakHint = "用正在学习的语言回答，并符合当前情境。",
        playLine = "重播",
        beatProgress = { current, total -> "第 $current 轮，共 $total 轮" },
        outlineTitle = "你要做的事",
        start = "开始",
        inputPlaceholder = "输入或按住说话…",
        next = "下一步",
        finish = "完成测试",
        scoring = "正在评分…",
        scoreError = "无法评分，请重试。",
        resultTitle = "测试结果",
        gradeLabel = "总评",
        listeningLabel = "听力理解",
        speakingLabel = "口语表达",
        retake = "重测",
        levelBeginner = "初学者",
        levelElementary = "初级",
        levelIntermediate = "中级",
        levelAdvanced = "高级",
        levelNative = "母语水平",
        resultCheer = { level ->
            when (level) {
                5 -> "你听懂了，也用上了。这就是真正的对话。"
                4 -> "听得准，答得有用。已经很接近顶尖。"
                3 -> "你把对话接住了。这是实打实的中间水平。"
                2 -> "你抓住了一部分。再来一次会更稳。"
                else -> "你已经进入对话。下一轮会更清楚。"
            }
        },
    ),
    chatAllTab = "全部",
    chatReadTapHint = "点按词语以高亮全文中所有相同词语",
    chatReadPlayText = "播放全文",
    chatReadPauseText = "暂停全文",
    chatWriteHint = "按显示内容准确输入或说出每一句",
    chatWritePlaceholder = "输入句子…",
    chatWriteCorrect = "正确！",
    chatWriteChecking = "正在检查…",
    chatWriteFeedbackError = "无法解释这次尝试。请再试一次。",
    chatExpressHint = "用你正在学习的语言表达每个意思",
    chatListenHint = "听每个句子，并用你的语言输入意思",
    chatListenPlaceholder = "输入句子的意思…",
    chatListenChecking = "正在检查…",
    chatExpressPlaceholder = "输入你的句子…",
    chatExpressCorrect = "很棒！",
    chatExpressChecking = "正在检查…",
    chatExpressFeedbackError = "无法检查这次尝试。请再试一次。",
    chatExpressLoading = "正在收集练习句…",
    chatExpressGenerating = "正在生成更多句子…",
    chatExpressEmpty = "还没有练习句。请等待词义加载完成。",
    chatPlayHint = "选择场景，练习一段简短对话",
    chatPlayLoading = "正在生成场景…",
    chatPlayError = "无法加载扮演场景。请重试。",
    chatPlayRetry = "重试",
    chatPlayPlaceholder = "用学习语言回复…",
    chatPlayYouLabel = "你",
    chatPlayOtherLabel = "对方",
    chatPlaySending = "思考中…",
    chatStudyLoading = "正在准备学习会话…",
    chatStudyCreationTitle = "正在创建学习会话",
    chatStudyCreationText = "学习文本",
    chatStudyCreationStructure = "会话结构",
    chatStudyCreationDissections = "词语释义",
    chatStudyCreationImages = "图片",
    chatStudyCreationTest = "测试",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "无法开始学习会话，请重试。",
    chatStudyLiteral = "字面意思",
    chatStudyRegister = "语体",
    chatStudyContext = "在此语境中",
    chatStudySynonyms = "近义词",
    chatStudyExamples = "例句",
    chatStudyPlaySentence = "播放句子",
    chatStudyPauseSentence = "暂停句子",
    chatStudyTapHint = "点按词语以剖析含义",
    chatStudyPreparingMeanings = "正在加载词义…",
    chatStudySentenceError = "无法加载此句的词义。",
    chatStudyRetrySentence = "重试",
    chatStudyMeaningLoading = "正在加载含义…",
    chatStudyPlayground = "自己试一试",
    chatStudyPlaygroundHint = "用这个词写一句话，测试用法是否自然。",
    chatStudyPlaygroundPlaceholder = "输入包含这个词的句子…",
    chatStudyPlaygroundSubmit = "检查句子",
    chatStudyPlaygroundNatural = "自然",
    chatStudyPlaygroundUnnatural = "不自然",
    chatStudyPlaygroundError = "无法检查这句话。请再试一次。",
    chatStudyPlaygroundImageLoading = "正在绘制漫画场景…",
    chatStudyPlaygroundImageError = "无法生成漫画图片。",
    chatStudyPlaygroundImageAlt = "这个词的漫画场景",
    chatStudySentenceImageLoading = "正在创建场景…",
    chatStudySentenceImageError = "无法创建场景图片。",
    chatStudySentenceImageAlt = "这句话的插图",
    chatStudyWordImageLoading = "正在创建词语场景…",
    chatStudyWordImageError = "无法创建词语图片。",
    chatStudyWordImageAlt = "这个词的插图",
    challengesTitle = "挑战",
    challengesPrompt = "和邻居一起趣味挑战！",
    challengesStartNew = "开始新挑战",
    challengesLoading = "正在加载挑战…",
    challengesEmpty = "还没有公开挑战。为邻里发起一个吧。",
    challengesInReview = "审核中",
    challengesProcessing = "正在处理视频…",
    challengesFailed = "处理失败",
    challengesYourName = "你的名字",
    challengesYourNamePlaceholder = "邻居该怎么称呼你？",
    challengesPickVideo = "选择视频（最长 5 分钟）",
    challengesRulesTitle = "挑战规则",
    challengesRulePlace = "作品必须在 One Verandah 拍摄",
    challengesRuleFamily = "内容需适合全家观看",
    challengesRuleLength = "视频最好为 2–4 分钟",
    challengesSubmit = "提交视频",
    challengesUploading = "正在处理并上传",
    challengesVote = "投票",
    challengesVoted = "已投票",
    challengesVotes = "票",
    challengesUntitled = "新挑战",
    challengesEnter = "参加这个挑战",
    challengesNext = "下一个作品",
    challengesPrevious = "上一个作品",
    challengesSubmitError = "无法提交视频，请再试一次。",
    challengesVoteError = "无法保存投票。",
    chatExploreChallenges = "挑战",
    chatExploreChallengesSub = "争夺邻里第一名",
    chatExploreMeets = "见面",
    chatExploreMeetsSub = "咖啡畅聊和新邻居",
    adminChallengesTab = "挑战",
    adminPendingSubmissions = "新提交",
    adminPublishSubmission = "发布",
    adminRenameChallenge = "重命名",
    adminDeleteChallenge = "删除挑战",
    adminDeleteSubmission = "删除作品",
    adminNoChallenges = "还没有挑战。",
    adminNoPending = "没有待审核内容。",
    meetsTitle = "见面",
    meetsPrompt = "咖啡畅聊、合作机会和新邻居。向你想认识的人发出邀请。",
    meetsStartNew = "发布见面",
    meetsLoading = "正在加载见面…",
    meetsEmpty = "还没有见面。来当第一个打招呼的人吧。",
    meetsInReview = "等待审核",
    meetsYourName = "你的名字",
    meetsYourNamePlaceholder = "大家该怎么称呼你？",
    meetsYourPhoto = "你最好的照片",
    meetsPickPhoto = "选择照片",
    meetsZaloHint = "见面进行期间对所有人可见",
    meetsPitchLimit = "可以有多少人向你发出邀请？",
    meetsPitchLimitHint = "每次完成见面会减少一次",
    meetsPitchLabel = "你的介绍",
    meetsPitchPlaceholder = "你想见面聊什么或一起做什么？",
    meetsSubmit = "发布见面",
    meetsUploading = "正在上传",
    meetsRemaining = { count -> "还剩 $count 次邀请" },
    meetsPitchPerson = { name -> "邀请 $name" },
    meetsPitchReplyLabel = "你的邀请",
    meetsPitchReplyPlaceholder = "为什么该见面？",
    meetsSendPitch = "发送邀请",
    meetsPitchSent = "已发送。合适的话我们会转交你的 Zalo。",
    meetsSubmitError = "无法发布，请再试一次。",
    meetsPitchError = "无法发送邀请，请再试一次。",
    meetsNoRemaining = "这次见面已满",
    meetsUntitled = "新见面",
    meetsCannotPitchOwn = "这是你自己的见面",
    meetsAlreadyPitched = "你已经发过邀请",
    adminMeetsTab = "见面",
    adminPendingMeets = "新见面",
    adminPendingPitches = "新邀请",
    adminApproveMeet = "通过",
    adminCompletePitch = "标记完成",
    adminResetRemaining = "重置剩余次数",
    adminResetRemainingHint = "还剩多少次邀请？",
    adminDeleteMeet = "删除见面",
    adminDeletePitch = "删除邀请",
    adminNoMeets = "还没有见面。",
    adminNoPendingMeets = "没有新见面。",
    adminNoPendingPitches = "没有新邀请。",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "发布者 Zalo",
    adminPitchReplyZalo = "邀请者 Zalo",
    locationsTitle = "地点",
    locationsPrompt = "发现附近的咖啡馆、工作室和正在成形的地方。",
    locationsSearchPlaceholder = "搜索地点",
    locationsAllCategories = "全部",
    locationsLoading = "正在加载地点…",
    locationsEmpty = "还没有地点，稍后再来。",
    locationsStatusPlanning = "筹建中",
    locationsStatusComingSoon = "即将开业",
    locationsStatusOpen = "营业中",
    locationsStatusClosed = "已打烊",
    locationsClosesAt = { time -> "$time 打烊" },
    locationsOpensAt = { time -> "$time 开门" },
    locationsOpensDayAt = { day, time -> "$day $time 开门" },
    locationsCommitFunds = "承诺出资",
    locationsContact = "联系",
    locationsDirections = "路线",
    locationsAmountLabel = "金额（越南盾）",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "留言",
    locationsMessagePlaceholder = "给主理人留言",
    locationsCommitSubmit = "提交",
    locationsCommitSent = "谢谢，我们会转达。",
    locationsCommitError = "无法提交，请重试。",
    locationsContactSubmit = "发送",
    locationsContactSent = "已发送，主理人会通过 Zalo 联系你。",
    locationsContactError = "无法发送，请重试。",
    locationsContactPlaceholder = "打个招呼",
    locationsLikeError = "无法点赞。",
    locationsSuggestTitle = "推荐一个地点",
    locationsSuggestHint = "知道该加入的咖啡馆、工作室或邻里角落吗？",
    locationsSuggestName = "地点名称",
    locationsSuggestType = "这是什么地方",
    locationsSuggestAddress = "地址或怎么找到",
    locationsSuggestWhy = "邻居为什么该知道这里",
    locationsSuggestSubmit = "提交",
    locationsSuggestSuccess = "已送出！我们会看看。",
    locationsSuggestError = "刚才没能发送，请再试一次。",
    locationsUntitled = "新地点",
    locationsDayMon = "周一",
    locationsDayTue = "周二",
    locationsDayWed = "周三",
    locationsDayThu = "周四",
    locationsDayFri = "周五",
    locationsDaySat = "周六",
    locationsDaySun = "周日",
    chatExploreLocations = "地点",
    chatExploreLocationsSub = "附近的地方",
    adminLocationsTab = "地点",
    adminAddLocation = "添加地点",
    adminEditLocation = "编辑",
    adminDeleteLocation = "删除地点",
    adminNoLocations = "还没有地点。",
    adminLocationName = "名称",
    adminLocationType = "类型",
    adminLocationCategory = "分类",
    adminLocationAddress = "地址",
    adminLocationStatus = "状态",
    adminLocationHostZalo = "主理人 Zalo",
    adminLocationMapsUrl = "地图链接",
    adminLocationHighlights = "亮点",
    adminLocationPhotos = "照片",
    adminLocationAddPhoto = "添加照片",
    adminLocationSchedule = "营业时间（UTC+7）",
    adminLocationClosedDay = "休息",
    adminLocationApplyBelow = "应用到下方",
    adminLocationContent = "故事",
    adminLocationAddText = "添加文字",
    adminLocationAddImage = "添加图片",
    adminLocationCommitments = "出资承诺",
    adminLocationContacts = "留言",
    clubsTitle = "俱乐部",
    clubsPrompt = "发现俱乐部 — 加入一个，或创建你自己的。",
    clubsSearchPlaceholder = "搜索俱乐部",
    clubsAllCategories = "全部",
    clubsLoading = "正在加载俱乐部…",
    clubsEmpty = "还没有俱乐部，稍后再来。",
    clubsUntitled = "新俱乐部",
    clubsCreateTitle = "创建俱乐部",
    clubsCreateHint = "填写名称、标语、简介和联系方式。其他细节会在我们创建后补充。",
    clubsCreateName = "俱乐部名称",
    clubsCreateTagline = "标语",
    clubsCreateDescription = "简介",
    clubsCreateHostName = "你的名字",
    clubsCreateSubmit = "创建俱乐部",
    clubsCreateSuccess = "已发送创建请求。",
    clubsCreateError = "无法发送创建请求，请重试。",
    clubsJoin = "加入俱乐部",
    clubsJoinSubmit = "加入俱乐部",
    clubsJoinSent = "谢谢！主办人会通过 Zalo 联系你！",
    clubsJoinError = "无法发送加入请求，请重试。",
    clubsYourName = "你的名字",
    clubsYourMessage = "留言",
    clubsYourMessagePlaceholder = "想告诉主办人的话",
    clubsHostedBy = "主办人",
    clubsDetails = "详情",
    adminClubsTab = "俱乐部",
    adminAddClub = "添加俱乐部",
    adminEditClub = "编辑俱乐部",
    adminDeleteClub = "删除俱乐部",
    adminNoClubs = "还没有俱乐部。",
    adminClubName = "名称",
    adminClubTagline = "标语",
    adminClubDescription = "简介",
    adminClubCategory = "分类",
    adminClubActivities = "活动",
    adminClubHostName = "主办人",
    adminClubHostZalo = "主办人 Zalo",
    adminClubPhotos = "照片",
    adminClubAddPhoto = "添加照片",
    adminClubContent = "故事",
    adminClubAddText = "添加文字",
    adminClubAddImage = "添加图片",
    adminClubJoinQuestions = "加入问题",
    adminClubCreateRequests = "创建请求",
    adminClubJoinRequests = "加入请求",
    adminNoClubCreateRequests = "没有创建请求。",
    adminNoClubJoinRequests = "没有加入请求。",
    adminDismissClubRequest = "忽略",
    adminDismissClubJoin = "忽略",
    adminStatsTab = "统计",
    adminStatsHint = "按越南时间（UTC+7）统计每日独立访客。柱为访客，线为访问次数。",
    adminStatsLoading = "正在加载统计…",
    adminStats7Days = "7天",
    adminStats30Days = "30天",
    adminStatsVisitors = "访客",
    adminStatsVisits = "访问次数",
    adminStatsNewVisitors = "新访客",
    adminStatsToday = "今天",
    adminStatsLast7Days = "近7天",
    adminStatsLast30Days = "近30天",
    adminStatsAllTime = "全部",
    adminStatsTopScreens = "热门页面",
    adminStatsLanguages = "语言",
    adminStatsNoData = "暂无访问记录。",
    adminStatsTimezone = "UTC+7 时间",
    adminStatsSelected = "所选日期",
)

private val Japanese = UiStrings(
    appTitle = "One Verandah アプリ",
    serviceDetails = "サービス詳細",
    homePrompt = "本日はどのようにお手伝いできますか？",
    reviews = { count -> "（レビュー${count}件）" },
    sectionBookingType = "予約タイプ",
    sectionDuration = "所要時間",
    sectionTargetArea = "対象部位",
    sectionComments = "コメント",
    sectionReviews = "レビュー",
    minutes = { minutes -> "${minutes}分" },
    hours = { hours -> "${hours}時間" },
    from = "から",
    total = "合計",
    priceSummary = "料金の内訳",
    tapToSelect = "体をタップして部位を選択",
    bookThisService = "このサービスを予約",
    booking = "予約中…",
    bookingSuccessTitle = "予約が確定しました！",
    bookingSuccessMessage = "詳細を提供者に送信しました。またお会いしましょう！",
    done = "完了",
    back = "戻る",
    menu = "メニュー",
    languageName = "日本語",
    layoutSwitchToDesktop = "デスクトップ表示",
    layoutSwitchToMobile = "モバイル表示",
    layoutFullscreen = "全画面表示",
    layoutExitFullscreen = "全画面表示を終了",
    menuTitle = "One Verandah アプリ",
    menuHome = "ホーム",
    menuAbout = "について",
    menuLanguage = "言語",
    menuHelp = "ヘルプとサポート",
    menuForAdults = "大人向け",
    menuForKids = "子ども向け",
    close = "閉じる",
    bookingDialogTitle = "予約内容の確認",
    zaloLabel = "Zalo番号",
    zaloPlaceholder = "電話番号",
    zaloError = "Zalo番号は必須です",
    messageLabel = "追加のコメント",
    messagePlaceholder = "提供者への任意のメッセージ",
    confirmBooking = "予約を確定",
    confirm = "確定",
    male = "男性",
    female = "女性",
    viewFront = "前面",
    viewBack = "背面",
    aboutText = "One Verandahの住民によって、One Verandahの住民のために作られました。",
    aboutExclusive = "One Verandahコミュニティ専用に構築",
    aboutTrusted = "あなたの安全が最優先—信頼でき、確認済みの提供者を優先します",
    aboutFree = "いつでも完全無料で利用可能",
    helpSuggestFeature = "機能を提案",
    helpSuggestFeatureSubtitle = "次に欲しい機能をお知らせください",
    helpFeedback = "フィードバックを送る",
    helpFeedbackSubtitle = "改善のためのご意見をお聞かせください",
    helpReportProblem = "問題を報告",
    helpReportProblemSubtitle = "見つかった問題をお知らせください",
    helpSubmit = "送信",
    helpPlaceholder = "ここにメッセージを入力…",
    helpErrorRequired = "メッセージは必須です",
    helpSuccessTitle = "ありがとうございます！",
    helpSuccessMessage = "送信を受け付けました。フィードバックに感謝します！",
    helpListService = "サービスを掲載",
    helpListServiceSubtitle = "コミュニティにサービスを提供する",
    listServiceDialogTitle = "サービスを掲載",
    serviceDescriptionLabel = "サービスの説明",
    serviceDescriptionPlaceholder = "どのようなサービスを提供しますか？",
    serviceDescriptionError = "サービスの説明は必須です",
    yourNameLabel = "お名前",
    yourSexLabel = "性別",
    yourNamePlaceholder = "お名前を入力",
    yourNameError = "お名前は必須です",
    blockAddressPlaceholder = "棟と部屋番号を入力（例: Block A - A.12.03）",
    blockAddressError = "棟/住所は必須です",
    listServiceSuccessTitle = "サービス掲載を送信しました！",
    listServiceSuccessMessage = "サービス掲載の詳細を送信しました。ホストが確認し、まもなく手動で追加します！",
    searchPlaceholder = "サービスを検索…",
    noResults = "EnterキーでAIチャットへ。",
    providedBy = "提供者",
    providerBlockLabel = "棟/住所",
    providerPhoneLabel = "Zalo電話",
    providerSexLabel = "性別",
    providerAboutLabel = "紹介",
    providerSkillsLabel = "スキル",
    providerSkillLevelLabel = "Lvl",
    providerServicesLabel = "その他のサービス提供：",
    providerAllServicesLabel = "すべてのサービス提供：",
    providerDetails = "提供者の詳細",
    whenLabel = "いつ",
    whenNow = "今すぐ",
    whenToday = "今日",
    whenTomorrow = "明日",
    whenLater = "後日",
    save = "保存",
    saved = "保存済み",
    share = "共有",
    savedToast = "保存リストに追加しました",
    removedToast = "保存リストから削除しました",
    copyLink = "リンクをコピー",
    copied = "コピーしました！",
    copiedToast = "リンクをクリップボードにコピーしました",
    pinnedRecentTitle = "保存＆最近",
    popularTitle = "人気サービス",
    clearAll = "すべてクリア",
    clearAllConfirm = "このリストから保存済みサービスをすべて削除しますか？",
    pinnedEmpty = "お気に入りのサービスを保存するか、予約してここに残しましょう！",
    homeViewServices = "サービス",
    homeViewCategories = "カテゴリ",
    homeLatestStories = "最新のストーリー",
    homeLatestStudySessions = "最新の学習セッション",
    homeLatestChallenges = "最新のチャレンジ",
    homeLatestLocations = "最新のロケーション",
    homeLatestClubs = "最新のクラブ",
    homeLatestMeets = "最新のミート",
    viewAll = "すべて見る",
    homeFeedLoadingMore = "読み込み中…",
    serviceTemporarilyUnavailable = "このサービスは一時的に利用できません",
    chatTitle = "チャット",
    studyTitle = "学習",
    studyPrompt = "自分のペースで言語を磨き上げよう！",
    phrasesTitle = "短語",
    chatPlaceholder = "何でも聞いてください…",
    chatAiSender = "AI",
    chatYouSender = "あなた",
    chatThinking = "考えています…",
    chatError = "問題が発生しました。もう一度お試しください。",
    chatPlaySentence = "文を再生",
    chatPauseSentence = "文を一時停止",
    chatDefinition = "定義",
    chatInContext = "この文での意味",
    chatResidentDetails = "住民の詳細を見る",
    chatSuggestions = listOf(
        "役立つアパートのお手入れヒントを教えて",
        "役立つベトナム語のフレーズを教えて",
        "今日 One Verandah でできることは？",
    ),
    phrasesBlurb = "Earth-Mystic Parlance の招待を使い、One Verandah のコミュニティをより豊かに。",
    phrasesAllLocations = "すべての場所",
    phrasesLoading = "フレーズを読み込み中…",
    phrasesEmpty = "フレーズはまだありません。",
    phrasesShowVerse = "タップして全文を読む",
    phrasesReact = "リアクション",
    adminTitle = "管理",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "パスワードを入力",
    adminSignIn = "解除",
    adminWrongPassword = "パスワードが違います。",
    adminLockedHint = "管理パスワードでフレーズと参考写真を管理します。",
    adminPhrasesTab = "フレーズ",
    adminPhotosTab = "参考写真",
    adminCharactersTab = "キャラクター",
    adminVoicesTab = "クローン音声",
    adminAddPhrase = "フレーズを追加",
    adminEditPhrase = "フレーズを編集",
    adminEdit = "編集",
    adminDelete = "削除",
    adminSave = "保存",
    adminSaved = "保存しました",
    adminError = "エラーが発生しました",
    adminLogout = "管理をロック",
    adminPhotoName = "写真名",
    adminPhotoNamePlaceholder = "例: Infinity Pool Dusk",
    adminPhotoFile = "写真ファイル",
    adminUploadPhoto = "写真をアップロード",
    adminUploading = "アップロード中…",
    adminPhotosHint = "名前付き参考写真は Qwen R2I で実際のアパートのシーンを作るために使います。",
    adminCharactersHint = "物語の写真生成に使うキャラクターの肖像です。",
    adminCharactersEmpty = "キャラクターはまだありません。",
    adminVoicesHint = "言語ごとに短く明瞭なサンプルをアップロード。言語ごとに1つの有効音声（または全言語フォールバック）。",
    adminVoiceName = "音声名",
    adminVoiceNamePlaceholder = "例: warm-vi",
    adminVoiceLanguage = "言語",
    adminVoiceLanguageAll = "すべての言語",
    adminVoiceFile = "音声サンプル",
    adminUploadVoice = "音声をアップロード",
    adminVoiceActivate = "この音声を使う",
    adminVoiceActive = "有効",
    adminVoiceEmpty = "クローン音声はまだありません。サンプルをアップロードして既定 TTS を置き換えます。",
    adminDeleteVoiceConfirm = "このクローン音声を削除しますか？",
    adminVoiceProviderId = "プロバイダ ID",
    adminLocationKey = "場所キー",
    adminLocationEn = "場所（英語）",
    adminLocationVi = "場所（ベトナム語）",
    adminIcon = "アイコン",
    adminSpokenEn = "発話（英語）",
    adminSpokenVi = "発話（ベトナム語）",
    adminVerseEn = "詩（英語）",
    adminVerseVi = "詩（ベトナム語）",
    adminRawAskEn = "本来の意図（英語）",
    adminRawAskVi = "本来の意図（ベトナム語）",
    adminImageUrl = "画像URL",
    adminApproved = "承認済み",
    adminUnapproved = "未承認",
    adminDeletePhraseConfirm = "このフレーズを削除しますか？",
    adminDeletePhotoConfirm = "この参考写真を削除しますか？",
    adminDeleteCharacterConfirm = "このキャラクターを削除しますか？",
    adminCharacterStudyLocked = "学習セッション由来のため削除できません。",
    adminInstructionsEn = "使い方（英語）",
    adminInstructionsVi = "使い方（ベトナム語）",
    adminGeneratePhoto = "写真を生成",
    adminGenerateCover = "カバーを生成",
    adminGenerate = "生成",
    adminGenerating = "生成中…",
    adminGeneratePrompt = "プロンプト",
    adminGenerateHint = "アパートの参考写真を選び、プロンプトを書いて Qwen R2I でシーンを作ります。",
    adminGenerateNoPhotos = "まず参考写真をアップロードして、One Verandah に根ざしたシーンにしましょう。",
    adminSelectReferences = "参考写真",
    adminCoverTitle = "フレーズブックのカバー",
    adminCoverHint = "プロンプトとアパートの参考写真からカバーを生成します。",
    adminCoverPromptDefault = "One Verandah の住民がゴールデンアワーに Earth-Mystic Parlance を分かち合う夢幻のシネマティックカバー、柔らかいフィルム粒子、文字なし",
    adminPhrasePromptDefault = "One Verandah の夢幻シネマティック写真、柔らかい神秘の光、穏やかな会話、文字なし",
    phrasesSpokenIn = "Earth-Mystic Parlance で",
    phrasesHowToUse = "使い方",
    phrasesVerse = "詩",
    phrasesOriginStory = "最初のフレーズはインフィニティプールの近くで耳にしたと言われている——住民が口にしたのではなく、庭を抜ける風に乗って届いたのだと。それは、人々が再び互いへの優しさを思い出した未来からの古い祈りのようだった。聞いた人たちは自らも話し始めた。さらに多くのフレーズが咲いた。建物そのものがそれらを吐き出すようだった。こうしてその言葉は生まれた——発明されたのではなく、見出されたのだと。",
    chatEmptyLead = "開く輝く扉がいくつか — あるいは心に生きることを何でも尋ねて。",
    chatEmptyOrAsk = "または火花を試す",
    chatExplorePhrases = "Earth-Mystic フレーズ",
    chatExplorePhrasesSub = "Lời Huyền Linh の招待状",
    chatExploreStories = "ミニストーリー",
    chatExploreStoriesSub = "この建物の小さな物語",
    storiesTitle = "ミニストーリー",
    storiesComingSoon = "物語が集まっています",
    storiesComingSoonBody = "ミニストーリーはもうすぐ。その間、Earth-Mystic フレーズブックを歩き、一行に扉を開けてもらいましょう。",
    storiesShelfHint = "ご近所で笑い合いながら、お互いの言葉を学ぶ小さな本。",
    storiesEmpty = "まだ棚に物語がありません。次の公開をお待ちください。",
    storiesLoading = "棚を開いています…",
    storiesFeaturedTitle = "次のミニストーリーに登場する",
    storiesFeaturedHint = "なりたい役と、あなたのお写真を1〜3枚送ってください。",
    storiesFeaturedRole = "希望の役",
    storiesFeaturedAbout = "あなたのこと",
    storiesFeaturedVibe = "どんな人か",
    storiesFeaturedWant = "物語でしたいこと",
    storiesFeaturedPhotos = "あなたの写真 1〜3枚",
    storiesFeaturedSubmit = "送信",
    storiesFeaturedSuccess = "送りました。次の物語に書きます。",
    storiesSuggestTitle = "物語のアイデアを提案",
    storiesSuggestHint = "場所、気持ち、ご近所のハプニング。種だけで十分です。",
    storiesSuggestPlaceholder = "あなたのアイデア",
    storiesSuggestSubmit = "アイデアを送る",
    storiesSuggestSuccess = "受け取りました。ありがとう。",
    storiesSubmitError = "今は送れませんでした。もう一度どうぞ。",
    storiesLearningMode = "学習モードをオン",
    storiesContinue = "続ける",
    storiesReadAgain = "最初から読む",
    storiesPreviousPage = "前のページ",
    storiesTheEnd = "おしまい",
    storiesPageOf = { page, total -> "${page} / ${total} ページ" },
    storiesPages = { count -> "${count} ページ" },
    storiesStudyThisPage = "このページを学ぶ",
    storiesPublished = "公開中",
    storiesDraft = "下書き",
    storiesOpens = { count -> "${count} 回開く" },
    adminStoriesTab = "ストーリー",
    adminAddBook = "新しい本",
    adminEditBook = "本を編集",
    adminBookName = "書名",
    adminBookCategory = "カテゴリ",
    adminBookCategoryPlaceholder = "ミステリー、寝かしつけ、交流…",
    adminPublish = "公開",
    adminUnpublish = "非公開",
    adminConfirmLanguage = "本の言語を確認",
    adminPublishing = "公開しています…",
    adminAddPage = "ページを追加",
    adminPageText = "本文",
    adminPagePhoto = "ページ写真",
    adminSelectScenes = "場面",
    adminSceneName = "場面名",
    adminUploadScene = "場面を追加",
    adminSelectCharacters = "登場人物",
    adminUploadCharacter = "キャラクターを追加",
    adminGenerateCharacter = "キャラクターを生成",
    adminCharacterName = "名前",
    adminCharacterPrompt = "キャラクターの説明",
    adminNoBooks = "本はまだありません。",
    chatSavedTitle = "保存したメッセージ",
    chatSavedEmpty = "メッセージをブックマークすると、この端末で後から見つけられます。",
    chatSavedSend = "送信",
    chatSavedEdit = "編集",
    chatSavedRemove = "削除",
    chatExploreSaved = "保存したメッセージ",
    chatExploreSavedSub = "この端末のブックマーク",
    chatExploreStudy = "学習セッション",
    chatExploreStudySub = "この端末で学習を再開",
    chatStudySessionsTitle = "学習セッション",
    chatStudySessionsMine = "自分の学習セッション",
    chatStudySessionsCommunity = "コミュニティから",
    chatStudySessionsCommunityEmpty = "コミュニティのセッションはまだありません。",
    chatStudySessionsEmpty = "上でセッションを始めるか、チャットメッセージの学習を開いてください。",
    chatStudySessionsPlaceholder = "学習するテキストを貼り付けるか入力…",
    chatStudyExactToggle = "原文のまま",
    chatStudySessionsOpen = "開く",
    chatStudySessionsRemove = "削除",
    chatStudySessionsRename = "名前を変更",
    chatStudySessionsNamePlaceholder = "セッション名",
    chatStudyAnotherLanguage = "このレッスンを別の言語で学ぶ",
    chatStudyAnotherLanguagePlaceholder = "言語名を入力",
    chatStudyAnotherLanguageConfirm = "セッションを作成",
    chatStudySessionsSaveName = "名前を保存",
    chatStudySessionsError = "学習セッションを更新できませんでした。もう一度お試しください。",
    chatSaveMessage = "メッセージを保存",
    chatUnsaveMessage = "保存を解除",
    chatSavedError = "保存メッセージを更新できませんでした。もう一度お試しください。",
    chatStudyMessage = "メッセージを学習",
    chatStudyTitle = "学習セッション",
    chatWatchTab = "見る",
    chatWatchPlay = "再生",
    chatWatchPause = "一時停止",
    chatWatchPreparing = "音声を準備中…",
    chatWatchSeekLabel = "セッションをスクラブ",
    chatWatchFullscreen = "全画面",
    chatWatchExitFullscreen = "全画面を終了",
    chatStoryTab = "ストーリー",
    chatStoryContinue = "続ける",
    chatStoryBack = "戻る",
    chatStudyTab = "学習",
    chatWordsTab = "単語",
    chatWordsWord = "単語",
    chatWordsMeaning = "意味",
    chatListenTab = "聞く",
    chatReadTab = "読む",
    chatWriteTab = "書く",
    chatExpressTab = "表現",
    chatPlayTab = "演じる",
    chatTest = StudyTestStrings(
        tab = "テスト",
        sceneLoading = "テスト場面を作成中…",
        sceneError = "テスト場面を作成できませんでした。",
        audioError = "台詞を再生できませんでした。もう一度お試しください。",
        retry = "再試行",
        listenHint = { name -> "${name}が言ったことを、あなたの言語で説明してください。" },
        speakHint = "学習中の言語で、状況に合う返答をしてください。",
        playLine = "もう一度",
        beatProgress = { current, total -> "$current / $total ラウンド" },
        outlineTitle = "この会話ですること",
        start = "開始",
        inputPlaceholder = "入力または長押しして話す…",
        next = "次へ",
        finish = "テストを終了",
        scoring = "回答を採点中…",
        scoreError = "テストを採点できませんでした。もう一度お試しください。",
        resultTitle = "テスト結果",
        gradeLabel = "総合評価",
        listeningLabel = "聞き取り",
        speakingLabel = "会話",
        retake = "もう一度受ける",
        levelBeginner = "初心者",
        levelElementary = "初級",
        levelIntermediate = "中級",
        levelAdvanced = "上級",
        levelNative = "ネイティブ",
        resultCheer = { level ->
            when (level) {
                5 -> "聞き取り、その場で使えました。本物の会話です。"
                4 -> "聞き取りが鋭く、返答も使えます。あと一歩です。"
                3 -> "会話をつなげました。しっかりした中間地点です。"
                2 -> "一部は捉えました。もう一度でしっかりします。"
                else -> "会話に入りました。次のラウンドはもっと明確です。"
            }
        },
    ),
    chatAllTab = "すべて",
    chatReadTapHint = "単語をタップすると、本文中の同じ語がすべてハイライトされます",
    chatReadPlayText = "全文を再生",
    chatReadPauseText = "全文を一時停止",
    chatWriteHint = "表示どおりに各文を入力または話してください",
    chatWritePlaceholder = "文を入力…",
    chatWriteCorrect = "正解！",
    chatWriteChecking = "確認中…",
    chatWriteFeedbackError = "その回答を説明できませんでした。もう一度お試しください。",
    chatExpressHint = "学習中の言語で、それぞれの意味を表現しましょう",
    chatListenHint = "各文を聞いて、あなたの言語で意味を入力してください",
    chatListenPlaceholder = "文の意味を入力…",
    chatListenChecking = "確認中…",
    chatExpressPlaceholder = "文を入力…",
    chatExpressCorrect = "いいね！",
    chatExpressChecking = "確認中…",
    chatExpressFeedbackError = "その解答を確認できませんでした。もう一度お試しください。",
    chatExpressLoading = "練習文を集めています…",
    chatExpressGenerating = "さらに文を作成中…",
    chatExpressEmpty = "まだ練習文がありません。意味の読み込みを待ってください。",
    chatPlayHint = "場面を選んで短い会話を練習しましょう",
    chatPlayLoading = "シナリオを作成中…",
    chatPlayError = "プレイ用シナリオを読み込めませんでした。再試行してください。",
    chatPlayRetry = "再試行",
    chatPlayPlaceholder = "学習中の言語で返信…",
    chatPlayYouLabel = "あなた",
    chatPlayOtherLabel = "相手",
    chatPlaySending = "考え中…",
    chatStudyLoading = "学習セッションを準備しています…",
    chatStudyCreationTitle = "学習セッションを作成しています",
    chatStudyCreationText = "学習テキスト",
    chatStudyCreationStructure = "セッション構成",
    chatStudyCreationDissections = "単語の意味",
    chatStudyCreationImages = "画像",
    chatStudyCreationTest = "テスト",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "学習セッションを開始できませんでした。もう一度お試しください。",
    chatStudyLiteral = "字義",
    chatStudyRegister = "レジスター",
    chatStudyContext = "この文脈での意味",
    chatStudySynonyms = "類義語",
    chatStudyExamples = "例文",
    chatStudyPlaySentence = "文を再生",
    chatStudyPauseSentence = "文を一時停止",
    chatStudyTapHint = "単語をタップして意味を解剖",
    chatStudyPreparingMeanings = "単語の意味を読み込み中…",
    chatStudySentenceError = "この文の意味を読み込めませんでした。",
    chatStudyRetrySentence = "再試行",
    chatStudyMeaningLoading = "意味を読み込み中…",
    chatStudyPlayground = "自分で使ってみる",
    chatStudyPlaygroundHint = "この語を使った文を書いて、自然かどうかを試しましょう。",
    chatStudyPlaygroundPlaceholder = "この語を含む文を入力…",
    chatStudyPlaygroundSubmit = "文を確認",
    chatStudyPlaygroundNatural = "自然",
    chatStudyPlaygroundUnnatural = "不自然",
    chatStudyPlaygroundError = "この文を確認できませんでした。もう一度お試しください。",
    chatStudyPlaygroundImageLoading = "漫画シーンを描いています…",
    chatStudyPlaygroundImageError = "漫画画像を生成できませんでした。",
    chatStudyPlaygroundImageAlt = "この語の漫画シーン",
    chatStudySentenceImageLoading = "シーンを作成しています…",
    chatStudySentenceImageError = "シーン画像を作成できませんでした。",
    chatStudySentenceImageAlt = "この文のイラスト",
    chatStudyWordImageLoading = "単語のイラストを作成中…",
    chatStudyWordImageError = "単語の画像を作成できませんでした。",
    chatStudyWordImageAlt = "この単語のイラスト",
    challengesTitle = "チャレンジ",
    challengesPrompt = "ご近所さんと楽しむチャレンジ！",
    challengesStartNew = "新しいチャレンジを始める",
    challengesLoading = "チャレンジを読み込み中…",
    challengesEmpty = "公開中のチャレンジはまだありません。",
    challengesInReview = "審査中",
    challengesProcessing = "動画を処理しています…",
    challengesFailed = "処理に失敗しました",
    challengesYourName = "あなたの名前",
    challengesYourNamePlaceholder = "近所の人は何と呼べばいい？",
    challengesPickVideo = "動画を選ぶ（最大5分）",
    challengesRulesTitle = "チャレンジのルール",
    challengesRulePlace = "撮影は One Verandah で行うこと",
    challengesRuleFamily = "家族向けの内容であること",
    challengesRuleLength = "2〜4分が理想です",
    challengesSubmit = "動画を送る",
    challengesUploading = "処理とアップロード中",
    challengesVote = "投票",
    challengesVoted = "投票済み",
    challengesVotes = "票",
    challengesUntitled = "新しいチャレンジ",
    challengesEnter = "このチャレンジに参加",
    challengesNext = "次の投稿",
    challengesPrevious = "前の投稿",
    challengesSubmitError = "動画を送れませんでした。",
    challengesVoteError = "投票を保存できませんでした。",
    chatExploreChallenges = "チャレンジ",
    chatExploreChallengesSub = "ご近所一番を目指そう",
    chatExploreMeets = "ミート",
    chatExploreMeetsSub = "カフェと新しいご近所づきあい",
    adminChallengesTab = "チャレンジ",
    adminPendingSubmissions = "新しい投稿",
    adminPublishSubmission = "公開",
    adminRenameChallenge = "名前を変更",
    adminDeleteChallenge = "チャレンジを削除",
    adminDeleteSubmission = "投稿を削除",
    adminNoChallenges = "チャレンジはまだありません。",
    adminNoPending = "審査待ちはありません。",
    meetsTitle = "ミート",
    meetsPrompt = "カフェ、コラボ、新しいご近所づきあい。会いたい人にピッチしよう。",
    meetsStartNew = "ミートを投稿",
    meetsLoading = "ミートを読み込み中…",
    meetsEmpty = "まだミートはありません。最初のあいさつをどうぞ。",
    meetsInReview = "承認待ち",
    meetsYourName = "お名前",
    meetsYourNamePlaceholder = "なんて呼ばれたいですか？",
    meetsYourPhoto = "いちばんいい写真",
    meetsPickPhoto = "写真を選ぶ",
    meetsZaloHint = "ミートが有効な間は全員に表示されます",
    meetsPitchLimit = "何人までピッチできますか？",
    meetsPitchLimitHint = "完了したミートごとに1回減ります",
    meetsPitchLabel = "あなたのピッチ",
    meetsPitchPlaceholder = "何を話したり、一緒にしたいですか？",
    meetsSubmit = "ミートを投稿",
    meetsUploading = "アップロード中",
    meetsRemaining = { count -> "残り${count}回" },
    meetsPitchPerson = { name -> "${name}にピッチ" },
    meetsPitchReplyLabel = "あなたのピッチ",
    meetsPitchReplyPlaceholder = "なぜ会うべき？",
    meetsSendPitch = "ピッチを送る",
    meetsPitchSent = "送信しました。マッチすればZaloをお伝えします。",
    meetsSubmitError = "投稿できませんでした。",
    meetsPitchError = "ピッチを送れませんでした。",
    meetsNoRemaining = "このミートは満員です",
    meetsUntitled = "新しいミート",
    meetsCannotPitchOwn = "これはあなたのミートです",
    meetsAlreadyPitched = "すでにピッチを送りました",
    adminMeetsTab = "ミート",
    adminPendingMeets = "新しいミート",
    adminPendingPitches = "新しいピッチ",
    adminApproveMeet = "承認",
    adminCompletePitch = "完了にする",
    adminResetRemaining = "残り回数をリセット",
    adminResetRemainingHint = "残りの回数を入力",
    adminDeleteMeet = "ミートを削除",
    adminDeletePitch = "ピッチを削除",
    adminNoMeets = "ミートはまだありません。",
    adminNoPendingMeets = "新しいミートはありません。",
    adminNoPendingPitches = "新しいピッチはありません。",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "投稿者のZalo",
    adminPitchReplyZalo = "返信者のZalo",
    locationsTitle = "場所",
    locationsPrompt = "近くのカフェ、スタジオ、これから生まれる場所を見つけましょう。",
    locationsSearchPlaceholder = "場所を検索",
    locationsAllCategories = "すべて",
    locationsLoading = "読み込み中…",
    locationsEmpty = "まだ場所がありません。",
    locationsStatusPlanning = "計画中",
    locationsStatusComingSoon = "近日公開",
    locationsStatusOpen = "営業中",
    locationsStatusClosed = "閉店",
    locationsClosesAt = { time -> "${time}に閉店" },
    locationsOpensAt = { time -> "${time}に開店" },
    locationsOpensDayAt = { day, time -> "${day} ${time}に開店" },
    locationsCommitFunds = "出資する",
    locationsContact = "連絡",
    locationsDirections = "行き方",
    locationsAmountLabel = "金額（VND）",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "メッセージ",
    locationsMessagePlaceholder = "ホストへのひとこと",
    locationsCommitSubmit = "送信",
    locationsCommitSent = "ありがとうございます。ホストに伝えます。",
    locationsCommitError = "送信できませんでした。",
    locationsContactSubmit = "送る",
    locationsContactSent = "送信しました。Zaloで連絡が来ます。",
    locationsContactError = "メッセージを送れませんでした。",
    locationsContactPlaceholder = "こんにちは",
    locationsLikeError = "いいねできませんでした。",
    locationsSuggestTitle = "場所を提案する",
    locationsSuggestHint = "追加すべきカフェ、スタジオ、近所のスポットはありますか？",
    locationsSuggestName = "場所の名前",
    locationsSuggestType = "どんな場所か",
    locationsSuggestAddress = "住所または行き方",
    locationsSuggestWhy = "近所の人が知るべき理由",
    locationsSuggestSubmit = "送信",
    locationsSuggestSuccess = "送りました。確認します。",
    locationsSuggestError = "送信できませんでした。もう一度お試しください。",
    locationsUntitled = "新しい場所",
    locationsDayMon = "月",
    locationsDayTue = "火",
    locationsDayWed = "水",
    locationsDayThu = "木",
    locationsDayFri = "金",
    locationsDaySat = "土",
    locationsDaySun = "日",
    chatExploreLocations = "場所",
    chatExploreLocationsSub = "近くのスポット",
    adminLocationsTab = "場所",
    adminAddLocation = "場所を追加",
    adminEditLocation = "編集",
    adminDeleteLocation = "場所を削除",
    adminNoLocations = "まだ場所がありません。",
    adminLocationName = "名前",
    adminLocationType = "タイプ",
    adminLocationCategory = "カテゴリ",
    adminLocationAddress = "住所",
    adminLocationStatus = "ステータス",
    adminLocationHostZalo = "ホストのZalo",
    adminLocationMapsUrl = "地図URL",
    adminLocationHighlights = "ハイライト",
    adminLocationPhotos = "写真",
    adminLocationAddPhoto = "写真を追加",
    adminLocationSchedule = "営業時間（UTC+7）",
    adminLocationClosedDay = "休み",
    adminLocationApplyBelow = "以下に適用",
    adminLocationContent = "ストーリー",
    adminLocationAddText = "テキストを追加",
    adminLocationAddImage = "画像を追加",
    adminLocationCommitments = "出資",
    adminLocationContacts = "メッセージ",
    clubsTitle = "クラブ",
    clubsPrompt = "クラブを見つけよう。参加するか、自分で始めましょう。",
    clubsSearchPlaceholder = "クラブを検索",
    clubsAllCategories = "すべて",
    clubsLoading = "クラブを読み込み中…",
    clubsEmpty = "まだクラブがありません。",
    clubsUntitled = "新しいクラブ",
    clubsCreateTitle = "クラブを作成",
    clubsCreateHint = "名前、キャッチコピー、説明、連絡先を入力してください。ほかの詳細は作成後に追加されます。",
    clubsCreateName = "クラブ名",
    clubsCreateTagline = "キャッチコピー",
    clubsCreateDescription = "説明",
    clubsCreateHostName = "あなたの名前",
    clubsCreateSubmit = "クラブを作成",
    clubsCreateSuccess = "作成リクエストを送りました。",
    clubsCreateError = "作成リクエストを送れませんでした。",
    clubsJoin = "クラブに参加",
    clubsJoinSubmit = "クラブに参加",
    clubsJoinSent = "ありがとうございます！ホストからZaloでご連絡します！",
    clubsJoinError = "参加リクエストを送れませんでした。",
    clubsYourName = "あなたの名前",
    clubsYourMessage = "メッセージ",
    clubsYourMessagePlaceholder = "ホストに伝えたいこと",
    clubsHostedBy = "主催",
    clubsDetails = "詳細",
    adminClubsTab = "クラブ",
    adminAddClub = "クラブを追加",
    adminEditClub = "クラブを編集",
    adminDeleteClub = "クラブを削除",
    adminNoClubs = "クラブはまだありません。",
    adminClubName = "名前",
    adminClubTagline = "キャッチコピー",
    adminClubDescription = "説明",
    adminClubCategory = "カテゴリ",
    adminClubActivities = "アクティビティ",
    adminClubHostName = "ホスト名",
    adminClubHostZalo = "ホスト Zalo",
    adminClubPhotos = "写真",
    adminClubAddPhoto = "写真を追加",
    adminClubContent = "ストーリー",
    adminClubAddText = "テキストを追加",
    adminClubAddImage = "画像を追加",
    adminClubJoinQuestions = "参加の質問",
    adminClubCreateRequests = "作成リクエスト",
    adminClubJoinRequests = "参加リクエスト",
    adminNoClubCreateRequests = "作成リクエストはありません。",
    adminNoClubJoinRequests = "参加リクエストはありません。",
    adminDismissClubRequest = "閉じる",
    adminDismissClubJoin = "閉じる",
    adminStatsTab = "統計",
    adminStatsHint = "ベトナム時間（UTC+7）の日別訪問者。棒は訪問者、線は訪問数です。",
    adminStatsLoading = "統計を読み込み中…",
    adminStats7Days = "7日間",
    adminStats30Days = "30日間",
    adminStatsVisitors = "訪問者",
    adminStatsVisits = "訪問数",
    adminStatsNewVisitors = "新規",
    adminStatsToday = "今日",
    adminStatsLast7Days = "過去7日",
    adminStatsLast30Days = "過去30日",
    adminStatsAllTime = "全期間",
    adminStatsTopScreens = "よく見る画面",
    adminStatsLanguages = "言語",
    adminStatsNoData = "まだ訪問がありません。",
    adminStatsTimezone = "UTC+7",
    adminStatsSelected = "選択した日",
)

private val Korean = UiStrings(
    appTitle = "One Verandah 앱",
    serviceDetails = "서비스 상세",
    homePrompt = "오늘 무엇을 도와드릴까요?",
    reviews = { count -> "(리뷰 ${count}개)" },
    sectionBookingType = "예약 유형",
    sectionDuration = "소요 시간",
    sectionTargetArea = "대상 부위",
    sectionComments = "코멘트",
    sectionReviews = "리뷰",
    minutes = { minutes -> "${minutes}분" },
    hours = { hours -> "${hours}시간" },
    from = "부터",
    total = "합계",
    priceSummary = "가격 요약",
    tapToSelect = "몸을 탭하여 부위를 선택하세요",
    bookThisService = "이 서비스 예약",
    booking = "예약 중…",
    bookingSuccessTitle = "예약이 확정되었습니다!",
    bookingSuccessMessage = "세부 정보를 제공자에게 보냈습니다. 곧 뵙겠습니다!",
    done = "완료",
    back = "뒤로",
    menu = "메뉴",
    languageName = "한국어",
    layoutSwitchToDesktop = "데스크톱 레이아웃",
    layoutSwitchToMobile = "모바일 레이아웃",
    layoutFullscreen = "전체 화면",
    layoutExitFullscreen = "전체 화면 종료",
    menuTitle = "One Verandah 앱",
    menuHome = "홈",
    menuAbout = "소개",
    menuLanguage = "언어",
    menuHelp = "도움말 및 지원",
    menuForAdults = "성인용",
    menuForKids = "어린이용",
    close = "닫기",
    bookingDialogTitle = "예약 확인",
    zaloLabel = "Zalo 번호",
    zaloPlaceholder = "전화번호",
    zaloError = "Zalo 번호는 필수입니다",
    messageLabel = "추가 코멘트",
    messagePlaceholder = "제공자에게 전할 선택 메시지",
    confirmBooking = "예약 확정",
    confirm = "확인",
    male = "남성",
    female = "여성",
    viewFront = "앞면",
    viewBack = "뒷면",
    aboutText = "One Verandah 주민이 One Verandah 주민을 위해 만들었습니다.",
    aboutExclusive = "One Verandah 커뮤니티 전용으로 제작",
    aboutTrusted = "안전이 최우선—신뢰할 수 있고 검증된 제공자를 우선합니다",
    aboutFree = "언제나 완전 무료로 이용",
    helpSuggestFeature = "기능 제안",
    helpSuggestFeatureSubtitle = "다음에 보고 싶은 기능을 알려 주세요",
    helpFeedback = "피드백 보내기",
    helpFeedbackSubtitle = "개선을 위한 의견을 공유해 주세요",
    helpReportProblem = "문제 신고",
    helpReportProblemSubtitle = "발견한 문제를 알려 주세요",
    helpSubmit = "제출",
    helpPlaceholder = "여기에 메시지를 입력하세요…",
    helpErrorRequired = "메시지는 필수입니다",
    helpSuccessTitle = "감사합니다!",
    helpSuccessMessage = "제출이 접수되었습니다. 피드백에 감사드립니다!",
    helpListService = "서비스 등록",
    helpListServiceSubtitle = "커뮤니티에 서비스를 제공하세요",
    listServiceDialogTitle = "서비스 등록",
    serviceDescriptionLabel = "서비스 설명",
    serviceDescriptionPlaceholder = "어떤 서비스를 제공하시나요?",
    serviceDescriptionError = "서비스 설명은 필수입니다",
    yourNameLabel = "이름",
    yourSexLabel = "성별",
    yourNamePlaceholder = "이름을 입력하세요",
    yourNameError = "이름은 필수입니다",
    blockAddressPlaceholder = "동과 호수를 입력하세요 (예: Block A - A.12.03)",
    blockAddressError = "동/주소는 필수입니다",
    listServiceSuccessTitle = "서비스 등록이 제출되었습니다!",
    listServiceSuccessMessage = "서비스 등록 정보가 전송되었습니다. 호스트가 검토 후 곧 수동으로 추가합니다!",
    searchPlaceholder = "서비스 검색…",
    noResults = "Enter를 눌러 AI 채팅으로 이동하세요.",
    providedBy = "제공자",
    providerBlockLabel = "동/주소",
    providerPhoneLabel = "Zalo 전화",
    providerSexLabel = "성별",
    providerAboutLabel = "소개",
    providerSkillsLabel = "스킬",
    providerSkillLevelLabel = "Lvl",
    providerServicesLabel = "다른 서비스 제공:",
    providerAllServicesLabel = "모든 서비스 제공:",
    providerDetails = "제공자 상세",
    whenLabel = "언제",
    whenNow = "지금",
    whenToday = "오늘",
    whenTomorrow = "내일",
    whenLater = "나중에",
    save = "저장",
    saved = "저장됨",
    share = "공유",
    savedToast = "저장 목록에 추가되었습니다",
    removedToast = "저장 목록에서 제거되었습니다",
    copyLink = "링크 복사",
    copied = "복사됨!",
    copiedToast = "링크가 클립보드에 복사되었습니다",
    pinnedRecentTitle = "저장 및 최근",
    popularTitle = "인기 서비스",
    clearAll = "모두 지우기",
    clearAllConfirm = "이 목록에서 저장된 서비스를 모두 제거할까요?",
    pinnedEmpty = "좋아하는 서비스를 저장하거나 예약하여 여기에 남겨 두세요!",
    homeViewServices = "서비스",
    homeViewCategories = "카테고리",
    homeLatestStories = "최신 스토리",
    homeLatestStudySessions = "최신 학습 세션",
    homeLatestChallenges = "최신 챌린지",
    homeLatestLocations = "최신 장소",
    homeLatestClubs = "최신 클럽",
    homeLatestMeets = "최신 만남",
    viewAll = "모두 보기",
    homeFeedLoadingMore = "더 불러오는 중…",
    serviceTemporarilyUnavailable = "이 서비스는 일시적으로 이용할 수 없습니다",
    chatTitle = "채팅",
    studyTitle = "학습",
    studyPrompt = "자기 페이스로 언어를 파고드세요!",
    phrasesTitle = "단어",
    chatPlaceholder = "무엇이든 물어보세요…",
    chatAiSender = "AI",
    chatYouSender = "나",
    chatThinking = "생각 중…",
    chatError = "문제가 발생했습니다. 다시 시도해 주세요.",
    chatPlaySentence = "문장 재생",
    chatPauseSentence = "문장 일시정지",
    chatDefinition = "정의",
    chatInContext = "이 문장에서의 의미",
    chatResidentDetails = "주민 상세 보기",
    chatSuggestions = listOf(
        "유용한 아파트 관리 팁을 알려 주세요",
        "유용한 베트남어 표현을 가르쳐 주세요",
        "오늘 One Verandah에서 무엇을 할 수 있나요?",
    ),
    phrasesBlurb = "Earth-Mystic Parlance 초대를 사용해 One Verandah 커뮤니티를 더 풍요롭게 하세요.",
    phrasesAllLocations = "모든 장소",
    phrasesLoading = "문구 불러오는 중…",
    phrasesEmpty = "아직 문구가 없습니다.",
    phrasesShowVerse = "탭하여 전체 시 읽기",
    phrasesReact = "반응",
    adminTitle = "관리",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "비밀번호 입력",
    adminSignIn = "잠금 해제",
    adminWrongPassword = "비밀번호가 올바르지 않습니다.",
    adminLockedHint = "관리자 비밀번호로 문구와 참고 사진을 관리하세요.",
    adminPhrasesTab = "문구",
    adminPhotosTab = "참고 사진",
    adminCharactersTab = "캐릭터",
    adminVoicesTab = "복제 음성",
    adminAddPhrase = "문구 추가",
    adminEditPhrase = "문구 편집",
    adminEdit = "편집",
    adminDelete = "삭제",
    adminSave = "저장",
    adminSaved = "저장됨",
    adminError = "문제가 발생했습니다",
    adminLogout = "관리 잠금",
    adminPhotoName = "사진 이름",
    adminPhotoNamePlaceholder = "예: Infinity Pool Dusk",
    adminPhotoFile = "사진 파일",
    adminUploadPhoto = "사진 업로드",
    adminUploading = "업로드 중…",
    adminPhotosHint = "이름이 지정된 참고 사진은 Qwen R2I로 실제 아파트 장면을 만드는 데 사용됩니다.",
    adminCharactersHint = "이야기 사진 생성에 쓰는 캐릭터 초상입니다.",
    adminCharactersEmpty = "캐릭터가 아직 없습니다.",
    adminVoicesHint = "언어별로 짧고 깨끗한 샘플을 업로드하세요. 언어당 활성 음성 하나(또는 모든 언어 대체).",
    adminVoiceName = "음성 이름",
    adminVoiceNamePlaceholder = "예: warm-vi",
    adminVoiceLanguage = "언어",
    adminVoiceLanguageAll = "모든 언어",
    adminVoiceFile = "오디오 샘플",
    adminUploadVoice = "음성 업로드",
    adminVoiceActivate = "이 음성 사용",
    adminVoiceActive = "활성",
    adminVoiceEmpty = "복제 음성이 없습니다. 샘플을 업로드해 기본 TTS 음성을 바꾸세요.",
    adminDeleteVoiceConfirm = "이 복제 음성을 삭제할까요?",
    adminVoiceProviderId = "제공자 ID",
    adminLocationKey = "위치 키",
    adminLocationEn = "위치 (영어)",
    adminLocationVi = "위치 (베트남어)",
    adminIcon = "아이콘",
    adminSpokenEn = "말하기 (영어)",
    adminSpokenVi = "말하기 (베트남어)",
    adminVerseEn = "시 (영어)",
    adminVerseVi = "시 (베트남어)",
    adminRawAskEn = "원래 의도 (영어)",
    adminRawAskVi = "원래 의도 (베트남어)",
    adminImageUrl = "이미지 URL",
    adminApproved = "승인됨",
    adminUnapproved = "미승인",
    adminDeletePhraseConfirm = "이 문구를 삭제할까요?",
    adminDeletePhotoConfirm = "이 참고 사진을 삭제할까요?",
    adminDeleteCharacterConfirm = "이 캐릭터를 삭제할까요?",
    adminCharacterStudyLocked = "학습 세션에서 가져온 것이라 삭제할 수 없습니다.",
    adminInstructionsEn = "사용법 (영어)",
    adminInstructionsVi = "사용법 (베트남어)",
    adminGeneratePhoto = "사진 생성",
    adminGenerateCover = "커버 생성",
    adminGenerate = "생성",
    adminGenerating = "생성 중…",
    adminGeneratePrompt = "프롬프트",
    adminGenerateHint = "아파트 참고 사진을 고르고 프롬프트를 적어 Qwen R2I로 장면을 만듭니다.",
    adminGenerateNoPhotos = "먼저 참고 사진을 올려 One Verandah에 뿌리를 둔 장면을 만드세요.",
    adminSelectReferences = "참고 사진",
    adminCoverTitle = "문구집 커버",
    adminCoverHint = "프롬프트와 아파트 참고 사진으로 커버를 생성합니다.",
    adminCoverPromptDefault = "골든아워에 One Verandah 주민들이 Earth-Mystic Parlance를 나누는 몽환적 시네마틱 커버, 부드러운 필름 그레인, 텍스트 없음",
    adminPhrasePromptDefault = "One Verandah의 몽환적 시네마틱 사진, 부드러운 신비로운 빛, 부드러운 대화, 텍스트 없음",
    phrasesSpokenIn = "Earth-Mystic Parlance로",
    phrasesHowToUse = "사용 방법",
    phrasesVerse = "시",
    phrasesOriginStory = "첫 문장은 인피니티 풀 근처에서 들렸다고 한다——어떤 주민이 말한 것이 아니라, 정원을 스치는 바람에 실려 왔다고. 그것은 사람들이 서로에게 다정해지는 법을 다시 기억한 미래에서 온 오래된 기도처럼 들렸다. 들은 이들은 스스로 말하기 시작했다. 더 많은 문장이 피어났다. 건물 자체가 그것들을 내쉬는 듯했다. 그렇게 그 말은 태어났다——발명된 것이 아니라, 발견된 것이다.",
    chatEmptyLead = "열 수 있는 빛나는 문들 — 또는 당신 안에 살아있는 무엇이든 물어보세요.",
    chatEmptyOrAsk = "또는 불꽃을 시도",
    chatExplorePhrases = "Earth-Mystic 문구",
    chatExplorePhrasesSub = "Lời Huyền Linh 시작 초대",
    chatExploreStories = "미니 스토리",
    chatExploreStoriesSub = "건물에서 온 짧은 이야기",
    storiesTitle = "미니 스토리",
    storiesComingSoon = "이야기가 모이고 있어요",
    storiesComingSoonBody = "미니 스토리가 곧 옵니다. 그동안 Earth-Mystic 문구집을 거닐며 한 줄이 문을 열게 하세요.",
    storiesShelfHint = "이웃이 함께 웃으며 서로의 언어를 배우는 작은 책.",
    storiesEmpty = "아직 선반에 이야기가 없어요. 다음 공개를 기다려 주세요.",
    storiesLoading = "선반을 여는 중…",
    storiesFeaturedTitle = "다음 미니 스토리에 등장하기",
    storiesFeaturedHint = "원하는 역할과 사진 1–3장을 보내 주세요.",
    storiesFeaturedRole = "원하는 역할",
    storiesFeaturedAbout = "나에 대해",
    storiesFeaturedVibe = "나는 어떤 사람인지",
    storiesFeaturedWant = "이야기에서 하고 싶은 일",
    storiesFeaturedPhotos = "내 사진 1–3장",
    storiesFeaturedSubmit = "보내기",
    storiesFeaturedSuccess = "보냈어요! 다음 이야기에 담을게요.",
    storiesSuggestTitle = "이야기 아이디어 제안",
    storiesSuggestHint = "장소, 느낌, 이웃의 해프닝. 씨앗이면 충분해요.",
    storiesSuggestPlaceholder = "아이디어",
    storiesSuggestSubmit = "아이디어 보내기",
    storiesSuggestSuccess = "받았어요. 고마워요.",
    storiesSubmitError = "지금은 보내지 못했어요. 다시 시도해 주세요.",
    storiesLearningMode = "학습 모드 켜기",
    storiesContinue = "계속",
    storiesReadAgain = "다시 읽기",
    storiesPreviousPage = "이전 쪽",
    storiesTheEnd = "끝",
    storiesPageOf = { page, total -> "$page / ${total}쪽" },
    storiesPages = { count -> "${count}쪽" },
    storiesStudyThisPage = "이 쪽 공부하기",
    storiesPublished = "공개됨",
    storiesDraft = "초안",
    storiesOpens = { count -> "열람 $count" },
    adminStoriesTab = "스토리",
    adminAddBook = "새 책",
    adminEditBook = "책 편집",
    adminBookName = "책 제목",
    adminBookCategory = "장르",
    adminBookCategoryPlaceholder = "미스터리, 잠자리, 사교…",
    adminPublish = "공개",
    adminUnpublish = "비공개",
    adminConfirmLanguage = "책의 언어 확인",
    adminPublishing = "공개 중…",
    adminAddPage = "쪽 추가",
    adminPageText = "본문",
    adminPagePhoto = "쪽 사진",
    adminSelectScenes = "장면",
    adminSceneName = "장면 이름",
    adminUploadScene = "장면 올리기",
    adminSelectCharacters = "인물",
    adminUploadCharacter = "인물 올리기",
    adminGenerateCharacter = "인물 만들기",
    adminCharacterName = "이름",
    adminCharacterPrompt = "인물 설명",
    adminNoBooks = "아직 책이 없어요.",
    chatSavedTitle = "저장된 메시지",
    chatSavedEmpty = "메시지를 북마크하면 이 기기에서 나중에 다시 볼 수 있어요.",
    chatSavedSend = "보내기",
    chatSavedEdit = "편집",
    chatSavedRemove = "삭제",
    chatExploreSaved = "저장된 메시지",
    chatExploreSavedSub = "이 기기의 북마크",
    chatExploreStudy = "학습 세션",
    chatExploreStudySub = "이 기기에서 학습 이어하기",
    chatStudySessionsTitle = "학습 세션",
    chatStudySessionsMine = "내 학습 세션",
    chatStudySessionsCommunity = "커뮤니티에서",
    chatStudySessionsCommunityEmpty = "아직 커뮤니티 세션이 없습니다.",
    chatStudySessionsEmpty = "위에서 세션을 시작하거나, 채팅 메시지에서 학습을 여세요.",
    chatStudySessionsPlaceholder = "학습할 텍스트를 붙이거나 입력…",
    chatStudyExactToggle = "원문 그대로",
    chatStudySessionsOpen = "열기",
    chatStudySessionsRemove = "삭제",
    chatStudySessionsRename = "이름 바꾸기",
    chatStudySessionsNamePlaceholder = "세션 이름",
    chatStudyAnotherLanguage = "이 수업을 다른 언어로 학습",
    chatStudyAnotherLanguagePlaceholder = "언어 이름 입력",
    chatStudyAnotherLanguageConfirm = "세션 만들기",
    chatStudySessionsSaveName = "이름 저장",
    chatStudySessionsError = "학습 세션을 업데이트하지 못했습니다. 다시 시도하세요.",
    chatSaveMessage = "메시지 저장",
    chatUnsaveMessage = "저장 해제",
    chatSavedError = "저장된 메시지를 업데이트하지 못했습니다. 다시 시도하세요.",
    chatStudyMessage = "메시지 학습",
    chatStudyTitle = "학습 세션",
    chatWatchTab = "보기",
    chatWatchPlay = "재생",
    chatWatchPause = "일시정지",
    chatWatchPreparing = "오디오 준비 중…",
    chatWatchSeekLabel = "세션 탐색",
    chatWatchFullscreen = "전체 화면",
    chatWatchExitFullscreen = "전체 화면 종료",
    chatStoryTab = "스토리",
    chatStoryContinue = "계속",
    chatStoryBack = "뒤로",
    chatStudyTab = "학습",
    chatWordsTab = "단어",
    chatWordsWord = "단어",
    chatWordsMeaning = "뜻",
    chatListenTab = "듣기",
    chatReadTab = "읽기",
    chatWriteTab = "쓰기",
    chatExpressTab = "표현",
    chatPlayTab = "플레이",
    chatTest = StudyTestStrings(
        tab = "테스트",
        sceneLoading = "테스트 장면을 만드는 중…",
        sceneError = "테스트 장면을 만들 수 없습니다.",
        audioError = "대사를 재생할 수 없습니다. 다시 시도하세요.",
        retry = "다시 시도",
        listenHint = { name -> "${name}가 말한 내용을 당신의 언어로 설명하세요." },
        speakHint = "배우는 언어로 상황에 맞게 답하세요.",
        playLine = "다시 듣기",
        beatProgress = { current, total -> "$current / $total 라운드" },
        outlineTitle = "이번 대화에서 할 일",
        start = "시작",
        inputPlaceholder = "입력하거나 길게 눌러 말하세요…",
        next = "다음",
        finish = "테스트 완료",
        scoring = "답변을 채점하는 중…",
        scoreError = "테스트를 채점하지 못했습니다. 다시 시도하세요.",
        resultTitle = "테스트 결과",
        gradeLabel = "종합 점수",
        listeningLabel = "듣기",
        speakingLabel = "말하기",
        retake = "다시 응시",
        levelBeginner = "초보",
        levelElementary = "초급",
        levelIntermediate = "중급",
        levelAdvanced = "고급",
        levelNative = "원어민",
        resultCheer = { level ->
            when (level) {
                5 -> "듣고 그 장면에서 써습니다. 진짜 대화입니다."
                4 -> "듣기가 예리하고 답도 유용합니다. 거의 정상입니다."
                3 -> "대화를 이어 갔습니다. 확실한 중간 지점입니다."
                2 -> "일부를 잡았습니다. 다시 하면 더 단단해집니다."
                else -> "대화에 들어왔습니다. 다음 라운드는 더 분명해집니다."
            }
        },
    ),
    chatAllTab = "전체",
    chatReadTapHint = "단어를 탭하면 본문에서 같은 단어가 모두 강조됩니다",
    chatReadPlayText = "전체 텍스트 재생",
    chatReadPauseText = "전체 텍스트 일시정지",
    chatWriteHint = "표시된 문장을 그대로 입력하거나 말해 보세요",
    chatWritePlaceholder = "문장 입력…",
    chatWriteCorrect = "정답!",
    chatWriteChecking = "확인 중…",
    chatWriteFeedbackError = "이 시도를 설명할 수 없습니다. 다시 시도해 주세요.",
    chatExpressHint = "배우고 있는 언어로 각 의미를 표현해 보세요",
    chatListenHint = "각 문장을 듣고 자신의 언어로 뜻을 입력하세요",
    chatListenPlaceholder = "문장의 뜻 입력…",
    chatListenChecking = "확인 중…",
    chatExpressPlaceholder = "문장을 입력하세요…",
    chatExpressCorrect = "좋아요!",
    chatExpressChecking = "확인 중…",
    chatExpressFeedbackError = "이 시도를 확인할 수 없습니다. 다시 시도해 주세요.",
    chatExpressLoading = "연습 문장을 모으는 중…",
    chatExpressGenerating = "문장을 더 만드는 중…",
    chatExpressEmpty = "아직 연습 문장이 없습니다. 의미 로딩을 기다려 주세요.",
    chatPlayHint = "장면을 고르고 짧은 대화를 연습하세요",
    chatPlayLoading = "시나리오 만드는 중…",
    chatPlayError = "플레이 시나리오를 불러오지 못했습니다. 다시 시도하세요.",
    chatPlayRetry = "다시 시도",
    chatPlayPlaceholder = "학습 언어로 답하기…",
    chatPlayYouLabel = "나",
    chatPlayOtherLabel = "상대",
    chatPlaySending = "생각 중…",
    chatStudyLoading = "학습 세션을 준비하는 중…",
    chatStudyCreationTitle = "학습 세션을 만드는 중",
    chatStudyCreationText = "학습 텍스트",
    chatStudyCreationStructure = "세션 구조",
    chatStudyCreationDissections = "단어 뜻",
    chatStudyCreationImages = "이미지",
    chatStudyCreationTest = "테스트",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "학습 세션을 시작할 수 없습니다. 다시 시도하세요.",
    chatStudyLiteral = "문자 그대로의 의미",
    chatStudyRegister = "어역",
    chatStudyContext = "이 맥락에서의 의미",
    chatStudySynonyms = "유의어",
    chatStudyExamples = "예문",
    chatStudyPlaySentence = "문장 재생",
    chatStudyPauseSentence = "문장 일시정지",
    chatStudyTapHint = "단어를 탭해 의미를 분석하세요",
    chatStudyPreparingMeanings = "단어 의미를 불러오는 중…",
    chatStudySentenceError = "이 문장의 의미를 불러오지 못했습니다.",
    chatStudyRetrySentence = "다시 시도",
    chatStudyMeaningLoading = "의미를 불러오는 중…",
    chatStudyPlayground = "직접 써 보기",
    chatStudyPlaygroundHint = "이 단어를 쓴 문장을 적어 자연스러운지 확인해 보세요.",
    chatStudyPlaygroundPlaceholder = "이 단어가 들어간 문장을 입력…",
    chatStudyPlaygroundSubmit = "문장 확인",
    chatStudyPlaygroundNatural = "자연스러움",
    chatStudyPlaygroundUnnatural = "부자연스러움",
    chatStudyPlaygroundError = "문장을 확인할 수 없습니다. 다시 시도하세요.",
    chatStudyPlaygroundImageLoading = "만화 장면을 그리는 중…",
    chatStudyPlaygroundImageError = "만화 이미지를 만들지 못했습니다.",
    chatStudyPlaygroundImageAlt = "이 단어의 만화 장면",
    chatStudySentenceImageLoading = "장면을 만드는 중…",
    chatStudySentenceImageError = "장면 이미지를 만들지 못했습니다.",
    chatStudySentenceImageAlt = "이 문장의 일러스트",
    chatStudyWordImageLoading = "단어 장면을 만드는 중…",
    chatStudyWordImageError = "단어 이미지를 만들지 못했습니다.",
    chatStudyWordImageAlt = "이 단어의 일러스트",
    challengesTitle = "챌린지",
    challengesPrompt = "이웃과 함께하는 즐거운 챌린지!",
    challengesStartNew = "새 챌린지 시작",
    challengesLoading = "챌린지를 불러오는 중…",
    challengesEmpty = "아직 공개된 챌린지가 없습니다.",
    challengesInReview = "검토 중",
    challengesProcessing = "영상을 처리하는 중…",
    challengesFailed = "처리 실패",
    challengesYourName = "이름",
    challengesYourNamePlaceholder = "이웃이 뭐라고 부를까요?",
    challengesPickVideo = "영상 선택 (최대 5분)",
    challengesRulesTitle = "챌린지 규칙",
    challengesRulePlace = "촬영은 One Verandah에서 해야 합니다",
    challengesRuleFamily = "가족 모두가 볼 수 있는 내용이어야 합니다",
    challengesRuleLength = "2–4분이 가장 좋아요",
    challengesSubmit = "영상 보내기",
    challengesUploading = "처리 및 업로드 중",
    challengesVote = "투표",
    challengesVoted = "투표함",
    challengesVotes = "표",
    challengesUntitled = "새 챌린지",
    challengesEnter = "이 챌린지에 참여",
    challengesNext = "다음 영상",
    challengesPrevious = "이전 영상",
    challengesSubmitError = "영상을 보내지 못했습니다.",
    challengesVoteError = "투표를 저장하지 못했습니다.",
    chatExploreChallenges = "챌린지",
    chatExploreChallengesSub = "동네 1등을 노려보세요",
    chatExploreMeets = "밋",
    chatExploreMeetsSub = "커피 챗과 새로운 이웃",
    adminChallengesTab = "챌린지",
    adminPendingSubmissions = "새 제출",
    adminPublishSubmission = "게시",
    adminRenameChallenge = "이름 바꾸기",
    adminDeleteChallenge = "챌린지 삭제",
    adminDeleteSubmission = "제출 삭제",
    adminNoChallenges = "아직 챌린지가 없습니다.",
    adminNoPending = "검토 대기 항목이 없습니다.",
    meetsTitle = "밋",
    meetsPrompt = "커피 챗, 협업, 새로운 이웃. 만나고 싶은 사람에게 피치를 보내세요.",
    meetsStartNew = "밋 올리기",
    meetsLoading = "밋을 불러오는 중…",
    meetsEmpty = "아직 밋이 없습니다. 첫 인사를 남겨 보세요.",
    meetsInReview = "승인 대기",
    meetsYourName = "이름",
    meetsYourNamePlaceholder = "어떻게 불러 드릴까요?",
    meetsYourPhoto = "가장 좋은 사진",
    meetsPickPhoto = "사진 선택",
    meetsZaloHint = "밋이 진행되는 동안 모든 사람에게 표시됩니다",
    meetsPitchLimit = "몇 명이 피치를 보낼 수 있나요?",
    meetsPitchLimitHint = "완료된 만남마다 한 번씩 줄어듭니다",
    meetsPitchLabel = "소개 글",
    meetsPitchPlaceholder = "만나서 무엇을 이야기하거나 하고 싶나요?",
    meetsSubmit = "밋 올리기",
    meetsUploading = "업로드 중",
    meetsRemaining = { count -> "남은 피치 ${count}회" },
    meetsPitchPerson = { name -> "${name}에게 피치" },
    meetsPitchReplyLabel = "내 피치",
    meetsPitchReplyPlaceholder = "왜 만나야 할까요?",
    meetsSendPitch = "피치 보내기",
    meetsPitchSent = "보냈습니다. 맞으면 Zalo를 전달할게요.",
    meetsSubmitError = "올리지 못했습니다.",
    meetsPitchError = "피치를 보내지 못했습니다.",
    meetsNoRemaining = "이 밋은 마감되었습니다",
    meetsUntitled = "새 밋",
    meetsCannotPitchOwn = "내 밋입니다",
    meetsAlreadyPitched = "이미 피치를 보냈습니다",
    adminMeetsTab = "밋",
    adminPendingMeets = "새 밋",
    adminPendingPitches = "새 피치",
    adminApproveMeet = "승인",
    adminCompletePitch = "완료 표시",
    adminResetRemaining = "남은 횟수 재설정",
    adminResetRemainingHint = "남은 횟수를 입력하세요",
    adminDeleteMeet = "밋 삭제",
    adminDeletePitch = "피치 삭제",
    adminNoMeets = "아직 밋이 없습니다.",
    adminNoPendingMeets = "새 밋이 없습니다.",
    adminNoPendingPitches = "새 피치가 없습니다.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "게시자 Zalo",
    adminPitchReplyZalo = "피치 Zalo",
    locationsTitle = "장소",
    locationsPrompt = "근처 카페, 스튜디오, 그리고 만들어지는 공간을 찾아보세요.",
    locationsSearchPlaceholder = "장소 검색",
    locationsAllCategories = "전체",
    locationsLoading = "장소를 불러오는 중…",
    locationsEmpty = "아직 장소가 없습니다.",
    locationsStatusPlanning = "기획 중",
    locationsStatusComingSoon = "곧 오픈",
    locationsStatusOpen = "영업 중",
    locationsStatusClosed = "영업 종료",
    locationsClosesAt = { time -> "${time}에 닫음" },
    locationsOpensAt = { time -> "${time}에 열림" },
    locationsOpensDayAt = { day, time -> "${day} ${time}에 열림" },
    locationsCommitFunds = "펀드 약정",
    locationsContact = "연락",
    locationsDirections = "길찾기",
    locationsAmountLabel = "금액 (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "메시지",
    locationsMessagePlaceholder = "호스트에게 남길 말",
    locationsCommitSubmit = "약정",
    locationsCommitSent = "감사합니다. 호스트에게 전달할게요.",
    locationsCommitError = "보내지 못했습니다. 다시 시도하세요.",
    locationsContactSubmit = "보내기",
    locationsContactSent = "보냈습니다. 호스트가 Zalo로 연락합니다.",
    locationsContactError = "메시지를 보내지 못했습니다.",
    locationsContactPlaceholder = "안녕하세요",
    locationsLikeError = "좋아요를 저장하지 못했습니다.",
    locationsSuggestTitle = "장소 제안하기",
    locationsSuggestHint = "추가할 카페, 스튜디오, 동네 장소를 알고 있나요?",
    locationsSuggestName = "장소 이름",
    locationsSuggestType = "어떤 곳인가요",
    locationsSuggestAddress = "주소 또는 찾는 방법",
    locationsSuggestWhy = "이웃이 알아야 하는 이유",
    locationsSuggestSubmit = "보내기",
    locationsSuggestSuccess = "보냈습니다. 살펴볼게요.",
    locationsSuggestError = "보내지 못했습니다. 다시 시도하세요.",
    locationsUntitled = "새 장소",
    locationsDayMon = "월",
    locationsDayTue = "화",
    locationsDayWed = "수",
    locationsDayThu = "목",
    locationsDayFri = "금",
    locationsDaySat = "토",
    locationsDaySun = "일",
    chatExploreLocations = "장소",
    chatExploreLocationsSub = "근처 공간",
    adminLocationsTab = "장소",
    adminAddLocation = "장소 추가",
    adminEditLocation = "수정",
    adminDeleteLocation = "장소 삭제",
    adminNoLocations = "아직 장소가 없습니다.",
    adminLocationName = "이름",
    adminLocationType = "유형",
    adminLocationCategory = "카테고리",
    adminLocationAddress = "주소",
    adminLocationStatus = "상태",
    adminLocationHostZalo = "호스트 Zalo",
    adminLocationMapsUrl = "지도 URL",
    adminLocationHighlights = "하이라이트",
    adminLocationPhotos = "사진",
    adminLocationAddPhoto = "사진 추가",
    adminLocationSchedule = "영업 시간 (UTC+7)",
    adminLocationClosedDay = "휴무",
    adminLocationApplyBelow = "아래에 적용",
    adminLocationContent = "이야기",
    adminLocationAddText = "텍스트 추가",
    adminLocationAddImage = "이미지 추가",
    adminLocationCommitments = "펀드 약정",
    adminLocationContacts = "메시지",
    clubsTitle = "클럽",
    clubsPrompt = "클럽을 찾아 가입하거나 직접 만들어 보세요.",
    clubsSearchPlaceholder = "클럽 검색",
    clubsAllCategories = "전체",
    clubsLoading = "클럽을 불러오는 중…",
    clubsEmpty = "아직 클럽이 없습니다.",
    clubsUntitled = "새 클럽",
    clubsCreateTitle = "클럽 만들기",
    clubsCreateHint = "이름, 소개 문구, 설명, 연락처를 입력하세요. 나머지 정보는 생성 후에 추가됩니다.",
    clubsCreateName = "클럽 이름",
    clubsCreateTagline = "소개 문구",
    clubsCreateDescription = "설명",
    clubsCreateHostName = "이름",
    clubsCreateSubmit = "클럽 만들기",
    clubsCreateSuccess = "클럽 요청을 보냈습니다.",
    clubsCreateError = "클럽 요청을 보내지 못했습니다.",
    clubsJoin = "클럽 가입",
    clubsJoinSubmit = "클럽 가입",
    clubsJoinSent = "감사합니다! 호스트가 Zalo로 연락드릴게요!",
    clubsJoinError = "가입 요청을 보내지 못했습니다.",
    clubsYourName = "이름",
    clubsYourMessage = "메시지",
    clubsYourMessagePlaceholder = "호스트에게 전하고 싶은 말",
    clubsHostedBy = "주최",
    clubsDetails = "상세",
    adminClubsTab = "클럽",
    adminAddClub = "클럽 추가",
    adminEditClub = "클럽 수정",
    adminDeleteClub = "클럽 삭제",
    adminNoClubs = "아직 클럽이 없습니다.",
    adminClubName = "이름",
    adminClubTagline = "소개 문구",
    adminClubDescription = "설명",
    adminClubCategory = "카테고리",
    adminClubActivities = "활동",
    adminClubHostName = "호스트 이름",
    adminClubHostZalo = "호스트 Zalo",
    adminClubPhotos = "사진",
    adminClubAddPhoto = "사진 추가",
    adminClubContent = "스토리",
    adminClubAddText = "텍스트 추가",
    adminClubAddImage = "이미지 추가",
    adminClubJoinQuestions = "가입 질문",
    adminClubCreateRequests = "생성 요청",
    adminClubJoinRequests = "가입 요청",
    adminNoClubCreateRequests = "생성 요청이 없습니다.",
    adminNoClubJoinRequests = "가입 요청이 없습니다.",
    adminDismissClubRequest = "닫기",
    adminDismissClubJoin = "닫기",
    adminStatsTab = "통계",
    adminStatsHint = "베트남 시간(UTC+7) 기준 일별 방문자. 막대는 방문자, 선은 방문 수입니다.",
    adminStatsLoading = "통계 불러오는 중…",
    adminStats7Days = "7일",
    adminStats30Days = "30일",
    adminStatsVisitors = "방문자",
    adminStatsVisits = "방문 수",
    adminStatsNewVisitors = "신규",
    adminStatsToday = "오늘",
    adminStatsLast7Days = "최근 7일",
    adminStatsLast30Days = "최근 30일",
    adminStatsAllTime = "전체",
    adminStatsTopScreens = "인기 화면",
    adminStatsLanguages = "언어",
    adminStatsNoData = "아직 방문 기록이 없습니다.",
    adminStatsTimezone = "UTC+7",
    adminStatsSelected = "선택한 날",
)

private val Russian = UiStrings(
    appTitle = "Приложение One Verandah",
    serviceDetails = "Детали услуги",
    homePrompt = "Чем мы можем помочь вам сегодня?",
    reviews = { count -> "($count отзывов)" },
    sectionBookingType = "Тип бронирования",
    sectionDuration = "Длительность",
    sectionTargetArea = "Зона воздействия",
    sectionComments = "Комментарии",
    sectionReviews = "Отзывы",
    minutes = { minutes -> "$minutes мин." },
    hours = { hours -> "$hours ч." },
    from = "от",
    total = "Итого",
    priceSummary = "Сводка цены",
    tapToSelect = "Нажмите на тело, чтобы выбрать зоны",
    bookThisService = "Забронировать услугу",
    booking = "Бронирование…",
    bookingSuccessTitle = "Бронирование подтверждено!",
    bookingSuccessMessage = "Мы отправили детали вашему исполнителю. До скорой встречи!",
    done = "Готово",
    back = "Назад",
    menu = "Меню",
    languageName = "Русский",
    layoutSwitchToDesktop = "Компьютерный вид",
    layoutSwitchToMobile = "Мобильный вид",
    layoutFullscreen = "На весь экран",
    layoutExitFullscreen = "Выйти из полноэкранного режима",
    menuTitle = "Приложение One Verandah",
    menuHome = "Главная",
    menuAbout = "О приложении",
    menuLanguage = "Язык",
    menuHelp = "Помощь и поддержка",
    menuForAdults = "Для взрослых",
    menuForKids = "Для детей",
    close = "Закрыть",
    bookingDialogTitle = "Подтвердите бронирование",
    zaloLabel = "Номер Zalo",
    zaloPlaceholder = "Ваш номер телефона",
    zaloError = "Номер Zalo обязателен",
    messageLabel = "Дополнительные комментарии",
    messagePlaceholder = "Необязательное сообщение для исполнителя",
    confirmBooking = "Подтвердить бронирование",
    confirm = "Подтвердить",
    male = "Мужской",
    female = "Женский",
    viewFront = "Спереди",
    viewBack = "Сзади",
    aboutText = "Создано жителями One Verandah для жителей One Verandah.",
    aboutExclusive = "Создано исключительно для сообщества One Verandah",
    aboutTrusted = "Ваша безопасность важна — мы ставим проверенных исполнителей на первое место",
    aboutFree = "Полностью бесплатно всегда",
    helpSuggestFeature = "Предложить функцию",
    helpSuggestFeatureSubtitle = "Расскажите, что вы хотели бы увидеть дальше",
    helpFeedback = "Отправить отзыв",
    helpFeedbackSubtitle = "Поделитесь мыслями, чтобы помочь нам улучшиться",
    helpReportProblem = "Сообщить о проблеме",
    helpReportProblemSubtitle = "Расскажите о любых найденных проблемах",
    helpSubmit = "Отправить",
    helpPlaceholder = "Введите сообщение здесь…",
    helpErrorRequired = "Сообщение обязательно",
    helpSuccessTitle = "Спасибо!",
    helpSuccessMessage = "Ваше обращение получено. Мы ценим ваш отзыв!",
    helpListService = "Добавить услугу",
    helpListServiceSubtitle = "Предложите свои услуги сообществу",
    listServiceDialogTitle = "Добавить услугу",
    serviceDescriptionLabel = "Опишите услугу",
    serviceDescriptionPlaceholder = "Какие услуги вы предоставляете?",
    serviceDescriptionError = "Описание услуги обязательно",
    yourNameLabel = "Ваше имя",
    yourSexLabel = "Ваш пол",
    yourNamePlaceholder = "Введите ваше имя",
    yourNameError = "Имя обязательно",
    blockAddressPlaceholder = "Введите корпус и номер квартиры (например, Block A - A.12.03)",
    blockAddressError = "Корпус/адрес обязателен",
    listServiceSuccessTitle = "Заявка на услугу отправлена!",
    listServiceSuccessMessage = "Данные о вашей услуге отправлены. Хост скоро проверит и добавит их вручную!",
    searchPlaceholder = "Поиск услуг…",
    noResults = "Нажмите Enter для чата с ИИ.",
    providedBy = "Предоставляет",
    providerBlockLabel = "Корпус/адрес",
    providerPhoneLabel = "Телефон Zalo",
    providerSexLabel = "Пол",
    providerAboutLabel = "О себе",
    providerSkillsLabel = "Навыки",
    providerSkillLevelLabel = "Ур.",
    providerServicesLabel = "Другие услуги от",
    providerAllServicesLabel = "Все услуги от",
    providerDetails = "Данные исполнителя",
    whenLabel = "Когда",
    whenNow = "Сейчас",
    whenToday = "Сегодня",
    whenTomorrow = "Завтра",
    whenLater = "Позже",
    save = "Сохранить",
    saved = "Сохранено",
    share = "Поделиться",
    savedToast = "Добавлено в список сохранённых",
    removedToast = "Удалено из сохранённых",
    copyLink = "Копировать ссылку",
    copied = "Скопировано!",
    copiedToast = "Ссылка скопирована в буфер обмена",
    pinnedRecentTitle = "Сохранённые и недавние",
    popularTitle = "Популярные услуги",
    clearAll = "Очистить всё",
    clearAllConfirm = "Удалить все сохранённые услуги из этого списка?",
    pinnedEmpty = "Сохраняйте любимые услуги или забронируйте одну, чтобы она появилась здесь!",
    homeViewServices = "Услуги",
    homeViewCategories = "Категории",
    homeLatestStories = "Последние истории",
    homeLatestStudySessions = "Последние учебные сессии",
    homeLatestChallenges = "Последние челленджи",
    homeLatestLocations = "Последние места",
    homeLatestClubs = "Последние клубы",
    homeLatestMeets = "Последние встречи",
    viewAll = "Смотреть все",
    homeFeedLoadingMore = "Загрузка…",
    serviceTemporarilyUnavailable = "Эта услуга временно недоступна",
    chatTitle = "Чат",
    studyTitle = "Учёба",
    studyPrompt = "Прогрызайте язык в своём темпе!",
    phrasesTitle = "Фразы",
    chatPlaceholder = "Спросите что угодно…",
    chatAiSender = "ИИ",
    chatYouSender = "Вы",
    chatThinking = "Думаю…",
    chatError = "Что-то пошло не так. Попробуйте ещё раз.",
    chatPlaySentence = "Воспроизвести предложение",
    chatPauseSentence = "Пауза предложения",
    chatDefinition = "Определение",
    chatInContext = "В этом предложении",
    chatResidentDetails = "Данные жителя",
    chatSuggestions = listOf(
        "Поделись полезным советом по уходу за квартирой",
        "Научи меня полезной вьетнамской фразе",
        "Что можно сделать в One Verandah сегодня?",
    ),
    phrasesBlurb = "Используйте приглашения Earth-Mystic Parlance, чтобы укрепить сообщество One Verandah.",
    phrasesAllLocations = "Все места",
    phrasesLoading = "Загрузка фраз…",
    phrasesEmpty = "Фраз пока нет. Загляните позже.",
    phrasesShowVerse = "Нажмите, чтобы прочитать стих",
    phrasesReact = "Реакция",
    adminTitle = "Админ",
    adminPasswordTitle = "What's the password?",
    adminPasswordPlaceholder = "Введите пароль",
    adminSignIn = "Открыть",
    adminWrongPassword = "Неверный пароль.",
    adminLockedHint = "Введите пароль администратора для управления фразами и эталонными фото.",
    adminPhrasesTab = "Фразы",
    adminPhotosTab = "Эталонные фото",
    adminCharactersTab = "Персонажи",
    adminVoicesTab = "Клонированные голоса",
    adminAddPhrase = "Добавить фразу",
    adminEditPhrase = "Изменить фразу",
    adminEdit = "Изменить",
    adminDelete = "Удалить",
    adminSave = "Сохранить",
    adminSaved = "Сохранено",
    adminError = "Что-то пошло не так",
    adminLogout = "Закрыть админ",
    adminPhotoName = "Имя фото",
    adminPhotoNamePlaceholder = "напр. Infinity Pool Dusk",
    adminPhotoFile = "Файл фото",
    adminUploadPhoto = "Загрузить фото",
    adminUploading = "Загрузка…",
    adminPhotosHint = "Именованные эталонные фото нужны Qwen R2I для сцен, снятых в апартаментах.",
    adminCharactersHint = "Портреты персонажей для генерации фотографий историй.",
    adminCharactersEmpty = "Персонажей пока нет.",
    adminVoicesHint = "Загрузите короткий чистый образец для языка. Один активный голос на язык (или запасной «Все языки»).",
    adminVoiceName = "Имя голоса",
    adminVoiceNamePlaceholder = "напр. warm-vi",
    adminVoiceLanguage = "Язык",
    adminVoiceLanguageAll = "Все языки",
    adminVoiceFile = "Аудиообразец",
    adminUploadVoice = "Загрузить голос",
    adminVoiceActivate = "Использовать этот голос",
    adminVoiceActive = "Активен",
    adminVoiceEmpty = "Клонированных голосов пока нет. Загрузите образец, чтобы заменить TTS по умолчанию.",
    adminDeleteVoiceConfirm = "Удалить этот клонированный голос?",
    adminVoiceProviderId = "ID провайдера",
    adminLocationKey = "Ключ места",
    adminLocationEn = "Место (EN)",
    adminLocationVi = "Место (VI)",
    adminIcon = "Иконка",
    adminSpokenEn = "Фраза (EN)",
    adminSpokenVi = "Фраза (VI)",
    adminVerseEn = "Стих (EN)",
    adminVerseVi = "Стих (VI)",
    adminRawAskEn = "Смысл (EN)",
    adminRawAskVi = "Смысл (VI)",
    adminImageUrl = "URL изображения",
    adminApproved = "Одобрено",
    adminUnapproved = "Не одобрено",
    adminDeletePhraseConfirm = "Удалить эту фразу?",
    adminDeletePhotoConfirm = "Удалить это эталонное фото?",
    adminDeleteCharacterConfirm = "Удалить этого персонажа?",
    adminCharacterStudyLocked = "Из учебной сессии — поэтому удалить нельзя.",
    adminInstructionsEn = "Инструкции (EN)",
    adminInstructionsVi = "Инструкции (VI)",
    adminGeneratePhoto = "Создать фото",
    adminGenerateCover = "Создать обложку",
    adminGenerate = "Создать",
    adminGenerating = "Создание…",
    adminGeneratePrompt = "Промпт",
    adminGenerateHint = "Выберите эталонные фото квартиры, напишите промпт и создайте сцену через Qwen R2I.",
    adminGenerateNoPhotos = "Сначала загрузите эталонные фото, чтобы сцена была настоящей One Verandah.",
    adminSelectReferences = "Эталонные фото",
    adminCoverTitle = "Обложка фраз",
    adminCoverHint = "Создайте обложку из промпта и эталонных фото квартиры.",
    adminCoverPromptDefault = "Мечтательная кинематографичная обложка: жители One Verandah делятся Earth-Mystic Parlance на закате, мягкое зерно плёнки, без текста",
    adminPhrasePromptDefault = "Мечтательное кинематографичное фото в One Verandah, мягкий мистический свет, нежный разговор, без текста",
    phrasesSpokenIn = "На Earth-Mystic Parlance",
    phrasesHowToUse = "Как использовать",
    phrasesVerse = "Стих",
    phrasesOriginStory = "Говорят, первая фраза была услышана у infinity pool — её произнёс не житель, а ветер, прошедший через сад. Она звучала как старая молитва из будущего, где люди снова помнят, как быть мягкими друг к другу. Услышавшие стали говорить сами. Расцвели новые фразы. Казалось, само здание выдыхает их. Так родился этот язык — не изобретённый, а открытый.",
    chatEmptyLead = "Несколько светящихся дверей — или спросите всё, что живёт в вас.",
    chatEmptyOrAsk = "Или попробуйте искру",
    chatExplorePhrases = "Earth-Mystic фразы",
    chatExplorePhrasesSub = "Приглашения на Lời Huyền Linh",
    chatExploreStories = "Мини-истории",
    chatExploreStoriesSub = "Короткие истории из дома",
    storiesTitle = "Мини-истории",
    storiesComingSoon = "Истории собираются",
    storiesComingSoonBody = "Мини-истории уже близко. А пока прогуляйтесь по phrasebook Earth-Mystic и пусть строка откроет дверь.",
    storiesShelfHint = "Соседи смеются вместе и учат языки друг друга.",
    storiesEmpty = "На полке пока пусто. Загляните после следующего выпуска.",
    storiesLoading = "Открываем полку…",
    storiesFeaturedTitle = "Попасть в следующую мини-историю",
    storiesFeaturedHint = "Напишите желаемую роль и пришлите 1–3 своих фото.",
    storiesFeaturedRole = "Желаемая роль",
    storiesFeaturedAbout = "Немного о вас",
    storiesFeaturedVibe = "Какой вы",
    storiesFeaturedWant = "Что хотите делать в истории",
    storiesFeaturedPhotos = "1–3 ваших фото",
    storiesFeaturedSubmit = "Отправить",
    storiesFeaturedSuccess = "Отправлено! Мы впишем вас в будущую историю.",
    storiesSuggestTitle = "Предложить идею истории",
    storiesSuggestHint = "Достаточно семечка — место, чувство, соседский казус.",
    storiesSuggestPlaceholder = "Ваша идея",
    storiesSuggestSubmit = "Отправить идею",
    storiesSuggestSuccess = "Идея получена. Спасибо.",
    storiesSubmitError = "Сейчас не отправилось. Попробуйте ещё раз.",
    storiesLearningMode = "Включить режим учёбы",
    storiesContinue = "Продолжить",
    storiesReadAgain = "Читать сначала",
    storiesPreviousPage = "Предыдущая страница",
    storiesTheEnd = "Конец",
    storiesPageOf = { page, total -> "Страница $page из $total" },
    storiesPages = { count -> if (count == 1) "1 страница" else "$count страниц" },
    storiesStudyThisPage = "Учить эту страницу",
    storiesPublished = "Опубликовано",
    storiesDraft = "Черновик",
    storiesOpens = { count -> "$count открытий" },
    adminStoriesTab = "Истории",
    adminAddBook = "Новая книга",
    adminEditBook = "Править книгу",
    adminBookName = "Название",
    adminBookCategory = "Категория",
    adminBookCategoryPlaceholder = "Детектив, сказка на ночь, общение…",
    adminPublish = "Опубликовать",
    adminUnpublish = "Снять с полки",
    adminConfirmLanguage = "Подтвердите язык книги",
    adminPublishing = "Публикуем…",
    adminAddPage = "Добавить страницу",
    adminPageText = "Текст страницы",
    adminPagePhoto = "Фото страницы",
    adminSelectScenes = "Сцены",
    adminSceneName = "Название сцены",
    adminUploadScene = "Загрузить сцену",
    adminSelectCharacters = "Персонажи",
    adminUploadCharacter = "Загрузить персонажа",
    adminGenerateCharacter = "Создать персонажа",
    adminCharacterName = "Имя персонажа",
    adminCharacterPrompt = "Описание персонажа",
    adminNoBooks = "Книг пока нет.",
    chatSavedTitle = "Сохранённые сообщения",
    chatSavedEmpty = "Добавьте сообщение в закладки, чтобы найти его позже на этом устройстве.",
    chatSavedSend = "Отправить",
    chatSavedEdit = "Изменить",
    chatSavedRemove = "Удалить",
    chatExploreSaved = "Сохранённые сообщения",
    chatExploreSavedSub = "Закладки на этом устройстве",
    chatExploreStudy = "Учебные сессии",
    chatExploreStudySub = "Продолжить обучение на этом устройстве",
    chatStudySessionsTitle = "Учебные сессии",
    chatStudySessionsMine = "Мои учебные сессии",
    chatStudySessionsCommunity = "Из сообщества",
    chatStudySessionsCommunityEmpty = "Пока нет сессий сообщества.",
    chatStudySessionsEmpty = "Начните сессию выше или откройте «Изучить» у любого сообщения чата.",
    chatStudySessionsPlaceholder = "Вставьте или введите текст для изучения…",
    chatStudyExactToggle = "Точный текст",
    chatStudySessionsOpen = "Открыть",
    chatStudySessionsRemove = "Удалить",
    chatStudySessionsRename = "Переименовать",
    chatStudySessionsNamePlaceholder = "Название сессии",
    chatStudyAnotherLanguage = "Изучить урок на другом языке",
    chatStudyAnotherLanguagePlaceholder = "Введите язык",
    chatStudyAnotherLanguageConfirm = "Создать сессию",
    chatStudySessionsSaveName = "Сохранить название",
    chatStudySessionsError = "Не удалось обновить учебные сессии. Попробуйте снова.",
    chatSaveMessage = "Сохранить сообщение",
    chatUnsaveMessage = "Убрать из сохранённых",
    chatSavedError = "Не удалось обновить сохранённые сообщения. Попробуйте снова.",
    chatStudyMessage = "Изучить сообщение",
    chatStudyTitle = "Учебная сессия",
    chatWatchTab = "Смотреть",
    chatWatchPlay = "Воспроизвести",
    chatWatchPause = "Пауза",
    chatWatchPreparing = "Готовим аудио…",
    chatWatchSeekLabel = "Перемотка сессии",
    chatWatchFullscreen = "На весь экран",
    chatWatchExitFullscreen = "Выйти из полноэкранного режима",
    chatStoryTab = "История",
    chatStoryContinue = "Продолжить",
    chatStoryBack = "Назад",
    chatStudyTab = "Учёба",
    chatWordsTab = "Слова",
    chatWordsWord = "Слово",
    chatWordsMeaning = "Значение",
    chatListenTab = "Слушать",
    chatReadTab = "Чтение",
    chatWriteTab = "Письмо",
    chatExpressTab = "Выражение",
    chatPlayTab = "Игра",
    chatTest = StudyTestStrings(
        tab = "Тест",
        sceneLoading = "Создаём сцену для теста…",
        sceneError = "Не удалось создать сцену для теста.",
        audioError = "Не удалось воспроизвести реплику. Попробуйте ещё раз.",
        retry = "Повторить",
        listenHint = { name -> "Объясните своими словами, что сказал(а) $name." },
        speakHint = "Ответьте на языке изучения так, чтобы ответ подходил ситуации.",
        playLine = "Ещё раз",
        beatProgress = { current, total -> "Раунд $current из $total" },
        outlineTitle = "Что вам предстоит сделать",
        start = "Начать",
        inputPlaceholder = "Напишите ответ или удерживайте для речи…",
        next = "Далее",
        finish = "Завершить тест",
        scoring = "Оцениваем ответы…",
        scoreError = "Не удалось оценить ответы. Попробуйте ещё раз.",
        resultTitle = "Результат теста",
        gradeLabel = "Общая оценка",
        listeningLabel = "Аудирование",
        speakingLabel = "Говорение",
        retake = "Пересдать",
        levelBeginner = "Начальный",
        levelElementary = "Элементарный",
        levelIntermediate = "Средний",
        levelAdvanced = "Продвинутый",
        levelNative = "Носитель языка",
        resultCheer = { level ->
            when (level) {
                5 -> "Вы услышали и использовали это. Вот это настоящий разговор."
                4 -> "Точный слух и полезные ответы. Почти вершина."
                3 -> "Вы удержали разговор. Это настоящая середина."
                2 -> "Вы уловили часть момента. Повтор закрепит её."
                else -> "Вы вошли в разговор. Следующий раунд будет яснее."
            }
        },
    ),
    chatAllTab = "Все",
    chatReadTapHint = "Нажмите слово, чтобы подсветить все такие же слова в тексте",
    chatReadPlayText = "Прослушать весь текст",
    chatReadPauseText = "Пауза всего текста",
    chatWriteHint = "Наберите или произнесите каждое предложение точно как показано",
    chatWritePlaceholder = "Введите предложение…",
    chatWriteCorrect = "Верно!",
    chatWriteChecking = "Проверяем…",
    chatWriteFeedbackError = "Не удалось объяснить эту попытку. Попробуйте ещё раз.",
    chatExpressHint = "Выразите каждое значение на изучаемом языке",
    chatListenHint = "Прослушайте каждое предложение и введите его значение на своём языке",
    chatListenPlaceholder = "Введите значение предложения…",
    chatListenChecking = "Проверяем…",
    chatExpressPlaceholder = "Введите своё предложение…",
    chatExpressCorrect = "Отлично!",
    chatExpressChecking = "Проверяем…",
    chatExpressFeedbackError = "Не удалось проверить эту попытку. Попробуйте ещё раз.",
    chatExpressLoading = "Собираем предложения для практики…",
    chatExpressGenerating = "Создаём ещё предложения…",
    chatExpressEmpty = "Пока нет предложений. Дождитесь загрузки значений.",
    chatPlayHint = "Выберите сцену и потренируйте короткий разговор",
    chatPlayLoading = "Придумываем сценарии…",
    chatPlayError = "Не удалось загрузить игровые сценарии. Попробуйте снова.",
    chatPlayRetry = "Повторить",
    chatPlayPlaceholder = "Ответьте на языке изучения…",
    chatPlayYouLabel = "Вы",
    chatPlayOtherLabel = "Собеседник",
    chatPlaySending = "Думаем…",
    chatStudyLoading = "Готовим учебную сессию…",
    chatStudyCreationTitle = "Создаём учебную сессию",
    chatStudyCreationText = "Учебный текст",
    chatStudyCreationStructure = "Структура сессии",
    chatStudyCreationDissections = "Значения слов",
    chatStudyCreationImages = "Изображения",
    chatStudyCreationTest = "Тест",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "Не удалось начать учебную сессию. Попробуйте снова.",
    chatStudyLiteral = "Буквальное значение",
    chatStudyRegister = "Регистр",
    chatStudyContext = "В этом контексте",
    chatStudySynonyms = "Близкие синонимы",
    chatStudyExamples = "Примеры предложений",
    chatStudyPlaySentence = "Прослушать предложение",
    chatStudyPauseSentence = "Пауза предложения",
    chatStudyTapHint = "Нажмите на слово, чтобы разобрать значение",
    chatStudyPreparingMeanings = "Загружаем значения слов…",
    chatStudySentenceError = "Не удалось загрузить значения для этого предложения.",
    chatStudyRetrySentence = "Повторить",
    chatStudyMeaningLoading = "Загружаем значение…",
    chatStudyPlayground = "Попробуйте сами",
    chatStudyPlaygroundHint = "Напишите предложение с этим словом, чтобы проверить, звучит ли оно естественно.",
    chatStudyPlaygroundPlaceholder = "Введите предложение с этим словом…",
    chatStudyPlaygroundSubmit = "Проверить предложение",
    chatStudyPlaygroundNatural = "Естественно",
    chatStudyPlaygroundUnnatural = "Неестественно",
    chatStudyPlaygroundError = "Не удалось проверить предложение. Попробуйте снова.",
    chatStudyPlaygroundImageLoading = "Рисуем комикс-сцену…",
    chatStudyPlaygroundImageError = "Не удалось создать комикс-изображение.",
    chatStudyPlaygroundImageAlt = "Комикс-сцена для этого слова",
    chatStudySentenceImageLoading = "Создаём сцену…",
    chatStudySentenceImageError = "Не удалось создать изображение сцены.",
    chatStudySentenceImageAlt = "Иллюстрация к этому предложению",
    chatStudyWordImageLoading = "Создаём картинку для слова…",
    chatStudyWordImageError = "Не удалось создать изображение слова.",
    chatStudyWordImageAlt = "Иллюстрация к этому слову",
    challengesTitle = "Челленджи",
    challengesPrompt = "Весёлые челленджи вместе с соседями!",
    challengesStartNew = "Начать новый челлендж",
    challengesLoading = "Загружаем челленджи…",
    challengesEmpty = "Пока нет открытых челленджей.",
    challengesInReview = "На проверке",
    challengesProcessing = "Обрабатываем видео…",
    challengesFailed = "Обработка не удалась",
    challengesYourName = "Ваше имя",
    challengesYourNamePlaceholder = "Как соседям вас называть?",
    challengesPickVideo = "Выберите видео (до 5 минут)",
    challengesRulesTitle = "Правила челленджа",
    challengesRulePlace = "Снимать нужно в One Verandah",
    challengesRuleFamily = "Контент должен быть семейным",
    challengesRuleLength = "Лучше 2–4 минуты",
    challengesSubmit = "Отправить видео",
    challengesUploading = "Обработка и загрузка",
    challengesVote = "Голос",
    challengesVoted = "Голос учтён",
    challengesVotes = "голосов",
    challengesUntitled = "Новый челлендж",
    challengesEnter = "Участвовать",
    challengesNext = "Следующее видео",
    challengesPrevious = "Предыдущее видео",
    challengesSubmitError = "Не удалось отправить видео.",
    challengesVoteError = "Не удалось сохранить голос.",
    chatExploreChallenges = "Челленджи",
    chatExploreChallengesSub = "Поспорьте за первое место во дворе",
    chatExploreMeets = "Встречи",
    chatExploreMeetsSub = "Кофе и новые соседи",
    adminChallengesTab = "Челленджи",
    adminPendingSubmissions = "Новые работы",
    adminPublishSubmission = "Опубликовать",
    adminRenameChallenge = "Переименовать",
    adminDeleteChallenge = "Удалить челлендж",
    adminDeleteSubmission = "Удалить работу",
    adminNoChallenges = "Челленджей пока нет.",
    adminNoPending = "На проверке ничего нет.",
    meetsTitle = "Встречи",
    meetsPrompt = "Кофе, коллаборации и новые соседи. Напишите тому, с кем хотите встретиться.",
    meetsStartNew = "Опубликовать встречу",
    meetsLoading = "Загрузка встреч…",
    meetsEmpty = "Встреч пока нет. Будьте первым, кто поздоровается.",
    meetsInReview = "Ожидает одобрения",
    meetsYourName = "Ваше имя",
    meetsYourNamePlaceholder = "Как к вам обращаться?",
    meetsYourPhoto = "Ваше лучшее фото",
    meetsPickPhoto = "Выбрать фото",
    meetsZaloHint = "Видно всем, пока встреча активна",
    meetsPitchLimit = "Сколько человек могут написать вам?",
    meetsPitchLimitHint = "Каждая состоявшаяся встреча уменьшает счётчик на один",
    meetsPitchLabel = "Ваш питч",
    meetsPitchPlaceholder = "О чём хотите поговорить или что сделать?",
    meetsSubmit = "Опубликовать",
    meetsUploading = "Загрузка",
    meetsRemaining = { count -> "Осталось $count" },
    meetsPitchPerson = { name -> "Написать $name" },
    meetsPitchReplyLabel = "Ваш питч",
    meetsPitchReplyPlaceholder = "Почему стоит встретиться?",
    meetsSendPitch = "Отправить",
    meetsPitchSent = "Отправлено. Если подойдёт, передадим ваш Zalo.",
    meetsSubmitError = "Не удалось опубликовать.",
    meetsPitchError = "Не удалось отправить питч.",
    meetsNoRemaining = "Эта встреча уже заполнена",
    meetsUntitled = "Новая встреча",
    meetsCannotPitchOwn = "Это ваша встреча",
    meetsAlreadyPitched = "Вы уже отправили питч",
    adminMeetsTab = "Встречи",
    adminPendingMeets = "Новые встречи",
    adminPendingPitches = "Новые питчи",
    adminApproveMeet = "Одобрить",
    adminCompletePitch = "Отметить выполненным",
    adminResetRemaining = "Сбросить остаток",
    adminResetRemainingHint = "Сколько откликов оставить?",
    adminDeleteMeet = "Удалить встречу",
    adminDeletePitch = "Удалить питч",
    adminNoMeets = "Встреч пока нет.",
    adminNoPendingMeets = "Новых встреч нет.",
    adminNoPendingPitches = "Новых питчей нет.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "Zalo автора",
    adminPitchReplyZalo = "Zalo отклика",
    locationsTitle = "Места",
    locationsPrompt = "Кафе, студии и места, которые ещё появляются рядом.",
    locationsSearchPlaceholder = "Искать места",
    locationsAllCategories = "Все",
    locationsLoading = "Загрузка мест…",
    locationsEmpty = "Пока нет мест. Загляните позже.",
    locationsStatusPlanning = "В планах",
    locationsStatusComingSoon = "Скоро",
    locationsStatusOpen = "Открыто",
    locationsStatusClosed = "Закрыто",
    locationsClosesAt = { time -> "Закроется в $time" },
    locationsOpensAt = { time -> "Откроется в $time" },
    locationsOpensDayAt = { day, time -> "Откроется $day в $time" },
    locationsCommitFunds = "Внести вклад",
    locationsContact = "Связаться",
    locationsDirections = "Маршрут",
    locationsAmountLabel = "Сумма (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "Сообщение",
    locationsMessagePlaceholder = "Записка хозяину",
    locationsCommitSubmit = "Отправить",
    locationsCommitSent = "Спасибо — мы передадим.",
    locationsCommitError = "Не удалось отправить. Попробуйте ещё раз.",
    locationsContactSubmit = "Отправить",
    locationsContactSent = "Отправлено. Хозяин напишет в Zalo.",
    locationsContactError = "Не удалось отправить сообщение.",
    locationsContactPlaceholder = "Привет",
    locationsLikeError = "Не удалось сохранить лайк.",
    locationsSuggestTitle = "Предложить место",
    locationsSuggestHint = "Знаете кафе, студию или уголок по соседству, который стоит добавить?",
    locationsSuggestName = "Название места",
    locationsSuggestType = "Какое это место",
    locationsSuggestAddress = "Адрес или как найти",
    locationsSuggestWhy = "Почему соседям стоит знать об этом",
    locationsSuggestSubmit = "Отправить",
    locationsSuggestSuccess = "Отправлено! Мы посмотрим.",
    locationsSuggestError = "Не удалось отправить. Попробуйте ещё раз.",
    locationsUntitled = "Новое место",
    locationsDayMon = "Пн",
    locationsDayTue = "Вт",
    locationsDayWed = "Ср",
    locationsDayThu = "Чт",
    locationsDayFri = "Пт",
    locationsDaySat = "Сб",
    locationsDaySun = "Вс",
    chatExploreLocations = "Места",
    chatExploreLocationsSub = "Рядом",
    adminLocationsTab = "Места",
    adminAddLocation = "Добавить место",
    adminEditLocation = "Изменить",
    adminDeleteLocation = "Удалить место",
    adminNoLocations = "Мест пока нет.",
    adminLocationName = "Название",
    adminLocationType = "Тип",
    adminLocationCategory = "Категория",
    adminLocationAddress = "Адрес",
    adminLocationStatus = "Статус",
    adminLocationHostZalo = "Zalo хозяина",
    adminLocationMapsUrl = "Ссылка на карту",
    adminLocationHighlights = "Особенности",
    adminLocationPhotos = "Фото",
    adminLocationAddPhoto = "Добавить фото",
    adminLocationSchedule = "Часы работы (UTC+7)",
    adminLocationClosedDay = "Выходной",
    adminLocationApplyBelow = "Применить ниже",
    adminLocationContent = "История",
    adminLocationAddText = "Добавить текст",
    adminLocationAddImage = "Добавить фото",
    adminLocationCommitments = "Вклады",
    adminLocationContacts = "Сообщения",
    clubsTitle = "Клубы",
    clubsPrompt = "Находите клубы — вступите или создайте свой.",
    clubsSearchPlaceholder = "Поиск клубов",
    clubsAllCategories = "Все",
    clubsLoading = "Загрузка клубов…",
    clubsEmpty = "Клубов пока нет.",
    clubsUntitled = "Новый клуб",
    clubsCreateTitle = "Создать клуб",
    clubsCreateHint = "Укажите название, слоган, описание и как с вами связаться. Остальное добавим после создания.",
    clubsCreateName = "Название клуба",
    clubsCreateTagline = "Слоган",
    clubsCreateDescription = "Описание",
    clubsCreateHostName = "Ваше имя",
    clubsCreateSubmit = "Создать клуб",
    clubsCreateSuccess = "Заявка на клуб отправлена.",
    clubsCreateError = "Не удалось отправить заявку. Попробуйте ещё раз.",
    clubsJoin = "Вступить",
    clubsJoinSubmit = "Вступить",
    clubsJoinSent = "Спасибо! Организатор свяжется с вами в Zalo!",
    clubsJoinError = "Не удалось отправить заявку.",
    clubsYourName = "Ваше имя",
    clubsYourMessage = "Сообщение",
    clubsYourMessagePlaceholder = "Что хотели бы сказать организатору",
    clubsHostedBy = "Организатор",
    clubsDetails = "Подробности",
    adminClubsTab = "Клубы",
    adminAddClub = "Добавить клуб",
    adminEditClub = "Изменить клуб",
    adminDeleteClub = "Удалить клуб",
    adminNoClubs = "Клубов пока нет.",
    adminClubName = "Название",
    adminClubTagline = "Слоган",
    adminClubDescription = "Описание",
    adminClubCategory = "Категория",
    adminClubActivities = "Занятия",
    adminClubHostName = "Имя организатора",
    adminClubHostZalo = "Zalo организатора",
    adminClubPhotos = "Фото",
    adminClubAddPhoto = "Добавить фото",
    adminClubContent = "История",
    adminClubAddText = "Добавить текст",
    adminClubAddImage = "Добавить фото",
    adminClubJoinQuestions = "Вопросы для вступления",
    adminClubCreateRequests = "Заявки на создание",
    adminClubJoinRequests = "Заявки на вступление",
    adminNoClubCreateRequests = "Нет заявок на создание.",
    adminNoClubJoinRequests = "Нет заявок на вступление.",
    adminDismissClubRequest = "Закрыть",
    adminDismissClubJoin = "Закрыть",
    adminStatsTab = "Статистика",
    adminStatsHint = "Посетители по дням (время Вьетнама, UTC+7). Столбцы — посетители, линия — визиты.",
    adminStatsLoading = "Загрузка статистики…",
    adminStats7Days = "7 дней",
    adminStats30Days = "30 дней",
    adminStatsVisitors = "Посетители",
    adminStatsVisits = "Визиты",
    adminStatsNewVisitors = "Новые",
    adminStatsToday = "Сегодня",
    adminStatsLast7Days = "За 7 дней",
    adminStatsLast30Days = "За 30 дней",
    adminStatsAllTime = "За всё время",
    adminStatsTopScreens = "Популярные экраны",
    adminStatsLanguages = "Языки",
    adminStatsNoData = "Пока нет визитов.",
    adminStatsTimezone = "Время UTC+7",
    adminStatsSelected = "Выбранный день",
)

private val Spanish = UiStrings(
    appTitle = "Aplicación One Verandah",
    serviceDetails = "Detalles de la oferta",
    homePrompt = "¿En qué podemos ayudarte hoy?",
    reviews = { count -> "($count reseñas)" },
    sectionBookingType = "Tipo de reserva",
    sectionDuration = "Duración",
    sectionTargetArea = "Zona a tratar",
    sectionComments = "Comentarios",
    sectionReviews = "Reseñas",
    minutes = { minutes -> "$minutes minutos" },
    hours = { hours -> "$hours horas" },
    from = "desde",
    total = "Total",
    priceSummary = "Resumen del precio",
    tapToSelect = "Toca el cuerpo para elegir las zonas",
    bookThisService = "Reservar este servicio",
    booking = "Reservando…",
    bookingSuccessTitle = "¡Reserva confirmada!",
    bookingSuccessMessage = "Hemos enviado los detalles a tu proveedor. ¡Nos vemos pronto!",
    done = "Hecho",
    back = "Atrás",
    menu = "Menú",
    languageName = "Español",
    layoutSwitchToDesktop = "Diseño de escritorio",
    layoutSwitchToMobile = "Diseño móvil",
    layoutFullscreen = "Pantalla completa",
    layoutExitFullscreen = "Salir de pantalla completa",
    menuTitle = "Aplicación One Verandah",
    menuHome = "Inicio",
    menuAbout = "Acerca de",
    menuLanguage = "Idioma",
    menuHelp = "Ayuda y soporte",
    menuForAdults = "Para adultos",
    menuForKids = "Para niños",
    close = "Cerrar",
    bookingDialogTitle = "Confirma tu reserva",
    zaloLabel = "Número de Zalo",
    zaloPlaceholder = "Tu número de teléfono",
    zaloError = "El número de Zalo es obligatorio",
    messageLabel = "Comentarios adicionales",
    messagePlaceholder = "Mensaje opcional para el proveedor",
    confirmBooking = "Confirmar reserva",
    confirm = "Confirmar",
    male = "Hombre",
    female = "Mujer",
    viewFront = "Frente",
    viewBack = "Espalda",
    aboutText = "Creado por residentes de One Verandah, para residentes de One Verandah.",
    aboutExclusive = "Creado exclusivamente para la comunidad de One Verandah",
    aboutTrusted = "Tu seguridad es importante: priorizamos a proveedores verificados y de confianza",
    aboutFree = "Completamente gratis de usar, siempre",
    helpSuggestFeature = "Sugerir una función",
    helpSuggestFeatureSubtitle = "Cuéntanos qué te gustaría ver a continuación",
    helpFeedback = "Envíanos tus comentarios",
    helpFeedbackSubtitle = "Comparte tus opiniones para ayudarnos a mejorar",
    helpReportProblem = "Informar de un problema",
    helpReportProblemSubtitle = "Cuéntanos sobre cualquier problema que hayas encontrado",
    helpSubmit = "Enviar",
    helpPlaceholder = "Escribe tu mensaje aquí…",
    helpErrorRequired = "El mensaje es obligatorio",
    helpSuccessTitle = "¡Gracias!",
    helpSuccessMessage = "Tu mensaje ha sido recibido. ¡Agradecemos tus comentarios!",
    helpListService = "Publicar un servicio",
    helpListServiceSubtitle = "Ofrece tus servicios a la comunidad",
    listServiceDialogTitle = "Publicar un servicio",
    serviceDescriptionLabel = "Describe el servicio",
    serviceDescriptionPlaceholder = "¿Qué servicios ofreces?",
    serviceDescriptionError = "La descripción del servicio es obligatoria",
    yourNameLabel = "Tu nombre",
    yourSexLabel = "Tu sexo",
    yourNamePlaceholder = "Introduce tu nombre",
    yourNameError = "Tu nombre es obligatorio",
    blockAddressPlaceholder = "Introduce tu torre y número de unidad (ej. Block A - A.12.03)",
    blockAddressError = "La torre/dirección es obligatoria",
    listServiceSuccessTitle = "¡Solicitud de servicio enviada!",
    listServiceSuccessMessage = "Los detalles de tu servicio han sido enviados. ¡El administrador los revisará y los añadirá manualmente pronto!",
    searchPlaceholder = "Buscar servicios…",
    noResults = "Pulsa Enter para chatear con la IA.",
    providedBy = "Ofrecido por",
    providerBlockLabel = "Torre/Dirección",
    providerPhoneLabel = "Teléfono Zalo",
    providerSexLabel = "Sexo",
    providerAboutLabel = "Acerca de",
    providerSkillsLabel = "Habilidades",
    providerSkillLevelLabel = "Nivel",
    providerServicesLabel = "Otros servicios de",
    providerAllServicesLabel = "Todos los servicios de",
    providerDetails = "Detalles del proveedor",
    whenLabel = "Cuándo",
    whenNow = "Ahora",
    whenToday = "Hoy",
    whenTomorrow = "Mañana",
    whenLater = "Más tarde",
    save = "Guardar",
    saved = "Guardado",
    share = "Compartir",
    savedToast = "Guardado en tu lista de guardados",
    removedToast = "Eliminado de guardados",
    copyLink = "Copiar enlace",
    copied = "¡Copiado!",
    copiedToast = "Enlace copiado al portapapeles",
    pinnedRecentTitle = "Guardados y recientes",
    popularTitle = "Servicios populares",
    clearAll = "Borrar todo",
    clearAllConfirm = "¿Eliminar todos los servicios guardados de esta lista?",
    pinnedEmpty = "¡Guarda los servicios que te gusten o reserva uno para guardarlo aquí!",
    homeViewServices = "Servicios",
    homeViewCategories = "Categorías",
    homeLatestStories = "Últimas historias",
    homeLatestStudySessions = "Últimas sesiones de estudio",
    homeLatestChallenges = "Últimos desafíos",
    homeLatestLocations = "Últimas ubicaciones",
    homeLatestClubs = "Últimos clubes",
    homeLatestMeets = "Últimos encuentros",
    viewAll = "Ver todo",
    homeFeedLoadingMore = "Cargando más…",
    serviceTemporarilyUnavailable = "Este servicio no está disponible temporalmente",
    chatTitle = "Chat",
    studyTitle = "Estudio",
    studyPrompt = "¡Avanza en el idioma a tu propio ritmo!",
    phrasesTitle = "Frases",
    chatPlaceholder = "Pregunta lo que quieras…",
    chatAiSender = "IA",
    chatYouSender = "Tú",
    chatThinking = "Estoy pensando…",
    chatError = "Algo salió mal. Por favor, inténtalo de nuevo.",
    chatPlaySentence = "Reproducir frase",
    chatPauseSentence = "Pausar frase",
    chatDefinition = "Definición",
    chatInContext = "En esta frase",
    chatResidentDetails = "Ver detalles del residente",
    chatSuggestions = listOf(
        "Sugiere una forma suave de conocer a un vecino en la piscina infinita",
        "Enséñame una frase luminosa en vietnamita para la calma compartida",
        "¿Qué pequeña aventura puedo probar hoy en One Verandah?",
        "Ayúdame a convertir una petición simple en Lenguaje Místico Terrenal",
    ),
    phrasesBlurb = "Usa invitaciones de Lenguaje Místico Terrenal para enriquecer tu comunidad en One Verandah.",
    phrasesAllLocations = "Todos los lugares",
    phrasesLoading = "Cargando frases…",
    phrasesEmpty = "Aún no hay frases. Vuelve pronto.",
    phrasesShowVerse = "Toca para leer el verso completo",
    phrasesReact = "Reaccionar",
    adminTitle = "Administración",
    adminPasswordTitle = "¿Cuál es la contraseña?",
    adminPasswordPlaceholder = "Introduce la contraseña",
    adminSignIn = "Desbloquear",
    adminWrongPassword = "Esa contraseña no abre esta puerta.",
    adminLockedHint = "Introduce la contraseña de administrador para gestionar frases y fotos de referencia.",
    adminPhrasesTab = "Frases",
    adminPhotosTab = "Fotos de referencia",
    adminCharactersTab = "Personajes",
    adminVoicesTab = "Voces clonadas",
    adminAddPhrase = "Añadir frase",
    adminEditPhrase = "Editar frase",
    adminEdit = "Editar",
    adminDelete = "Eliminar",
    adminSave = "Guardar",
    adminSaved = "Guardado",
    adminError = "Algo salió mal",
    adminLogout = "Bloquear administración",
    adminPhotoName = "Nombre de la foto",
    adminPhotoNamePlaceholder = "ej. Infinity Pool Dusk",
    adminPhotoFile = "Archivo de foto",
    adminUploadPhoto = "Subir foto",
    adminUploading = "Subiendo…",
    adminPhotosHint = "Las fotos de referencia con nombre impulsan las escenas Qwen R2I ambientadas en el complejo residencial.",
    adminCharactersHint = "Retratos de personajes con nombre utilizados al generar fotos de historias.",
    adminCharactersEmpty = "Aún no hay personajes.",
    adminVoicesHint = "Sube una muestra corta y limpia por idioma. Una voz activa por idioma (o reserva para Todos los idiomas).",
    adminVoiceName = "Nombre de la voz",
    adminVoiceNamePlaceholder = "ej. warm-vi-companion",
    adminVoiceLanguage = "Idioma",
    adminVoiceLanguageAll = "Todos los idiomas",
    adminVoiceFile = "Muestra de audio",
    adminUploadVoice = "Subir voz",
    adminVoiceActivate = "Usar esta voz",
    adminVoiceActive = "Activa",
    adminVoiceEmpty = "Aún no hay voces clonadas. Sube una muestra para reemplazar la voz TTS predeterminada.",
    adminDeleteVoiceConfirm = "¿Eliminar esta voz clonada?",
    adminVoiceProviderId = "ID de proveedor",
    adminLocationKey = "Clave de ubicación",
    adminLocationEn = "Ubicación (Inglés)",
    adminLocationVi = "Ubicación (Vietnamita)",
    adminIcon = "Icono",
    adminSpokenEn = "Hablado (Inglés)",
    adminSpokenVi = "Hablado (Vietnamita)",
    adminVerseEn = "Verso (Inglés)",
    adminVerseVi = "Verso (Vietnamita)",
    adminRawAskEn = "Petición original (Inglés)",
    adminRawAskVi = "Petición original (Vietnamita)",
    adminImageUrl = "URL de imagen",
    adminApproved = "Aprobado",
    adminUnapproved = "No aprobado",
    adminDeletePhraseConfirm = "¿Eliminar esta frase?",
    adminDeletePhotoConfirm = "¿Eliminar esta foto de referencia?",
    adminDeleteCharacterConfirm = "¿Eliminar este personaje?",
    adminCharacterStudyLocked = "Proviene de una sesión de estudio, por lo que no se puede eliminar.",
    adminInstructionsEn = "Instrucciones (Inglés)",
    adminInstructionsVi = "Instrucciones (Vietnamita)",
    adminGeneratePhoto = "Generar foto",
    adminGenerateCover = "Generar portada",
    adminGenerate = "Generar",
    adminGenerating = "Generando…",
    adminGeneratePrompt = "Prompt",
    adminGenerateHint = "Elige fotos de referencia del apartamento, escribe un prompt y crea la escena con Qwen R2I.",
    adminGenerateNoPhotos = "Sube fotos de referencia primero para que la escena pueda ambientarse en One Verandah.",
    adminSelectReferences = "Fotos de referencia",
    adminCoverTitle = "Portada del libro de frases",
    adminCoverHint = "Genera una portada a partir de un prompt y fotos de referencia del apartamento.",
    adminCoverPromptDefault = "Portada cinematográfica y onírica de residentes de One Verandah compartiendo un suave Lenguaje Místico Terrenal en la hora dorada, grano de película suave, sin texto",
    adminPhrasePromptDefault = "Foto cinematográfica y onírica en One Verandah, suave luz mística, residentes en conversación apacible, sin texto",
    phrasesSpokenIn = "Hablado en Lenguaje Místico Terrenal",
    phrasesHowToUse = "Cómo usar",
    phrasesVerse = "Verso",
    phrasesOriginStory = "Dicen que la primera frase se escuchó cerca de la piscina infinita; no la pronunció ningún residente, sino que la trajo el viento a través del jardín. Sonaba como una antigua oración de un futuro donde la gente ha recordado cómo ser amable con los demás. Quienes la escucharon comenzaron a hablarla ellos mismos. Más frases florecieron. El propio edificio parecía exhalarlas. Y así nació el lenguaje: no inventado, sino descubierto.",
    chatEmptyLead = "Algunas puertas luminosas para abrir, o pregunta cualquier cosa que viva en ti.",
    chatEmptyOrAsk = "O prueba una chispa",
    chatExplorePhrases = "Frases de Lenguaje Místico",
    chatExplorePhrasesSub = "Invitaciones iniciales en Lời Huyền Linh",
    chatExploreStories = "Minihistorias",
    chatExploreStoriesSub = "Relatos breves del edificio",
    storiesTitle = "Minihistorias",
    storiesComingSoon = "Las historias se están reuniendo",
    storiesComingSoonBody = "Las minihistorias están en camino. Mientras tanto, pasea por el libro de frases místicas y deja que una línea abra una puerta.",
    storiesShelfHint = "Una forma divertida de reír juntos entre vecinos mientras aprenden los idiomas de los demás.",
    storiesEmpty = "Aún no hay historias en la estantería. Vuelve después del próximo lanzamiento.",
    storiesLoading = "Abriendo la estantería…",
    storiesFeaturedTitle = "Aparece en la próxima minihistoria",
    storiesFeaturedHint = "Cuéntanos el papel que deseas y envía de 1 a 3 fotos tuyas.",
    storiesFeaturedRole = "Tu papel deseado",
    storiesFeaturedAbout = "Un poco sobre ti",
    storiesFeaturedVibe = "Cómo eres",
    storiesFeaturedWant = "Qué quieres hacer en la historia",
    storiesFeaturedPhotos = "De 1 a 3 fotos tuyas",
    storiesFeaturedSubmit = "Enviar",
    storiesFeaturedSuccess = "¡Enviado! Te escribiremos en una historia futura.",
    storiesSuggestTitle = "Sugiere una idea para una historia",
    storiesSuggestHint = "Una semilla es suficiente: un lugar, un sentimiento, un percance entre vecinos.",
    storiesSuggestPlaceholder = "Tu idea de historia",
    storiesSuggestSubmit = "Enviar idea",
    storiesSuggestSuccess = "Idea recibida. Gracias.",
    storiesSubmitError = "No se pudo enviar en este momento. Por favor, inténtalo de nuevo.",
    storiesLearningMode = "Activar modo de aprendizaje",
    storiesContinue = "Continuar",
    storiesReadAgain = "Leer de nuevo",
    storiesPreviousPage = "Página anterior",
    storiesTheEnd = "Fin",
    storiesPageOf = { page, total -> "Página $page de $total" },
    storiesPages = { count -> if (count == 1) "1 página" else "$count páginas" },
    storiesStudyThisPage = "Estudiar esta página",
    storiesPublished = "Publicado",
    storiesDraft = "Borrador",
    storiesOpens = { count -> if (count == 1) "1 apertura" else "$count aperturas" },
    adminStoriesTab = "Historias",
    adminAddBook = "Nuevo libro",
    adminEditBook = "Editar libro",
    adminBookName = "Nombre del libro",
    adminBookCategory = "Categoría",
    adminBookCategoryPlaceholder = "Misterio, Antes de dormir, Social…",
    adminPublish = "Publicar",
    adminUnpublish = "Despublicar",
    adminConfirmLanguage = "Confirmar el idioma del libro",
    adminPublishing = "Publicando…",
    adminAddPage = "Añadir página",
    adminPageText = "Texto de la página",
    adminPagePhoto = "Foto de la página",
    adminSelectScenes = "Escenas",
    adminSceneName = "Nombre de la escena",
    adminUploadScene = "Subir escena",
    adminSelectCharacters = "Personajes",
    adminUploadCharacter = "Subir personaje",
    adminGenerateCharacter = "Generar personaje",
    adminCharacterName = "Nombre del personaje",
    adminCharacterPrompt = "Descripción del personaje",
    adminNoBooks = "Aún no hay libros.",
    chatSavedTitle = "Mensajes guardados",
    chatSavedEmpty = "Guarda cualquier mensaje para encontrarlo aquí más tarde en este dispositivo.",
    chatSavedSend = "Enviar",
    chatSavedEdit = "Editar",
    chatSavedRemove = "Eliminar",
    chatExploreSaved = "Mensajes guardados",
    chatExploreSavedSub = "Marcadores de este dispositivo",
    chatExploreStudy = "Sesiones de estudio",
    chatExploreStudySub = "Reanudar el aprendizaje desde este dispositivo",
    chatStudySessionsTitle = "Sesiones de estudio",
    chatStudySessionsMine = "Mis sesiones de estudio",
    chatStudySessionsCommunity = "De la comunidad",
    chatStudySessionsCommunityEmpty = "Aún no hay sesiones de la comunidad.",
    chatStudySessionsEmpty = "Inicia una sesión arriba o abre Estudiar en cualquier mensaje del chat.",
    chatStudySessionsPlaceholder = "Pega o escribe texto para estudiar…",
    chatStudyExactToggle = "Texto exacto",
    chatStudySessionsOpen = "Abrir",
    chatStudySessionsRemove = "Eliminar",
    chatStudySessionsRename = "Renombrar",
    chatStudySessionsNamePlaceholder = "Nombre de la sesión",
    chatStudyAnotherLanguage = "Estudiar esta lección en otro idioma",
    chatStudyAnotherLanguagePlaceholder = "Escribe un idioma",
    chatStudyAnotherLanguageConfirm = "Crear sesión",
    chatStudySessionsSaveName = "Guardar nombre",
    chatStudySessionsError = "No se pudieron actualizar las sesiones de estudio. Inténtalo de nuevo.",
    chatSaveMessage = "Guardar mensaje",
    chatUnsaveMessage = "Eliminar de guardados",
    chatSavedError = "No se pudieron actualizar los mensajes guardados. Inténtalo de nuevo.",
    chatStudyMessage = "Estudiar mensaje",
    chatStudyTitle = "Sesión de estudio",
    chatWatchTab = "Ver",
    chatWatchPlay = "Reproducir",
    chatWatchPause = "Pausar",
    chatWatchPreparing = "Preparando audio…",
    chatWatchSeekLabel = "Desplazar la sesión",
    chatWatchFullscreen = "Pantalla completa",
    chatWatchExitFullscreen = "Salir de pantalla completa",
    chatStoryTab = "Historia",
    chatStoryContinue = "Continuar",
    chatStoryBack = "Atrás",
    chatStudyTab = "Estudiar",
    chatWordsTab = "Palabras",
    chatWordsWord = "Palabra",
    chatWordsMeaning = "Significado",
    chatListenTab = "Escuchar",
    chatReadTab = "Leer",
    chatWriteTab = "Escribir",
    chatExpressTab = "Expresar",
    chatPlayTab = "Jugar",
    chatTest = StudyTestStrings(
        tab = "Prueba",
        sceneLoading = "Creando la escena de prueba…",
        sceneError = "No se pudo crear la escena de prueba.",
        audioError = "No se pudo reproducir la frase. Inténtalo de nuevo.",
        retry = "Reintentar",
        listenHint = { name -> "Explica en tu idioma lo que dijo $name." },
        speakHint = "Responde en el idioma de estudio de forma adecuada a la situación.",
        playLine = "Repetir",
        beatProgress = { current, total -> "Ronda $current de $total" },
        outlineTitle = "Lo que vas a hacer",
        start = "Empezar",
        inputPlaceholder = "Escribe o mantén pulsado para hablar…",
        next = "Siguiente",
        finish = "Terminar prueba",
        scoring = "Evaluando tus respuestas…",
        scoreError = "No se pudo evaluar la prueba. Inténtalo de nuevo.",
        resultTitle = "Resultado de la prueba",
        gradeLabel = "Nota general",
        listeningLabel = "Comprensión auditiva",
        speakingLabel = "Expresión oral",
        retake = "Repetir prueba",
        levelBeginner = "Principiante",
        levelElementary = "Elemental",
        levelIntermediate = "Intermedio",
        levelAdvanced = "Avanzado",
        levelNative = "Nativo",
        resultCheer = { level ->
            when (level) {
                5 -> "Lo oíste y lo usaste. Eso es una conversación de verdad."
                4 -> "Oído fino y respuestas útiles. Casi en la cima."
                3 -> "Sostuviste la conversación. Un punto medio de verdad."
                2 -> "Captaste una parte. Repetir la fija mejor."
                else -> "Entraste en la conversación. La siguiente ronda será más clara."
            }
        },
    ),
    chatAllTab = "Todo",
    chatReadTapHint = "Toca una palabra para resaltar todas las palabras coincidentes en el texto",
    chatReadPlayText = "Reproducir texto completo",
    chatReadPauseText = "Pausar texto completo",
    chatWriteHint = "Escribe o di cada frase exactamente como se muestra",
    chatWritePlaceholder = "Escribe la frase…",
    chatWriteCorrect = "¡Correcto!",
    chatWriteChecking = "Comprobando…",
    chatWriteFeedbackError = "No se pudo explicar ese intento. Inténtalo de nuevo.",
    chatExpressHint = "Expresa cada significado en el idioma que estás aprendiendo",
    chatListenHint = "Escucha cada frase y escribe su significado en tu idioma",
    chatListenPlaceholder = "Escribe el significado de la frase…",
    chatListenChecking = "Comprobando…",
    chatExpressPlaceholder = "Escribe tu frase…",
    chatExpressCorrect = "¡Bien!",
    chatExpressChecking = "Comprobando…",
    chatExpressFeedbackError = "No se pudo comprobar ese intento. Inténtalo de nuevo.",
    chatExpressLoading = "Recopilando frases de práctica…",
    chatExpressGenerating = "Creando más frases…",
    chatExpressEmpty = "Aún no hay frases de práctica. Espera a que se carguen los significados.",
    chatPlayHint = "Elige una escena y practica una conversación corta",
    chatPlayLoading = "Inventando escenarios…",
    chatPlayError = "No se pudieron cargar los escenarios de juego. Inténtalo de nuevo.",
    chatPlayRetry = "Reintentar",
    chatPlayPlaceholder = "Responde en el idioma de estudio…",
    chatPlayYouLabel = "Tú",
    chatPlayOtherLabel = "Ellos",
    chatPlaySending = "Pensando…",
    chatStudyLoading = "Preparando tu sesión de estudio…",
    chatStudyCreationTitle = "Creando tu sesión de estudio",
    chatStudyCreationText = "Texto de estudio",
    chatStudyCreationStructure = "Estructura de la sesión",
    chatStudyCreationDissections = "Significados de palabras",
    chatStudyCreationImages = "Imágenes",
    chatStudyCreationTest = "Prueba",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "No se pudo iniciar la sesión de estudio. Inténtalo de nuevo.",
    chatStudyLiteral = "Significado literal",
    chatStudyRegister = "Registro",
    chatStudyContext = "En este contexto",
    chatStudySynonyms = "Sinónimos cercanos",
    chatStudyExamples = "Frases de ejemplo",
    chatStudyPlaySentence = "Reproducir frase",
    chatStudyPauseSentence = "Pausar frase",
    chatStudyTapHint = "Toca una palabra para desglosar su significado",
    chatStudyPreparingMeanings = "Cargando significados de palabras…",
    chatStudySentenceError = "No se pudieron cargar los significados de esta frase.",
    chatStudyRetrySentence = "Reintentar",
    chatStudyMeaningLoading = "Cargando significado…",
    chatStudyPlayground = "Pruébalo tú mismo",
    chatStudyPlaygroundHint = "Escribe una frase usando esta palabra para comprobar si suena natural.",
    chatStudyPlaygroundPlaceholder = "Escribe una frase con esta palabra…",
    chatStudyPlaygroundSubmit = "Comprobar frase",
    chatStudyPlaygroundNatural = "Natural",
    chatStudyPlaygroundUnnatural = "No natural",
    chatStudyPlaygroundError = "No se pudo comprobar esa frase. Inténtalo de nuevo.",
    chatStudyPlaygroundImageLoading = "Dibujando una escena de cómic…",
    chatStudyPlaygroundImageError = "No se pudo generar la imagen del cómic.",
    chatStudyPlaygroundImageAlt = "Escena de cómic para esta palabra",
    chatStudySentenceImageLoading = "Creando una escena…",
    chatStudySentenceImageError = "No se pudo crear la imagen de la escena.",
    chatStudySentenceImageAlt = "Ilustración para esta frase",
    chatStudyWordImageLoading = "Creando una escena para la palabra…",
    chatStudyWordImageError = "No se pudo crear la imagen de la palabra.",
    chatStudyWordImageAlt = "Ilustración para esta palabra",
    challengesTitle = "Retos",
    challengesPrompt = "¡Retos divertidos con el vecindario!",
    challengesStartNew = "Empezar un reto nuevo",
    challengesLoading = "Cargando retos…",
    challengesEmpty = "Aún no hay retos públicos.",
    challengesInReview = "En revisión",
    challengesProcessing = "Procesando tu video…",
    challengesFailed = "Error al procesar",
    challengesYourName = "Tu nombre",
    challengesYourNamePlaceholder = "¿Cómo te llaman los vecinos?",
    challengesPickVideo = "Elige un video (máx. 5 minutos)",
    challengesRulesTitle = "Reglas del reto",
    challengesRulePlace = "La grabación debe ser en One Verandah",
    challengesRuleFamily = "El contenido debe ser familiar",
    challengesRuleLength = "Lo ideal es de 2 a 4 minutos",
    challengesSubmit = "Enviar video",
    challengesUploading = "Procesando y subiendo",
    challengesVote = "Votar",
    challengesVoted = "Votado",
    challengesVotes = "votos",
    challengesUntitled = "Nuevo reto",
    challengesEnter = "Entrar a este reto",
    challengesNext = "Siguiente envío",
    challengesPrevious = "Envío anterior",
    challengesSubmitError = "No se pudo enviar el video.",
    challengesVoteError = "No se pudo guardar el voto.",
    chatExploreChallenges = "Retos",
    chatExploreChallengesSub = "Compite por el primer puesto del barrio",
    chatExploreMeets = "Quedadas",
    chatExploreMeetsSub = "Cafés y nuevos vecinos",
    adminChallengesTab = "Retos",
    adminPendingSubmissions = "Nuevos envíos",
    adminPublishSubmission = "Publicar",
    adminRenameChallenge = "Renombrar",
    adminDeleteChallenge = "Eliminar reto",
    adminDeleteSubmission = "Eliminar envío",
    adminNoChallenges = "Aún no hay retos.",
    adminNoPending = "Nada en revisión.",
    meetsTitle = "Quedadas",
    meetsPrompt = "Cafés, colaboraciones y nuevos vecinos. Escribe a quien quieras conocer.",
    meetsStartNew = "Publicar quedada",
    meetsLoading = "Cargando quedadas…",
    meetsEmpty = "Aún no hay quedadas. Sé el primero en saludar.",
    meetsInReview = "Esperando aprobación",
    meetsYourName = "Tu nombre",
    meetsYourNamePlaceholder = "¿Cómo te llamamos?",
    meetsYourPhoto = "Tu mejor foto",
    meetsPickPhoto = "Elegir foto",
    meetsZaloHint = "Visible para todos mientras el encuentro esté activo",
    meetsPitchLimit = "¿Cuántas personas pueden escribirte?",
    meetsPitchLimitHint = "Cada quedada completada usa una",
    meetsPitchLabel = "Tu presentación",
    meetsPitchPlaceholder = "¿De qué quieres hablar o qué quieres hacer?",
    meetsSubmit = "Publicar quedada",
    meetsUploading = "Subiendo",
    meetsRemaining = { count -> if (count == 1) "1 plaza" else "$count plazas" },
    meetsPitchPerson = { name -> "Escribir a $name" },
    meetsPitchReplyLabel = "Tu mensaje",
    meetsPitchReplyPlaceholder = "¿Por qué deberían quedar?",
    meetsSendPitch = "Enviar",
    meetsPitchSent = "Enviado. Si encaja, pasaremos tu Zalo.",
    meetsSubmitError = "No se pudo publicar.",
    meetsPitchError = "No se pudo enviar el mensaje.",
    meetsNoRemaining = "Esta quedada está llena",
    meetsUntitled = "Nueva quedada",
    meetsCannotPitchOwn = "Esta es tu quedada",
    meetsAlreadyPitched = "Ya enviaste una propuesta",
    adminMeetsTab = "Quedadas",
    adminPendingMeets = "Nuevas quedadas",
    adminPendingPitches = "Nuevos mensajes",
    adminApproveMeet = "Aprobar",
    adminCompletePitch = "Marcar completado",
    adminResetRemaining = "Restablecer plazas",
    adminResetRemainingHint = "¿Cuántas propuestas quedan?",
    adminDeleteMeet = "Eliminar quedada",
    adminDeletePitch = "Eliminar mensaje",
    adminNoMeets = "Aún no hay quedadas.",
    adminNoPendingMeets = "No hay quedadas nuevas.",
    adminNoPendingPitches = "No hay mensajes nuevos.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "Zalo del anfitrión",
    adminPitchReplyZalo = "Zalo de la propuesta",
    locationsTitle = "Lugares",
    locationsPrompt = "Descubre cafés, estudios y sitios que aún están tomando forma.",
    locationsSearchPlaceholder = "Buscar lugares",
    locationsAllCategories = "Todos",
    locationsLoading = "Cargando lugares…",
    locationsEmpty = "Aún no hay lugares. Vuelve pronto.",
    locationsStatusPlanning = "En planificación",
    locationsStatusComingSoon = "Próximamente",
    locationsStatusOpen = "Abierto",
    locationsStatusClosed = "Cerrado",
    locationsClosesAt = { time -> "Cierra a las $time" },
    locationsOpensAt = { time -> "Abre a las $time" },
    locationsOpensDayAt = { day, time -> "Abre $day a las $time" },
    locationsCommitFunds = "Aportar fondos",
    locationsContact = "Contactar",
    locationsDirections = "Cómo llegar",
    locationsAmountLabel = "Importe (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "Mensaje",
    locationsMessagePlaceholder = "Una nota para el anfitrión",
    locationsCommitSubmit = "Enviar",
    locationsCommitSent = "Gracias — se lo pasamos al anfitrión.",
    locationsCommitError = "No se pudo enviar. Inténtalo de nuevo.",
    locationsContactSubmit = "Enviar",
    locationsContactSent = "Enviado. El anfitrión te escribirá por Zalo.",
    locationsContactError = "No se pudo enviar el mensaje.",
    locationsContactPlaceholder = "Hola",
    locationsLikeError = "No se pudo guardar el me gusta.",
    locationsSuggestTitle = "Sugerir un lugar",
    locationsSuggestHint = "¿Conoces un café, estudio o rincón del barrio que deberíamos añadir?",
    locationsSuggestName = "Nombre del lugar",
    locationsSuggestType = "Qué tipo de lugar",
    locationsSuggestAddress = "Dirección o cómo encontrarlo",
    locationsSuggestWhy = "Por qué los vecinos deberían conocerlo",
    locationsSuggestSubmit = "Enviar",
    locationsSuggestSuccess = "¡Enviado! Lo revisaremos.",
    locationsSuggestError = "No se pudo enviar. Inténtalo de nuevo.",
    locationsUntitled = "Nuevo lugar",
    locationsDayMon = "Lun",
    locationsDayTue = "Mar",
    locationsDayWed = "Mié",
    locationsDayThu = "Jue",
    locationsDayFri = "Vie",
    locationsDaySat = "Sáb",
    locationsDaySun = "Dom",
    chatExploreLocations = "Lugares",
    chatExploreLocationsSub = "Cerca de ti",
    adminLocationsTab = "Lugares",
    adminAddLocation = "Añadir lugar",
    adminEditLocation = "Editar",
    adminDeleteLocation = "Eliminar lugar",
    adminNoLocations = "Aún no hay lugares.",
    adminLocationName = "Nombre",
    adminLocationType = "Tipo",
    adminLocationCategory = "Categoría",
    adminLocationAddress = "Dirección",
    adminLocationStatus = "Estado",
    adminLocationHostZalo = "Zalo del anfitrión",
    adminLocationMapsUrl = "URL del mapa",
    adminLocationHighlights = "Destacados",
    adminLocationPhotos = "Fotos",
    adminLocationAddPhoto = "Añadir fotos",
    adminLocationSchedule = "Horario (UTC+7)",
    adminLocationClosedDay = "Cerrado",
    adminLocationApplyBelow = "Aplicar abajo",
    adminLocationContent = "Historia",
    adminLocationAddText = "Añadir texto",
    adminLocationAddImage = "Añadir imagen",
    adminLocationCommitments = "Aportes",
    adminLocationContacts = "Mensajes",
    clubsTitle = "Clubes",
    clubsPrompt = "Descubre clubes — únete o crea el tuyo.",
    clubsSearchPlaceholder = "Buscar clubes",
    clubsAllCategories = "Todos",
    clubsLoading = "Cargando clubes…",
    clubsEmpty = "Aún no hay clubes.",
    clubsUntitled = "Nuevo club",
    clubsCreateTitle = "Crear un club",
    clubsCreateHint = "Escribe el nombre, eslogan, descripción y cómo contactarte. El resto se añade después de crear el club.",
    clubsCreateName = "Nombre del club",
    clubsCreateTagline = "Eslogan",
    clubsCreateDescription = "Descripción",
    clubsCreateHostName = "Tu nombre",
    clubsCreateSubmit = "Crear un club",
    clubsCreateSuccess = "Solicitud de club enviada.",
    clubsCreateError = "No se pudo enviar la solicitud. Inténtalo de nuevo.",
    clubsJoin = "Unirse al club",
    clubsJoinSubmit = "Unirse al club",
    clubsJoinSent = "¡Gracias! El anfitrión te contactará por Zalo.",
    clubsJoinError = "No se pudo enviar la solicitud de unión.",
    clubsYourName = "Tu nombre",
    clubsYourMessage = "Tu mensaje",
    clubsYourMessagePlaceholder = "Algo que quieras decirle al anfitrión",
    clubsHostedBy = "Organizado por",
    clubsDetails = "Detalles",
    adminClubsTab = "Clubes",
    adminAddClub = "Añadir club",
    adminEditClub = "Editar club",
    adminDeleteClub = "Eliminar club",
    adminNoClubs = "Aún no hay clubes.",
    adminClubName = "Nombre",
    adminClubTagline = "Eslogan",
    adminClubDescription = "Descripción",
    adminClubCategory = "Categoría",
    adminClubActivities = "Actividades",
    adminClubHostName = "Nombre del anfitrión",
    adminClubHostZalo = "Zalo del anfitrión",
    adminClubPhotos = "Fotos",
    adminClubAddPhoto = "Añadir fotos",
    adminClubContent = "Historia",
    adminClubAddText = "Añadir texto",
    adminClubAddImage = "Añadir imagen",
    adminClubJoinQuestions = "Preguntas de unión",
    adminClubCreateRequests = "Solicitudes de creación",
    adminClubJoinRequests = "Solicitudes de unión",
    adminNoClubCreateRequests = "No hay solicitudes de creación.",
    adminNoClubJoinRequests = "No hay solicitudes de unión.",
    adminDismissClubRequest = "Descartar",
    adminDismissClubJoin = "Descartar",
    adminStatsTab = "Estadísticas",
    adminStatsHint = "Visitantes por día en hora de Vietnam (UTC+7). Las barras son visitantes; la línea, visitas.",
    adminStatsLoading = "Cargando estadísticas…",
    adminStats7Days = "7 días",
    adminStats30Days = "30 días",
    adminStatsVisitors = "Visitantes",
    adminStatsVisits = "Visitas",
    adminStatsNewVisitors = "Nuevos",
    adminStatsToday = "Hoy",
    adminStatsLast7Days = "Últimos 7 días",
    adminStatsLast30Days = "Últimos 30 días",
    adminStatsAllTime = "Todo",
    adminStatsTopScreens = "Pantallas más vistas",
    adminStatsLanguages = "Idiomas",
    adminStatsNoData = "Aún no hay visitas.",
    adminStatsTimezone = "Horario UTC+7",
    adminStatsSelected = "Día seleccionado",
)

private val German = UiStrings(
    appTitle = "One Verandah App",
    serviceDetails = "Angebotsdetails",
    homePrompt = "Wie können wir dir heute helfen?",
    reviews = { count -> "($count Bewertungen)" },
    sectionBookingType = "Buchungsart",
    sectionDuration = "Dauer",
    sectionTargetArea = "Zielbereich",
    sectionComments = "Anmerkungen",
    sectionReviews = "Bewertungen",
    minutes = { minutes -> "$minutes Minuten" },
    hours = { hours -> "$hours Stunden" },
    from = "ab",
    total = "Gesamt",
    priceSummary = "Preisübersicht",
    tapToSelect = "Tippe auf den Körper, um Bereiche auszuwählen",
    bookThisService = "Diesen Service buchen",
    booking = "Buchung läuft…",
    bookingSuccessTitle = "Buchung bestätigt!",
    bookingSuccessMessage = "Wir haben die Details an deinen Anbieter gesendet. Bis bald!",
    done = "Fertig",
    back = "Zurück",
    menu = "Menü",
    languageName = "Deutsch",
    layoutSwitchToDesktop = "Desktop-Layout",
    layoutSwitchToMobile = "Mobiles Layout",
    layoutFullscreen = "Vollbild",
    layoutExitFullscreen = "Vollbild beenden",
    menuTitle = "One Verandah App",
    menuHome = "Startseite",
    menuAbout = "Über uns",
    menuLanguage = "Sprache",
    menuHelp = "Hilfe & Support",
    menuForAdults = "Für Erwachsene",
    menuForKids = "Für Kinder",
    close = "Schließen",
    bookingDialogTitle = "Buchung bestätigen",
    zaloLabel = "Zalo-Nummer",
    zaloPlaceholder = "Deine Telefonnummer",
    zaloError = "Zalo-Nummer ist erforderlich",
    messageLabel = "Zusätzliche Anmerkungen",
    messagePlaceholder = "Optionale Nachricht an den Anbieter",
    confirmBooking = "Buchung bestätigen",
    confirm = "Bestätigen",
    male = "Junge",
    female = "Mädchen",
    viewFront = "Vorne",
    viewBack = "Hinten",
    aboutText = "Von Bewohnern von One Verandah für Bewohner von One Verandah gemacht.",
    aboutExclusive = "Exklusiv für die Gemeinschaft von One Verandah entwickelt",
    aboutTrusted = "Deine Sicherheit zählt – vertrauenswürdige, geprüfte Anbieter stehen an erster Stelle",
    aboutFree = "Vollständig kostenlos nutzbar, immer",
    helpSuggestFeature = "Funktion vorschlagen",
    helpSuggestFeatureSubtitle = "Sag uns, was du als Nächstes sehen möchtest",
    helpFeedback = "Feedback senden",
    helpFeedbackSubtitle = "Teile deine Gedanken, um uns bei Verbesserungen zu helfen",
    helpReportProblem = "Problem melden",
    helpReportProblemSubtitle = "Gib uns Bescheid, wenn du auf Fehler stößt",
    helpSubmit = "Absenden",
    helpPlaceholder = "Schreibe deine Nachricht hier…",
    helpErrorRequired = "Nachricht ist erforderlich",
    helpSuccessTitle = "Vielen Dank!",
    helpSuccessMessage = "Deine Nachricht ist eingegangen. Wir schätzen dein Feedback!",
    helpListService = "Service anbieten",
    helpListServiceSubtitle = "Biete deine Dienste der Gemeinschaft an",
    listServiceDialogTitle = "Service anbieten",
    serviceDescriptionLabel = "Service beschreiben",
    serviceDescriptionPlaceholder = "Welche Dienste bietest du an?",
    serviceDescriptionError = "Servicebeschreibung ist erforderlich",
    yourNameLabel = "Dein Name",
    yourSexLabel = "Dein Geschlecht",
    yourNamePlaceholder = "Gib deinen Namen ein",
    yourNameError = "Dein Name ist erforderlich",
    blockAddressPlaceholder = "Block- und Wohnungsnummer eingeben (z. B. Block A - A.12.03)",
    blockAddressError = "Block/Adresse ist erforderlich",
    listServiceSuccessTitle = "Service eingereicht!",
    listServiceSuccessMessage = "Deine Service-Details wurden übermittelt. Der Host wird sie prüfen und in Kürze hinzufügen!",
    searchPlaceholder = "Nach Services suchen…",
    noResults = "Drücke Enter für KI-Suche.",
    providedBy = "Angeboten von",
    providerBlockLabel = "Block/Adresse",
    providerPhoneLabel = "Zalo-Telefon",
    providerSexLabel = "Geschlecht",
    providerAboutLabel = "Über mich",
    providerSkillsLabel = "Fähigkeiten",
    providerSkillLevelLabel = "Stufe",
    providerServicesLabel = "Weitere Services von",
    providerAllServicesLabel = "Alle Services von",
    providerDetails = "Anbieter-Details",
    whenLabel = "Wann",
    whenNow = "Jetzt",
    whenToday = "Heute",
    whenTomorrow = "Morgen",
    whenLater = "Später",
    save = "Speichern",
    saved = "Gespeichert",
    share = "Teilen",
    savedToast = "Zu deinen Gespeicherten hinzugefügt",
    removedToast = "Aus Gespeicherten entfernt",
    copyLink = "Link kopieren",
    copied = "Kopiert!",
    copiedToast = "Link in die Zwischenablage kopiert",
    pinnedRecentTitle = "Gespeichert & Kürzlich",
    popularTitle = "Beliebte Services",
    clearAll = "Alle löschen",
    clearAllConfirm = "Alle gespeicherten Services aus dieser Liste entfernen?",
    pinnedEmpty = "Speichere Services, die du magst, oder buche einen, um ihn hier zu sehen!",
    homeViewServices = "Services",
    homeViewCategories = "Kategorien",
    homeLatestStories = "Neueste Geschichten",
    homeLatestStudySessions = "Neueste Lernsitzungen",
    homeLatestChallenges = "Neueste Challenges",
    homeLatestLocations = "Neueste Orte",
    homeLatestClubs = "Neueste Clubs",
    homeLatestMeets = "Neueste Treffen",
    viewAll = "Alle ansehen",
    homeFeedLoadingMore = "Mehr laden…",
    serviceTemporarilyUnavailable = "Dieser Service ist vorübergehend nicht verfügbar",
    chatTitle = "Chat",
    studyTitle = "Lernen",
    studyPrompt = "Arbeite dich in deinem Tempo durch die Sprache!",
    phrasesTitle = "Redewendungen",
    chatPlaceholder = "Frag etwas…",
    chatAiSender = "KI",
    chatYouSender = "Du",
    chatThinking = "Ich überlege…",
    chatError = "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    chatPlaySentence = "Satz abspielen",
    chatPauseSentence = "Satz anhalten",
    chatDefinition = "Definition",
    chatInContext = "In diesem Satz",
    chatResidentDetails = "Bewohner-Details ansehen",
    chatSuggestions = listOf(
        "Flüstere eine sanfte Art, einen Nachbarn am Infinity-Pool anzusprechen",
        "Bring mir eine leuchtende vietnamesische Redewendung für gemeinsame Stille bei",
        "Welches kleine Abenteuer kann ich heute in One Verandah erleben?",
        "Hilf mir, eine einfache Bitte in Earth-Mystic-Sprache zu verwandeln",
    ),
    phrasesBlurb = "Nutze Einladungen in Earth-Mystic-Sprache, um die Gemeinschaft in One Verandah zu bereichern.",
    phrasesAllLocations = "Alle Orte",
    phrasesLoading = "Redewendungen werden geladen…",
    phrasesEmpty = "Noch keine Redewendungen. Schau bald wieder vorbei.",
    phrasesShowVerse = "Tippen, um den ganzen Vers zu lesen",
    phrasesReact = "Reagieren",
    adminTitle = "Admin",
    adminPasswordTitle = "Wie lautet das Passwort?",
    adminPasswordPlaceholder = "Passwort eingeben",
    adminSignIn = "Entsperren",
    adminWrongPassword = "Dieses Passwort öffnet diese Tür nicht.",
    adminLockedHint = "Gib das Admin-Passwort ein, um Redewendungen und Referenzfotos zu verwalten.",
    adminPhrasesTab = "Redewendungen",
    adminPhotosTab = "Referenzfotos",
    adminCharactersTab = "Charaktere",
    adminVoicesTab = "Geklonte Stimmen",
    adminAddPhrase = "Redewendung hinzufügen",
    adminEditPhrase = "Redewendung bearbeiten",
    adminEdit = "Bearbeiten",
    adminDelete = "Löschen",
    adminSave = "Speichern",
    adminSaved = "Gespeichert",
    adminError = "Etwas ist schiefgelaufen",
    adminLogout = "Admin sperren",
    adminPhotoName = "Fotoname",
    adminPhotoNamePlaceholder = "z. B. Infinity Pool Dusk",
    adminPhotoFile = "Fotodatei",
    adminUploadPhoto = "Foto hochladen",
    adminUploading = "Wird hochgeladen…",
    adminPhotosHint = "Benannte Referenzfotos ermöglichen Qwen R2I-Szenen, die tatsächlich im Apartmentkomplex spielen.",
    adminCharactersHint = "Benannte Charakterporträts zur Erstellung von Story-Fotos.",
    adminCharactersEmpty = "Noch keine Charaktere.",
    adminVoicesHint = "Lade eine kurze, saubere Aufnahme pro Sprache hoch. Eine aktive Stimme pro Sprache (oder Fallback für alle Sprachen).",
    adminVoiceName = "Stimmenname",
    adminVoiceNamePlaceholder = "z. B. warm-de-companion",
    adminVoiceLanguage = "Sprache",
    adminVoiceLanguageAll = "Alle Sprachen",
    adminVoiceFile = "Audiodatei",
    adminUploadVoice = "Stimme hochladen",
    adminVoiceActivate = "Diese Stimme verwenden",
    adminVoiceActive = "Aktiv",
    adminVoiceEmpty = "Noch keine geklonten Stimmen. Lade ein Audiobeispiel hoch, um die Standard-TTS-Stimme zu ersetzen.",
    adminDeleteVoiceConfirm = "Diese geklonte Stimme löschen?",
    adminVoiceProviderId = "Provider-ID",
    adminLocationKey = "Standort-Schlüssel",
    adminLocationEn = "Standort (Englisch)",
    adminLocationVi = "Standort (Vietnamesisch)",
    adminIcon = "Symbol",
    adminSpokenEn = "Gesprochen (Englisch)",
    adminSpokenVi = "Gesprochen (Vietnamesisch)",
    adminVerseEn = "Vers (Englisch)",
    adminVerseVi = "Vers (Vietnamesisch)",
    adminRawAskEn = "Ursprüngliche Anfrage (Englisch)",
    adminRawAskVi = "Ursprüngliche Anfrage (Vietnamesisch)",
    adminImageUrl = "Bild-URL",
    adminApproved = "Genehmigt",
    adminUnapproved = "Nicht genehmigt",
    adminDeletePhraseConfirm = "Diese Redewendung löschen?",
    adminDeletePhotoConfirm = "Dieses Referenzfoto löschen?",
    adminDeleteCharacterConfirm = "Diesen Charakter löschen?",
    adminCharacterStudyLocked = "Stammt aus einer Lerneinheit und kann nicht gelöscht werden.",
    adminInstructionsEn = "Anweisungen (Englisch)",
    adminInstructionsVi = "Anweisungen (Vietnamesisch)",
    adminGeneratePhoto = "Foto generieren",
    adminGenerateCover = "Titelbild generieren",
    adminGenerate = "Generieren",
    adminGenerating = "Wird generiert…",
    adminGeneratePrompt = "Prompt",
    adminGenerateHint = "Wähle Referenzfotos der Wohnanlage, schreibe einen Prompt und erstelle die Szene mit Qwen R2I.",
    adminGenerateNoPhotos = "Lade zuerst Referenzfotos hoch, damit die Szene in One Verandah verortet werden kann.",
    adminSelectReferences = "Referenzfotos",
    adminCoverTitle = "Titelbild des Phrasenbuchs",
    adminCoverHint = "Generiere ein Titelbild aus einem Prompt und Referenzfotos der Wohnanlage.",
    adminCoverPromptDefault = "Traumhaftes filmisches Titelbild von Bewohnern von One Verandah, die in der goldenen Stunde sanfte Earth-Mystic-Sprache teilen, weiche Filmkörnung, kein Text",
    adminPhrasePromptDefault = "Traumhaftes filmisches Foto in One Verandah, sanftes mystisches Licht, Bewohner im ruhigen Gespräch, kein Text",
    phrasesSpokenIn = "Gesprochen in Earth-Mystic-Sprache",
    phrasesHowToUse = "Verwendung",
    phrasesVerse = "Vers",
    phrasesOriginStory = "Man sagt, die erste Redewendung sei am Infinity-Pool belauscht worden – von keinem Bewohner gesprochen, sondern vom Wind durch den Garten getragen. Sie klang wie ein altes Gebet aus einer Zukunft, in der die Menschen wieder gelernt haben, behutsam miteinander umzugehen. Wer sie hörte, begann sie selbst zu sprechen. Weitere Redewendungen erblühten. Das Gebäude selbst schien sie auszuatmen. So wurde die Sprache geboren – nicht erfunden, sondern entdeckt.",
    chatEmptyLead = "Einige lichte Türen zum Öffnen – oder frage, was immer dich bewegt.",
    chatEmptyOrAsk = "Oder probiere einen Funken",
    chatExplorePhrases = "Earth-Mystic-Redewendungen",
    chatExplorePhrasesSub = "Erste Einladungen in Lời Huyền Linh",
    chatExploreStories = "Mini-Geschichten",
    chatExploreStoriesSub = "Kleine Geschichten aus dem Gebäude",
    storiesTitle = "Mini-Geschichten",
    storiesComingSoon = "Geschichten sammeln sich",
    storiesComingSoonBody = "Mini-Geschichten sind unterwegs. Erkunde derweil das Earth-Mystic-Phrasenbuch und lass dich von einer Zeile inspirieren.",
    storiesShelfHint = "Ein schöner Weg für Nachbarn, gemeinsam zu lachen und dabei die Sprache des anderen zu lernen.",
    storiesEmpty = "Noch keine Geschichten im Regal. Schau nach dem nächsten Update wieder rein.",
    storiesLoading = "Regal wird geöffnet…",
    storiesFeaturedTitle = "Werde Teil der nächsten Mini-Geschichte",
    storiesFeaturedHint = "Sag uns, welche Rolle du möchtest, und sende 1–3 Fotos von dir.",
    storiesFeaturedRole = "Deine Wunschrolle",
    storiesFeaturedAbout = "Ein wenig über dich",
    storiesFeaturedVibe = "Wie du so bist",
    storiesFeaturedWant = "Was du in der Geschichte tun möchtest",
    storiesFeaturedPhotos = "1 bis 3 Fotos von dir",
    storiesFeaturedSubmit = "Absenden",
    storiesFeaturedSuccess = "Gesendet! Wir schreiben dich in eine zukünftige Geschichte.",
    storiesSuggestTitle = "Schlage eine Story-Idee vor",
    storiesSuggestHint = "Ein Funke genügt – ein Ort, ein Gefühl, ein Missgeschick unter Nachbarn.",
    storiesSuggestPlaceholder = "Deine Geschichtenidee",
    storiesSuggestSubmit = "Idee senden",
    storiesSuggestSuccess = "Idee erhalten. Vielen Dank!",
    storiesSubmitError = "Konnte gerade nicht gesendet werden. Bitte versuche es erneut.",
    storiesLearningMode = "Lernmodus aktivieren",
    storiesContinue = "Weiter",
    storiesReadAgain = "Nochmal lesen",
    storiesPreviousPage = "Vorherige Seite",
    storiesTheEnd = "Ende",
    storiesPageOf = { page, total -> "Seite $page von $total" },
    storiesPages = { count -> if (count == 1) "1 Seite" else "$count Seiten" },
    storiesStudyThisPage = "Diese Seite lernen",
    storiesPublished = "Veröffentlicht",
    storiesDraft = "Entwurf",
    storiesOpens = { count -> if (count == 1) "1 Mal geöffnet" else "$count Mal geöffnet" } ,
    adminStoriesTab = "Geschichten",
    adminAddBook = "Neues Buch",
    adminEditBook = "Buch bearbeiten",
    adminBookName = "Buchname",
    adminBookCategory = "Kategorie",
    adminBookCategoryPlaceholder = "Krimi, Gute Nacht, Alltag…",
    adminPublish = "Veröffentlichen",
    adminUnpublish = "Veröffentlichung aufheben",
    adminConfirmLanguage = "Sprache des Buches bestätigen",
    adminPublishing = "Wird veröffentlicht…",
    adminAddPage = "Seite hinzufügen",
    adminPageText = "Seitentext",
    adminPagePhoto = "Seitenfoto",
    adminSelectScenes = "Szenen",
    adminSceneName = "Szenenname",
    adminUploadScene = "Szene hochladen",
    adminSelectCharacters = "Charaktere",
    adminUploadCharacter = "Charakter hochladen",
    adminGenerateCharacter = "Charakter erzeugen",
    adminCharacterName = "Charaktername",
    adminCharacterPrompt = "Charakterbeschreibung",
    adminNoBooks = "Noch keine Bücher.",
    chatSavedTitle = "Gespeicherte Nachrichten",
    chatSavedEmpty = "Setze ein Lesezeichen für eine Nachricht, um sie später hier auf diesem Gerät zu finden.",
    chatSavedSend = "Senden",
    chatSavedEdit = "Bearbeiten",
    chatSavedRemove = "Entfernen",
    chatExploreSaved = "Gespeicherte Nachrichten",
    chatExploreSavedSub = "Lesezeichen auf diesem Gerät",
    chatExploreStudy = "Lerneinheiten",
    chatExploreStudySub = "Lernen auf diesem Gerät fortsetzen",
    chatStudySessionsTitle = "Lerneinheiten",
    chatStudySessionsMine = "Meine Lerneinheiten",
    chatStudySessionsCommunity = "Aus der Community",
    chatStudySessionsCommunityEmpty = "Noch keine Community-Einheiten.",
    chatStudySessionsEmpty = "Starte oben eine Einheit oder tippe bei einer Chatnachricht auf Lernen.",
    chatStudySessionsPlaceholder = "Text zum Lernen eingeben oder einfügen…",
    chatStudyExactToggle = "Genauer Text",
    chatStudySessionsOpen = "Öffnen",
    chatStudySessionsRemove = "Entfernen",
    chatStudySessionsRename = "Umbenennen",
    chatStudySessionsNamePlaceholder = "Name der Lerneinheit",
    chatStudyAnotherLanguage = "Diese Lektion in einer anderen Sprache lernen",
    chatStudyAnotherLanguagePlaceholder = "Sprache eingeben",
    chatStudyAnotherLanguageConfirm = "Sitzung erstellen",
    chatStudySessionsSaveName = "Namen speichern",
    chatStudySessionsError = "Lerneinheiten konnten nicht aktualisiert werden. Erneut versuchen.",
    chatSaveMessage = "Nachricht speichern",
    chatUnsaveMessage = "Gespeicherte Nachricht entfernen",
    chatSavedError = "Gespeicherte Nachrichten konnten nicht aktualisiert werden. Erneut versuchen.",
    chatStudyMessage = "Nachricht lernen",
    chatStudyTitle = "Lerneinheit",
    chatWatchTab = "Ansehen",
    chatWatchPlay = "Abspielen",
    chatWatchPause = "Pause",
    chatWatchPreparing = "Audio wird vorbereitet…",
    chatWatchSeekLabel = "In der Einheit spulen",
    chatWatchFullscreen = "Vollbild",
    chatWatchExitFullscreen = "Vollbild beenden",
    chatStoryTab = "Geschichte",
    chatStoryContinue = "Weiter",
    chatStoryBack = "Zurück",
    chatStudyTab = "Lernen",
    chatWordsTab = "Wörter",
    chatWordsWord = "Wort",
    chatWordsMeaning = "Bedeutung",
    chatListenTab = "Hören",
    chatReadTab = "Lesen",
    chatWriteTab = "Schreiben",
    chatExpressTab = "Ausdrücken",
    chatPlayTab = "Spielen",
    chatTest = StudyTestStrings(
        tab = "Test",
        sceneLoading = "Testsituation wird erstellt…",
        sceneError = "Testsituation konnte nicht erstellt werden.",
        audioError = "Die Zeile konnte nicht abgespielt werden. Bitte erneut versuchen.",
        retry = "Erneut versuchen",
        listenHint = { name -> "Erkläre in deiner Sprache, was $name gesagt hat." },
        speakHint = "Antworte in der Lernsprache passend zur Situation.",
        playLine = "Nochmal",
        beatProgress = { current, total -> "Runde $current von $total" },
        outlineTitle = "Das wirst du tun",
        start = "Starten",
        inputPlaceholder = "Schreiben oder zum Sprechen gedrückt halten…",
        next = "Weiter",
        finish = "Test beenden",
        scoring = "Antworten werden bewertet…",
        scoreError = "Test konnte nicht bewertet werden. Bitte erneut versuchen.",
        resultTitle = "Testergebnis",
        gradeLabel = "Gesamtnote",
        listeningLabel = "Hörverständnis",
        speakingLabel = "Sprechen",
        retake = "Wiederholen",
        levelBeginner = "Anfänger",
        levelElementary = "Grundstufe",
        levelIntermediate = "Mittelstufe",
        levelAdvanced = "Fortgeschritten",
        levelNative = "Muttersprachlich",
        resultCheer = { level ->
            when (level) {
                5 -> "Du hast es gehört und benutzt. Das ist ein echtes Gespräch."
                4 -> "Scharfes Hören, nützliche Antworten. Fast ganz oben."
                3 -> "Du hast das Gespräch gehalten. Eine echte Mitte."
                2 -> "Du hast einen Teil erwischt. Nochmal macht es fester."
                else -> "Du bist im Gespräch. Die nächste Runde wird klarer."
            }
        },
    ),
    chatAllTab = "Alle",
    chatReadTapHint = "Tippe auf ein Wort, um jedes passende Wort im Text hervorzuheben",
    chatReadPlayText = "Gesamten Text abspielen",
    chatReadPauseText = "Gesamten Text pausieren",
    chatWriteHint = "Tippe oder sprich jeden Satz genau so, wie er angezeigt wird",
    chatWritePlaceholder = "Tippe den Satz…",
    chatWriteCorrect = "Richtig!",
    chatWriteChecking = "Wird überprüft…",
    chatWriteFeedbackError = "Konnte den Versuch nicht erklären. Bitte erneut versuchen.",
    chatExpressHint = "Drücke jede Bedeutung in der Sprache aus, die du lernst",
    chatListenHint = "Höre jeden Satz an und gib die Bedeutung in deiner Sprache ein",
    chatListenPlaceholder = "Bedeutung des Satzes eingeben…",
    chatListenChecking = "Wird überprüft…",
    chatExpressPlaceholder = "Tippe deinen Satz…",
    chatExpressCorrect = "Super!",
    chatExpressChecking = "Wird überprüft…",
    chatExpressFeedbackError = "Konnte diesen Versuch nicht prüfen. Bitte erneut versuchen.",
    chatExpressLoading = "Übungssätze werden gesammelt…",
    chatExpressGenerating = "Weitere Sätze werden erstellt…",
    chatExpressEmpty = "Noch keine Übungssätze. Warte, bis die Bedeutungen geladen sind.",
    chatPlayHint = "Wähle eine Szene und übe ein kurzes Gespräch",
    chatPlayLoading = "Szenarien werden ausgedacht…",
    chatPlayError = "Szenarien konnten nicht geladen werden. Bitte erneut versuchen.",
    chatPlayRetry = "Wiederholen",
    chatPlayPlaceholder = "Antworte in der Lernsprache…",
    chatPlayYouLabel = "Du",
    chatPlayOtherLabel = "Gegenüber",
    chatPlaySending = "Überlegt…",
    chatStudyLoading = "Lerneinheit wird vorbereitet…",
    chatStudyCreationTitle = "Lerneinheit wird erstellt",
    chatStudyCreationText = "Lerntext",
    chatStudyCreationStructure = "Sitzungsstruktur",
    chatStudyCreationDissections = "Wortbedeutungen",
    chatStudyCreationImages = "Bilder",
    chatStudyCreationTest = "Test",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "Lerneinheit konnte nicht gestartet werden. Bitte erneut versuchen.",
    chatStudyLiteral = "Wörtliche Bedeutung",
    chatStudyRegister = "Sprachregister",
    chatStudyContext = "In diesem Kontext",
    chatStudySynonyms = "Ähnliche Synonyme",
    chatStudyExamples = "Beispielsätze",
    chatStudyPlaySentence = "Satz abspielen",
    chatStudyPauseSentence = "Satz pausieren",
    chatStudyTapHint = "Tippe auf ein Wort, um seine Bedeutung zu analysieren",
    chatStudyPreparingMeanings = "Wortbedeutungen werden geladen…",
    chatStudySentenceError = "Bedeutungen für diesen Satz konnten nicht geladen werden.",
    chatStudyRetrySentence = "Wiederholen",
    chatStudyMeaningLoading = "Bedeutung wird geladen…",
    chatStudyPlayground = "Probier es selbst aus",
    chatStudyPlaygroundHint = "Schreibe einen Satz mit diesem Wort, um zu testen, ob er natürlich klingt.",
    chatStudyPlaygroundPlaceholder = "Schreibe einen Satz mit diesem Wort…",
    chatStudyPlaygroundSubmit = "Satz prüfen",
    chatStudyPlaygroundNatural = "Natürlich",
    chatStudyPlaygroundUnnatural = "Nicht natürlich",
    chatStudyPlaygroundError = "Satz konnte nicht geprüft werden. Bitte erneut versuchen.",
    chatStudyPlaygroundImageLoading = "Comic-Szene wird gezeichnet…",
    chatStudyPlaygroundImageError = "Comic-Bild konnte nicht generiert werden.",
    chatStudyPlaygroundImageAlt = "Comic-Szene für dieses Wort",
    chatStudySentenceImageLoading = "Szene wird erstellt…",
    chatStudySentenceImageError = "Szenenbild konnte nicht erstellt werden.",
    chatStudySentenceImageAlt = "Illustration für diesen Satz",
    chatStudyWordImageLoading = "Wortszene wird erstellt…",
    chatStudyWordImageError = "Wortbild konnte nicht erstellt werden.",
    chatStudyWordImageAlt = "Illustration für dieses Wort",
    challengesTitle = "Challenges",
    challengesPrompt = "Lustige Challenges mit der Nachbarschaft!",
    challengesStartNew = "Neue Challenge starten",
    challengesLoading = "Challenges werden geladen…",
    challengesEmpty = "Noch keine öffentlichen Challenges. Starte eine für die Nachbarschaft.",
    challengesInReview = "In Prüfung",
    challengesProcessing = "Dein Video wird verarbeitet…",
    challengesFailed = "Verarbeitung fehlgeschlagen",
    challengesYourName = "Dein Name",
    challengesYourNamePlaceholder = "Wie sollen Nachbarn dich nennen?",
    challengesPickVideo = "Wähle ein Video (bis zu 5 Minuten)",
    challengesRulesTitle = "Challenge-Regeln",
    challengesRulePlace = "Dein Beitrag muss in One Verandah stattfinden",
    challengesRuleFamily = "Dein Beitrag sollte familienfreundlich sein",
    challengesRuleLength = "Dein Beitrag sollte 2–4 Minuten lang sein",
    challengesSubmit = "Video einreichen",
    challengesUploading = "Verarbeitung & Upload",
    challengesVote = "Abstimmen",
    challengesVoted = "Abgestimmt",
    challengesVotes = "Stimmen",
    challengesUntitled = "Neue Challenge",
    challengesEnter = "An dieser Challenge teilnehmen",
    challengesNext = "Nächster Beitrag",
    challengesPrevious = "Vorheriger Beitrag",
    challengesSubmitError = "Video konnte nicht eingereicht werden. Bitte erneut versuchen.",
    challengesVoteError = "Stimme konnte nicht gespeichert werden.",
    chatExploreChallenges = "Challenges",
    chatExploreChallengesSub = "Kämpfe um den Spitzenplatz in der Nachbarschaft",
    chatExploreMeets = "Treffen",
    chatExploreMeetsSub = "Kaffeegespräche und neue Nachbarn",
    adminChallengesTab = "Challenges",
    adminPendingSubmissions = "Neue Beiträge",
    adminPublishSubmission = "Veröffentlichen",
    adminRenameChallenge = "Umbenennen",
    adminDeleteChallenge = "Challenge löschen",
    adminDeleteSubmission = "Beitrag löschen",
    adminNoChallenges = "Noch keine Challenges.",
    adminNoPending = "Nichts wartet auf Prüfung.",
    meetsTitle = "Treffen",
    meetsPrompt = "Kaffeegespräche, Zusammenarbeit und neue Nachbarn. Schreib jemanden an, den du treffen möchtest.",
    meetsStartNew = "Treffen veröffentlichen",
    meetsLoading = "Treffen werden geladen…",
    meetsEmpty = "Noch keine Treffen. Sei der Erste, der Hallo sagt.",
    meetsInReview = "Wartet auf Genehmigung",
    meetsYourName = "Dein Name",
    meetsYourNamePlaceholder = "Wie soll man dich nennen?",
    meetsYourPhoto = "Dein bestes Foto",
    meetsPickPhoto = "Foto auswählen",
    meetsZaloHint = "Für alle sichtbar, solange das Treffen aktiv ist",
    meetsPitchLimit = "Wie viele Personen können dir schreiben?",
    meetsPitchLimitHint = "Jedes zustande gekommene Treffen verbraucht eine Anfrage",
    meetsPitchLabel = "Deine Vorstellung",
    meetsPitchPlaceholder = "Worüber möchtest du sprechen oder was möchtest du unternehmen?",
    meetsSubmit = "Treffen veröffentlichen",
    meetsUploading = "Wird hochgeladen",
    meetsRemaining = { count -> if (count == 1) "1 Anfrage übrig" else "$count Anfragen übrig" },
    meetsPitchPerson = { name -> "Anfrage an $name senden" },
    meetsPitchReplyLabel = "Deine Nachricht",
    meetsPitchReplyPlaceholder = "Warum solltet ihr euch treffen?",
    meetsSendPitch = "Anfrage senden",
    meetsPitchSent = "Anfrage gesendet. Wenn es passt, geben wir deine Zalo-Nummer weiter.",
    meetsSubmitError = "Treffen konnte nicht veröffentlicht werden. Bitte erneut versuchen.",
    meetsPitchError = "Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.",
    meetsNoRemaining = "Dieses Treffen ist voll",
    meetsUntitled = "Neues Treffen",
    meetsCannotPitchOwn = "Dies ist dein eigenes Treffen",
    meetsAlreadyPitched = "Du hast bereits eine Anfrage gesendet",
    adminMeetsTab = "Treffen",
    adminPendingMeets = "Neue Treffen",
    adminPendingPitches = "Neue Treffen-Anfragen",
    adminApproveMeet = "Genehmigen",
    adminCompletePitch = "Als erledigt markieren",
    adminResetRemaining = "Verbleibende zurücksetzen",
    adminResetRemainingHint = "Wie viele Anfragen sollen verbleiben?",
    adminDeleteMeet = "Treffen löschen",
    adminDeletePitch = "Anfrage löschen",
    adminNoMeets = "Noch keine Treffen.",
    adminNoPendingMeets = "Keine neuen Treffen.",
    adminNoPendingPitches = "Keine neuen Anfragen.",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "Zalo des Erstellers",
    adminPitchReplyZalo = "Zalo des Anfragenden",
    locationsTitle = "Orte",
    locationsPrompt = "Entdecke Cafés, Studios und Orte, die gerade entstehen.",
    locationsSearchPlaceholder = "Orte suchen",
    locationsAllCategories = "Alle",
    locationsLoading = "Orte werden geladen…",
    locationsEmpty = "Noch keine Orte. Schau bald wieder vorbei.",
    locationsStatusPlanning = "In Planung",
    locationsStatusComingSoon = "Demnächst",
    locationsStatusOpen = "Geöffnet",
    locationsStatusClosed = "Geschlossen",
    locationsClosesAt = { time -> "Schließt um $time" },
    locationsOpensAt = { time -> "Öffnet um $time" },
    locationsOpensDayAt = { day, time -> "Öffnet $day um $time" },
    locationsCommitFunds = "Geld zusagen",
    locationsContact = "Kontakt",
    locationsDirections = "Route",
    locationsAmountLabel = "Betrag (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "Nachricht",
    locationsMessagePlaceholder = "Eine Notiz an den Gastgeber",
    locationsCommitSubmit = "Zusagen",
    locationsCommitSent = "Danke — wir geben es weiter.",
    locationsCommitError = "Konnte nicht gesendet werden. Bitte erneut versuchen.",
    locationsContactSubmit = "Senden",
    locationsContactSent = "Gesendet. Der Gastgeber schreibt per Zalo.",
    locationsContactError = "Nachricht konnte nicht gesendet werden.",
    locationsContactPlaceholder = "Hallo",
    locationsLikeError = "Like konnte nicht gespeichert werden.",
    locationsSuggestTitle = "Einen Ort vorschlagen",
    locationsSuggestHint = "Kennst du ein Café, Studio oder ein Nachbarschaftsplätzchen, das wir hinzufügen sollten?",
    locationsSuggestName = "Name des Orts",
    locationsSuggestType = "Was für ein Ort",
    locationsSuggestAddress = "Adresse oder wie man ihn findet",
    locationsSuggestWhy = "Warum Nachbarn ihn kennen sollten",
    locationsSuggestSubmit = "Senden",
    locationsSuggestSuccess = "Gesendet! Wir schauen uns das an.",
    locationsSuggestError = "Konnte nicht gesendet werden. Bitte erneut versuchen.",
    locationsUntitled = "Neuer Ort",
    locationsDayMon = "Mo",
    locationsDayTue = "Di",
    locationsDayWed = "Mi",
    locationsDayThu = "Do",
    locationsDayFri = "Fr",
    locationsDaySat = "Sa",
    locationsDaySun = "So",
    chatExploreLocations = "Orte",
    chatExploreLocationsSub = "In der Nähe",
    adminLocationsTab = "Orte",
    adminAddLocation = "Ort hinzufügen",
    adminEditLocation = "Bearbeiten",
    adminDeleteLocation = "Ort löschen",
    adminNoLocations = "Noch keine Orte.",
    adminLocationName = "Name",
    adminLocationType = "Typ",
    adminLocationCategory = "Kategorie",
    adminLocationAddress = "Adresse",
    adminLocationStatus = "Status",
    adminLocationHostZalo = "Host-Zalo",
    adminLocationMapsUrl = "Karten-URL",
    adminLocationHighlights = "Highlights",
    adminLocationPhotos = "Fotos",
    adminLocationAddPhoto = "Fotos hinzufügen",
    adminLocationSchedule = "Öffnungszeiten (UTC+7)",
    adminLocationClosedDay = "Geschlossen",
    adminLocationApplyBelow = "Unten anwenden",
    adminLocationContent = "Geschichte",
    adminLocationAddText = "Text hinzufügen",
    adminLocationAddImage = "Bild hinzufügen",
    adminLocationCommitments = "Zusagen",
    adminLocationContacts = "Nachrichten",
    clubsTitle = "Clubs",
    clubsPrompt = "Entdecke Clubs — tritt bei oder starte deinen eigenen.",
    clubsSearchPlaceholder = "Clubs suchen",
    clubsAllCategories = "Alle",
    clubsLoading = "Clubs werden geladen…",
    clubsEmpty = "Noch keine Clubs.",
    clubsUntitled = "Neuer Club",
    clubsCreateTitle = "Club erstellen",
    clubsCreateHint = "Name, Tagline, Beschreibung und Kontakt angeben. Weitere Details kommen nach der Erstellung.",
    clubsCreateName = "Clubname",
    clubsCreateTagline = "Tagline",
    clubsCreateDescription = "Beschreibung",
    clubsCreateHostName = "Dein Name",
    clubsCreateSubmit = "Club erstellen",
    clubsCreateSuccess = "Club-Anfrage gesendet.",
    clubsCreateError = "Club-Anfrage konnte nicht gesendet werden.",
    clubsJoin = "Club beitreten",
    clubsJoinSubmit = "Club beitreten",
    clubsJoinSent = "Danke! Der Gastgeber meldet sich bei dir über Zalo!",
    clubsJoinError = "Beitrittsanfrage konnte nicht gesendet werden.",
    clubsYourName = "Dein Name",
    clubsYourMessage = "Deine Nachricht",
    clubsYourMessagePlaceholder = "Was der Host wissen soll",
    clubsHostedBy = "Veranstaltet von",
    clubsDetails = "Details",
    adminClubsTab = "Clubs",
    adminAddClub = "Club hinzufügen",
    adminEditClub = "Club bearbeiten",
    adminDeleteClub = "Club löschen",
    adminNoClubs = "Noch keine Clubs.",
    adminClubName = "Name",
    adminClubTagline = "Tagline",
    adminClubDescription = "Beschreibung",
    adminClubCategory = "Kategorie",
    adminClubActivities = "Aktivitäten",
    adminClubHostName = "Host-Name",
    adminClubHostZalo = "Host-Zalo",
    adminClubPhotos = "Fotos",
    adminClubAddPhoto = "Fotos hinzufügen",
    adminClubContent = "Story",
    adminClubAddText = "Text hinzufügen",
    adminClubAddImage = "Bild hinzufügen",
    adminClubJoinQuestions = "Beitrittsfragen",
    adminClubCreateRequests = "Erstellungsanfragen",
    adminClubJoinRequests = "Beitrittsanfragen",
    adminNoClubCreateRequests = "Keine Erstellungsanfragen.",
    adminNoClubJoinRequests = "Keine Beitrittsanfragen.",
    adminDismissClubRequest = "Verwerfen",
    adminDismissClubJoin = "Verwerfen",
    adminStatsTab = "Statistik",
    adminStatsHint = "Besucher pro Tag in der Vietnam-Zeit (UTC+7). Balken sind Besucher, die Linie Aufrufe.",
    adminStatsLoading = "Statistik wird geladen…",
    adminStats7Days = "7 Tage",
    adminStats30Days = "30 Tage",
    adminStatsVisitors = "Besucher",
    adminStatsVisits = "Aufrufe",
    adminStatsNewVisitors = "Neue Besucher",
    adminStatsToday = "Heute",
    adminStatsLast7Days = "Letzte 7 Tage",
    adminStatsLast30Days = "Letzte 30 Tage",
    adminStatsAllTime = "Gesamt",
    adminStatsTopScreens = "Beliebte Screens",
    adminStatsLanguages = "Sprachen",
    adminStatsNoData = "Noch keine Besuche.",
    adminStatsTimezone = "Zeit in UTC+7",
    adminStatsSelected = "Ausgewählter Tag",
)

private val Burmese = UiStrings(
    appTitle = "One Verandah App",
    serviceDetails = "ဝန်ဆောင်မှုအသေးစိတ်",
    homePrompt = "ဒီကနေ့ ဘာကူညီပေးရမလဲ။",
    reviews = { count -> "($count သုံးသပ်ချက်)" },
    sectionBookingType = "ရက်ချိန်းအမျိုးအစား",
    sectionDuration = "ကြာချိန်",
    sectionTargetArea = "ကုသလိုသည့်နေရာ",
    sectionComments = "မှတ်ချက်များ",
    sectionReviews = "သုံးသပ်ချက်များ",
    minutes = { minutes -> "$minutes မိနစ်" },
    hours = { hours -> "$hours နာရီ" },
    from = "မှ",
    total = "စုစုပေါင်း",
    priceSummary = "စျေးနှုန်းအကျဉ်းချုပ်",
    tapToSelect = "နေရာရွေးချယ်ရန် ခန္ဓာကိုယ်ကို နှိပ်ပါ",
    bookThisService = "ဒီဝန်ဆောင်မှုကို ရယူမည်",
    booking = "ရက်ချိန်းယူနေသည်…",
    bookingSuccessTitle = "ရက်ချိန်းယူခြင်း အောင်မြင်ပါသည်!",
    bookingSuccessMessage = "ဝန်ဆောင်မှုပေးသူထံ အသေးစိတ်အချက်အလက်များကို ပေးပို့ပြီးပါပြီ။ မကြာမီ တွေ့ဆုံကြပါစို့!",
    done = "ပြီးပါပြီ",
    back = "နောက်သို့",
    menu = "မီနူး",
    languageName = "မြန်မာစာ",
    layoutSwitchToDesktop = "ဒက်စ်တော့ အသွင်အပြင်",
    layoutSwitchToMobile = "မိုဘိုင်း အသွင်အပြင်",
    layoutFullscreen = "မျက်နှာပြင်ပြည့်",
    layoutExitFullscreen = "မျက်နှာပြင်ပြည့်မှ ထွက်မည်",
    menuTitle = "One Verandah App",
    menuHome = "ပင်မစာမျက်နှာ",
    menuAbout = "ကျွန်ုပ်တို့အကြောင်း",
    menuLanguage = "ဘာသာစကား",
    menuHelp = "အကူအညီနှင့် ပံ့ပိုးမှု",
    menuForAdults = "လူကြီးများအတွက်",
    menuForKids = "ကလေးများအတွက်",
    close = "ပိတ်မည်",
    bookingDialogTitle = "ရက်ချိန်းအတည်ပြုပါ",
    zaloLabel = "Zalo နံပါတ်",
    zaloPlaceholder = "သင့်ဖုန်းနံပါတ်",
    zaloError = "Zalo နံပါတ် လိုအပ်ပါသည်",
    messageLabel = "ထပ်ဆောင်းမှတ်ချက်များ",
    messagePlaceholder = "ဝန်ဆောင်မှုပေးသူထံ စာတိုပေးပို့ရန် (မဖြစ်မနေ မဟုတ်ပါ)",
    confirmBooking = "ရက်ချိန်းအတည်ပြုမည်",
    confirm = "အတည်ပြုမည်",
    male = "အမျိုးသား",
    female = "အမျိုးသမီး",
    viewFront = "အရှေ့",
    viewBack = "အနောက်",
    aboutText = "One Verandah နေထိုင်သူများမှ One Verandah နေထိုင်သူများအတွက် ဖန်တီးထားသည်။",
    aboutExclusive = "One Verandah အသိုက်အဝန်းအတွက် သီးသန့် ဖန်တီးထားသည်",
    aboutTrusted = "သင့်ဘေးကင်းလုံခြုံရေးသည် အဓိကဖြစ်သည် — ယုံကြည်စိတ်ချရပြီး စိစစ်ထားသော ဝန်ဆောင်မှုပေးသူများကို ဦးစားပေးပါသည်",
    aboutFree = "အမြဲတမ်း အခမဲ့ အသုံးပြုနိုင်ပါသည်",
    helpSuggestFeature = "လုပ်ဆောင်ချက်အသစ် အကြံပြုရန်",
    helpSuggestFeatureSubtitle = "နောက်ထပ် ဘာတွေကို တွေ့မြင်လိုလဲ ပြောပြပါ",
    helpFeedback = "အကြံပြုချက် ပေးပို့ရန်",
    helpFeedbackSubtitle = "ပိုမိုကောင်းမွန်စေရန် သင့်အမြင်များကို မျှဝေပါ",
    helpReportProblem = "ပြဿနာတစ်ခု တိုင်ကြားရန်",
    helpReportProblemSubtitle = "သင်ကြုံတွေ့ရသော ပြဿနာများကို အသိပေးပါ",
    helpSubmit = "ပေးပို့မည်",
    helpPlaceholder = "ဤနေရာတွင် သင့်စာကို ရိုက်ထည့်ပါ…",
    helpErrorRequired = "စာတို ရိုက်ထည့်ရန် လိုအပ်ပါသည်",
    helpSuccessTitle = "ကျေးဇူးတင်ပါသည်!",
    helpSuccessMessage = "သင့်ပေးပို့ချက်ကို လက်ခံရရှိပါပြီ။ သင့်အကြံပြုချက်အတွက် ကျေးဇူးတင်ပါသည်!",
    helpListService = "ဝန်ဆောင်မှုတစ်ခု စာရင်းသွင်းရန်",
    helpListServiceSubtitle = "ရပ်ရွာလူထုထံ သင့်ဝန်ဆောင်မှုများကို ကမ်းလှမ်းပါ",
    listServiceDialogTitle = "ဝန်ဆောင်မှုတစ်ခု စာရင်းသွင်းရန်",
    serviceDescriptionLabel = "ဝန်ဆောင်မှုအကြောင်း ဖော်ပြချက်",
    serviceDescriptionPlaceholder = "သင်သည် မည်သည့်ဝန်ဆောင်မှုများကို ပေးပါသလဲ။",
    serviceDescriptionError = "ဝန်ဆောင်မှု ဖော်ပြချက် လိုအပ်ပါသည်",
    yourNameLabel = "သင့်အမည်",
    yourSexLabel = "ကျား/မ",
    yourNamePlaceholder = "သင့်အမည်ကို ရိုက်ထည့်ပါ",
    yourNameError = "သင့်အမည် လိုအပ်ပါသည်",
    blockAddressPlaceholder = "သင့်တိုက်ခန်းနှင့် အခန်းနံပါတ်ကို ရိုက်ထည့်ပါ (ဥပမာ- Block A - A.12.03)",
    blockAddressError = "တိုက်ခန်း/လိပ်စာ လိုအပ်ပါသည်",
    listServiceSuccessTitle = "ဝန်ဆောင်မှု စာရင်းသွင်းခြင်း ပေးပို့ပြီးပါပြီ!",
    listServiceSuccessMessage = "သင့်ဝန်ဆောင်မှု အချက်အလက်များကို ပေးပို့ပြီးပါပြီ။ မကြာမီ စိစစ်ပြီး ထည့်သွင်းပေးပါမည်!",
    searchPlaceholder = "ဝန်ဆောင်မှုများ ရှာဖွေရန်…",
    noResults = "AI ဖြင့် ရှာဖွေရန် Enter နှိပ်ပါ။",
    providedBy = "ဝန်ဆောင်မှုပေးသူ -",
    providerBlockLabel = "တိုက်ခန်း/လိပ်စာ",
    providerPhoneLabel = "Zalo ဖုန်းနံပါတ်",
    providerSexLabel = "ကျား/မ",
    providerAboutLabel = "မိတ်ဆက်",
    providerSkillsLabel = "ကျွမ်းကျင်မှုများ",
    providerSkillLevelLabel = "အဆင့်",
    providerServicesLabel = "အခြားဝန်ဆောင်မှုများ -",
    providerAllServicesLabel = "ဝန်ဆောင်မှုအားလုံး -",
    providerDetails = "ဝန်ဆောင်မှုပေးသူ အသေးစိတ်",
    whenLabel = "အချိန်",
    whenNow = "ယခု",
    whenToday = "ယနေ့",
    whenTomorrow = "မနက်ဖြန်",
    whenLater = "နောက်မှ",
    save = "သိမ်းမည်",
    saved = "သိမ်းထားပြီး",
    share = "မျှဝေမည်",
    savedToast = "သိမ်းဆည်းထားသော စာရင်းထဲသို့ ထည့်ပြီးပါပြီ",
    removedToast = "သိမ်းဆည်းထားသော စာရင်းမှ ဖယ်ရှားပြီးပါပြီ",
    copyLink = "လင့်ခ် ကူးယူမည်",
    copied = "ကူးယူပြီးပါပြီ!",
    copiedToast = "လင့်ခ်ကို ကလစ်ဘုတ်သို့ ကူးယူပြီးပါပြီ",
    pinnedRecentTitle = "သိမ်းဆည်းထားသည်နှင့် လတ်တလော",
    popularTitle = "လူကြိုက်များသော ဝန်ဆောင်မှုများ",
    clearAll = "အားလုံးရှင်းလင်းမည်",
    clearAllConfirm = "သိမ်းဆည်းထားသော ဝန်ဆောင်မှုအားလုံးကို ဖယ်ရှားမလား။",
    pinnedEmpty = "သင်ကြိုက်နှစ်သက်သော ဝန်ဆောင်မှုများကို သိမ်းဆည်းပါ၊ သို့မဟုတ် ရက်ချိန်းယူ၍ ဤနေရာတွင် သိမ်းဆည်းပါ!",
    homeViewServices = "ဝန်ဆောင်မှုများ",
    homeViewCategories = "ကဏ္ဍများ",
    homeLatestStories = "လတ်တလော ပုံပြင်များ",
    homeLatestStudySessions = "လတ်တလော လေ့လာမှုအစည်းအဝေးများ",
    homeLatestChallenges = "လတ်တလော စိန်ခေါ်မှုများ",
    homeLatestLocations = "လတ်တလော နေရာများ",
    homeLatestClubs = "လတ်တလော ကလပ်များ",
    homeLatestMeets = "လတ်တလော တွေ့ဆုံမှုများ",
    viewAll = "အားလုံးကြည့်မည်",
    homeFeedLoadingMore = "ထပ်မံဖွင့်နေသည်…",
    serviceTemporarilyUnavailable = "ဤဝန်ဆောင်မှုကို ယာယီ မရရှိနိုင်ပါ",
    chatTitle = "စကားပြောခန်း",
    studyTitle = "လေ့လာရန်",
    studyPrompt = "သင့်စိတ်ကြိုက်အရှိန်ဖြင့် ဘာသာစကားကို လေ့လာပါ!",
    phrasesTitle = "စကားစုများ",
    chatPlaceholder = "ဘာမဆို မေးမြန်းနိုင်ပါသည်…",
    chatAiSender = "AI",
    chatYouSender = "သင်",
    chatThinking = "စဉ်းစားနေပါသည်…",
    chatError = "တစ်စုံတစ်ခု မှားယွင်းနေပါသည်။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatPlaySentence = "ဝါကျကို အသံဖွင့်မည်",
    chatPauseSentence = "ဝါကျအသံကို ခေတ္တရပ်မည်",
    chatDefinition = "အဓိပ္ပာယ်ဖွင့်ဆိုချက်",
    chatInContext = "ဤဝါကျအတွင်း",
    chatResidentDetails = "နေထိုင်သူ အသေးစိတ်ကို ကြည့်မည်",
    chatSuggestions = listOf(
        "ရေကူးကန်မှာ အိမ်နီးချင်းနဲ့ မိတ်ဖွဲ့ဖို့ ယဉ်ကျေးတဲ့စကား အကြံပေးပါ",
        "တိတ်ဆိတ်ငြိမ်သက်မှုကို ဖော်ညွှန်းတဲ့ ဗီယက်နမ်စကားစု သင်ပေးပါ",
        "ဒီကနေ့ One Verandah မှာ ဘာထူးခြားတာတွေ လုပ်ကြည့်လို့ရမလဲ။",
        "ရိုးရိုးတောင်းဆိုမှုကို Earth-Mystic စကားအဖြစ် ပြောင်းလဲပေးပါ",
    ),
    phrasesBlurb = "One Verandah ရှိ သင့်အသိုက်အဝန်းကို ပိုမိုလှပစေရန် Earth-Mystic စကားစု ဖိတ်ခေါ်ချက်များကို အသုံးပြုပါ။",
    phrasesAllLocations = "နေရာအားလုံး",
    phrasesLoading = "စကားစုများကို ဖွင့်နေသည်…",
    phrasesEmpty = "စကားစုများ မရှိသေးပါ။ မကြာမီ ပြန်လာကြည့်ပါ။",
    phrasesShowVerse = "ကဗျာအပြည့်အစုံကို ဖတ်ရန် နှိပ်ပါ",
    phrasesReact = "တုံ့ပြန်မည်",
    adminTitle = "အက်ဒမင်",
    adminPasswordTitle = "စကားဝှက်က ဘာလဲ။",
    adminPasswordPlaceholder = "စကားဝှက် ရိုက်ထည့်ပါ",
    adminSignIn = "သော့ဖွင့်မည်",
    adminWrongPassword = "စကားဝှက် မှားယွင်းနေပါသည်။",
    adminLockedHint = "စကားစုများနှင့် ကိုးကားဓာတ်ပုံများကို စီမံခန့်ခွဲရန် အက်ဒမင်စကားဝှက်ကို ရိုက်ထည့်ပါ။",
    adminPhrasesTab = "စကားစုများ",
    adminPhotosTab = "ကိုးကားဓာတ်ပုံများ",
    adminCharactersTab = "ဇာတ်ကောင်များ",
    adminVoicesTab = "အသံတုများ",
    adminAddPhrase = "စကားစုအသစ် ထည့်မည်",
    adminEditPhrase = "စကားစု ပြင်ဆင်မည်",
    adminEdit = "ပြင်ဆင်မည်",
    adminDelete = "ဖျက်မည်",
    adminSave = "သိမ်းဆည်းမည်",
    adminSaved = "သိမ်းဆည်းပြီးပါပြီ",
    adminError = "တစ်စုံတစ်ခု မှားယွင်းနေပါသည်",
    adminLogout = "အက်ဒမင်ကို သော့ခတ်မည်",
    adminPhotoName = "ဓာတ်ပုံအမည်",
    adminPhotoNamePlaceholder = "ဥပမာ- Infinity Pool Dusk",
    adminPhotoFile = "ဓာတ်ပုံဖိုင်",
    adminUploadPhoto = "ဓာတ်ပုံ တင်မည်",
    adminUploading = "တင်နေသည်…",
    adminPhotosHint = "အမည်တပ်ထားသော ကိုးကားဓာတ်ပုံများသည် တိုက်ခန်းရှိ အမှန်တကယ်မြင်ကွင်းများကို Qwen R2I ဖြင့် ဖန်တီးပေးပါသည်။",
    adminCharactersHint = "ပုံပြင်ဓာတ်ပုံများ ထုတ်လုပ်ရာတွင် အသုံးပြုသည့် ဇာတ်ကောင်ပုံတူများ။",
    adminCharactersEmpty = "ဇာတ်ကောင် မရှိသေးပါ။",
    adminVoicesHint = "ဘာသာစကားတစ်ခုစီအတွက် တိုတောင်းပြီး ကြည်လင်သောအသံနမူနာ တင်ပါ။ ဘာသာစကားတစ်ခုလျှင် အသုံးပြုနေသောအသံတစ်ခု (သို့မဟုတ် ဘာသာစကားအားလုံးအတွက်)။",
    adminVoiceName = "အသံအမည်",
    adminVoiceNamePlaceholder = "ဥပမာ- warm-vi-companion",
    adminVoiceLanguage = "ဘာသာစကား",
    adminVoiceLanguageAll = "ဘာသာစကားအားလုံး",
    adminVoiceFile = "အသံနမူနာဖိုင်",
    adminUploadVoice = "အသံ တင်မည်",
    adminVoiceActivate = "ဒီအသံကို သုံးမည်",
    adminVoiceActive = "လက်ရှိသုံးနေသည်",
    adminVoiceEmpty = "အသံတုများ မရှိသေးပါ။ ပုံမှန် TTS အသံအစားထိုးရန် နမူနာတင်ပါ။",
    adminDeleteVoiceConfirm = "ဤအသံတုကို ဖျက်မလား။",
    adminVoiceProviderId = "ပံ့ပိုးသူ ID",
    adminLocationKey = "နေရာ သော့ချက်",
    adminLocationEn = "နေရာ (အင်္ဂလိပ်)",
    adminLocationVi = "နေရာ (ဗီယက်နမ်)",
    adminIcon = "အိုင်ကွန်",
    adminSpokenEn = "အပြော (အင်္ဂလိပ်)",
    adminSpokenVi = "အပြော (ဗီယက်နမ်)",
    adminVerseEn = "ကဗျာ (အင်္ဂလိပ်)",
    adminVerseVi = "ကဗျာ (ဗီယက်နမ်)",
    adminRawAskEn = "မူရင်းမေးခွန်း (အင်္ဂလိပ်)",
    adminRawAskVi = "မူရင်းမေးခွန်း (ဗီယက်နမ်)",
    adminImageUrl = "ပုံ URL",
    adminApproved = "အတည်ပြုပြီး",
    adminUnapproved = "အတည်မပြုရသေး",
    adminDeletePhraseConfirm = "ဤစကားစုကို ဖျက်မလား။",
    adminDeletePhotoConfirm = "ဤကိုးကားဓာတ်ပုံကို ဖျက်မလား။",
    adminDeleteCharacterConfirm = "ဤဇာတ်ကောင်ကို ဖျက်မလား။",
    adminCharacterStudyLocked = "လေ့လာမှုအပိုင်းတစ်ခုမှ ဖြစ်သောကြောင့် ဖျက်၍မရပါ။",
    adminInstructionsEn = "ညွှန်ကြားချက် (အင်္ဂလိပ်)",
    adminInstructionsVi = "ညွှန်ကြားချက် (ဗီယက်နမ်)",
    adminGeneratePhoto = "ဓာတ်ပုံ ထုတ်လုပ်မည်",
    adminGenerateCover = "မျက်နှာဖုံး ထုတ်လုပ်မည်",
    adminGenerate = "ထုတ်လုပ်မည်",
    adminGenerating = "ထုတ်လုပ်နေသည်…",
    adminGeneratePrompt = "ညွှန်ကြားချက် (Prompt)",
    adminGenerateHint = "တိုက်ခန်း ကိုးကားဓာတ်ပုံများကို ရွေးချယ်ပါ၊ Prompt ရေးသားပြီး Qwen R2I ဖြင့် မြင်ကွင်းဖန်တီးပါ။",
    adminGenerateNoPhotos = "One Verandah မြင်ကွင်းကို အခြေခံနိုင်ရန် ဦးစွာ ကိုးကားဓာတ်ပုံများ တင်ပါ။",
    adminSelectReferences = "ကိုးကားဓာတ်ပုံများ",
    adminCoverTitle = "စကားစုစာအုပ် မျက်နှာဖုံး",
    adminCoverHint = "Prompt နှင့် တိုက်ခန်းကိုးကားဓာတ်ပုံများမှ မျက်နှာဖုံး ထုတ်လုပ်ပါ။",
    adminCoverPromptDefault = "Dreamlike cinematic cover of One Verandah residents sharing gentle Earth-Mystic Parlance at golden hour, soft film grain, no text",
    adminPhrasePromptDefault = "Dreamlike cinematic photo at One Verandah, soft mystical light, residents in gentle conversation, no text",
    phrasesSpokenIn = "Earth-Mystic စကားဖြင့် ပြောဆိုပါသည်",
    phrasesHowToUse = "အသုံးပြုပုံ",
    phrasesVerse = "ကဗျာ",
    phrasesOriginStory = "ပထမဆုံးစကားစုကို အဆုံးမဲ့ရေကူးကန်အနီးတွင် ကြားခဲ့ရသည်ဟု ဆိုကြသည် — မည်သည့်နေထိုင်သူမှ ပြောခြင်းမဟုတ်ဘဲ ဥယျာဉ်ထဲ လေပြည်နှင့်အတူ ပါလာခြင်းဖြစ်သည်။ တစ်ဦးနှင့်တစ်ဦး ညင်သာစွာ ဆက်ဆံနည်းကို ပြန်လည်မှတ်မိလာကြသည့် အနာဂတ်မှ ရှေးဟောင်းဆုတောင်းတစ်ခုကဲ့သို့ ဖြစ်သည်။ ကြားရသူများက စတင်ပြောဆိုလာကြပြီး စကားစုများ ပိုမိုပွင့်လန်းလာခဲ့သည်။ အဆောက်အအုံကိုယ်တိုင်ကပင် ယင်းတို့ကို သက်ပြင်းချသလို ထုတ်ဖော်ပေးနေပုံရသည်။ ထို့ကြောင့် ဤစကားသည် တီထွင်ခဲ့ခြင်းမဟုတ်ဘဲ ရှာဖွေတွေ့ရှိခဲ့ခြင်း ဖြစ်သည်။",
    chatEmptyLead = "တံခါးအချို့ကို ဖွင့်လှစ်ပါ — သို့မဟုတ် သင့်ရင်ထဲရှိရာကို မေးမြန်းပါ။",
    chatEmptyOrAsk = "သို့မဟုတ် စတင်ကြည့်ပါ",
    chatExplorePhrases = "Earth-Mystic စကားစုများ",
    chatExplorePhrasesSub = "Lời Huyền Linh အစပြုဖိတ်ခေါ်ချက်များ",
    chatExploreStories = "ပုံပြင်တိုများ",
    chatExploreStoriesSub = "အဆောက်အအုံမှ ဇာတ်လမ်းတိုများ",
    storiesTitle = "ပုံပြင်တိုများ",
    storiesComingSoon = "ပုံပြင်များ စုစည်းနေသည်",
    storiesComingSoonBody = "ပုံပြင်တိုများ မကြာမီ လာပါတော့မည်။ ထိုအတောအတွင်း Earth-Mystic စကားစုများကို လေ့လာကြည့်ရှုပါ။",
    storiesShelfHint = "အိမ်နီးချင်းများ အချင်းချင်း ဘာသာစကား သင်ယူရင်း အတူရယ်မောနိုင်မည့် ပျော်စရာနည်းလမ်း။",
    storiesEmpty = "စင်ပေါ်တွင် ပုံပြင်များ မရှိသေးပါ။ နောက်တစ်ကြိမ် ထပ်မံစစ်ဆေးပါ။",
    storiesLoading = "စင်ကို ဖွင့်နေသည်…",
    storiesFeaturedTitle = "နောက်ထွက်မည့် ပုံပြင်တိုတွင် ပါဝင်လိုက်ပါ",
    storiesFeaturedHint = "သင်လိုချင်သော ဇာတ်ကောင်နေရာကို ပြောပြပြီး သင့်ဓာတ်ပုံ ၁–၃ ပုံ ပေးပို့ပါ။",
    storiesFeaturedRole = "သင်လိုချင်သော ဇာတ်ကောင်နေရာ",
    storiesFeaturedAbout = "သင့်အကြောင်း အနည်းငယ်",
    storiesFeaturedVibe = "သင့်ပင်ကိုယ်စရိုက်",
    storiesFeaturedWant = "ပုံပြင်ထဲတွင် သင်ဘာလုပ်ချင်သလဲ",
    storiesFeaturedPhotos = "သင့်ဓာတ်ပုံ ၁ မှ ၃ ပုံ",
    storiesFeaturedSubmit = "ပေးပို့မည်",
    storiesFeaturedSuccess = "ပေးပို့ပြီးပါပြီ! နောင်ထွက်မည့် ပုံပြင်ထဲတွင် သင့်ကို ထည့်သွင်းပေးပါမည်။",
    storiesSuggestTitle = "ပုံပြင် စိတ်ကူးတစ်ခု အကြံပြုရန်",
    storiesSuggestHint = "စိတ်ကူးအစအနလေးဆို လုံလောက်ပါသည် — နေရာတစ်ခု၊ ခံစားချက်တစ်ခု၊ အိမ်နီးချင်း အဖြစ်အပျက်တစ်ခု။",
    storiesSuggestPlaceholder = "သင့်ပုံပြင် စိတ်ကူး",
    storiesSuggestSubmit = "စိတ်ကူး ပေးပို့မည်",
    storiesSuggestSuccess = "စိတ်ကူးကို လက်ခံရရှိပါပြီ။ ကျေးဇူးတင်ပါသည်။",
    storiesSubmitError = "ယခု ပေးပို့၍ မရသေးပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    storiesLearningMode = "လေ့လာရေးမုဒ် ဖွင့်မည်",
    storiesContinue = "ဆက်လက်ဖတ်ရှုမည်",
    storiesReadAgain = "ပြန်လည်ဖတ်ရှုမည်",
    storiesPreviousPage = "ယခင်စာမျက်နှာ",
    storiesTheEnd = "ပြီးပါပြီ",
    storiesPageOf = { page, total -> "စာမျက်နှာ $page / $total" },
    storiesPages = { count -> "$count မျက်နှာ" },
    storiesStudyThisPage = "ဒီစာမျက်နှာကို လေ့လာမည်",
    storiesPublished = "ထုတ်ဝေပြီး",
    storiesDraft = "မူကြမ်း",
    storiesOpens = { count -> "$count ကြိမ် ဖွင့်ဖတ်ထားသည်" },
    adminStoriesTab = "ပုံပြင်များ",
    adminAddBook = "စာအုပ်အသစ်",
    adminEditBook = "စာအုပ် ပြင်ဆင်မည်",
    adminBookName = "စာအုပ်အမည်",
    adminBookCategory = "ကဏ္ဍ",
    adminBookCategoryPlaceholder = "စုံထောက်၊ အိပ်ရာဝင်၊ လူမှုရေး…",
    adminPublish = "ထုတ်ဝေမည်",
    adminUnpublish = "ထုတ်ဝေမှု ပယ်ဖျက်မည်",
    adminConfirmLanguage = "စာအုပ်၏ ဘာသာစကားကို အတည်ပြုပါ",
    adminPublishing = "ထုတ်ဝေနေသည်…",
    adminAddPage = "စာမျက်နှာ ထည့်မည်",
    adminPageText = "စာမျက်နှာ စာသား",
    adminPagePhoto = "စာမျက်နှာ ဓာတ်ပုံ",
    adminSelectScenes = "မြင်ကွင်းများ",
    adminSceneName = "မြင်ကွင်းအမည်",
    adminUploadScene = "မြင်ကွင်း တင်မည်",
    adminSelectCharacters = "ဇာတ်ကောင်များ",
    adminUploadCharacter = "ဇာတ်ကောင် တင်မည်",
    adminGenerateCharacter = "ဇာတ်ကောင် ထုတ်လုပ်မည်",
    adminCharacterName = "ဇာတ်ကောင်အမည်",
    adminCharacterPrompt = "ဇာတ်ကောင် Prompt",
    adminNoBooks = "စာအုပ် မရှိသေးပါ။",
    chatSavedTitle = "သိမ်းဆည်းထားသော စာတိုများ",
    chatSavedEmpty = "ဤစက်တွင် နောက်မှ ပြန်လည်ရှာဖွေနိုင်ရန် မည်သည့်စာတိုကိုမဆို မှတ်သားထားပါ။",
    chatSavedSend = "ပေးပို့မည်",
    chatSavedEdit = "ပြင်ဆင်မည်",
    chatSavedRemove = "ဖယ်ရှားမည်",
    chatExploreSaved = "သိမ်းဆည်းထားသော စာတိုများ",
    chatExploreSavedSub = "ဤစက်မှ မှတ်သားချက်များ",
    chatExploreStudy = "လေ့လာမှုအပိုင်းများ",
    chatExploreStudySub = "ဤစက်မှ လေ့လာမှုကို ဆက်လက်လုပ်ဆောင်ပါ",
    chatStudySessionsTitle = "လေ့လာမှုအပိုင်းများ",
    chatStudySessionsMine = "ကျွန်ုပ်၏ လေ့လာမှုများ",
    chatStudySessionsCommunity = "ရပ်ရွာလူထုထံမှ",
    chatStudySessionsCommunityEmpty = "ရပ်ရွာ လေ့လာမှုများ မရှိသေးပါ။",
    chatStudySessionsEmpty = "အထက်တွင် လေ့လာမှုတစ်ခု စတင်ပါ၊ သို့မဟုတ် စကားပြောခန်းရှိ စာတိုတစ်ခုခုတွင် လေ့လာရန် နှိပ်ပါ။",
    chatStudySessionsPlaceholder = "လေ့လာရန် စာသားကို ကူးထည့်ပါ သို့မဟုတ် ရိုက်ထည့်ပါ…",
    chatStudyExactToggle = "အတိအကျ စာသား",
    chatStudySessionsOpen = "ဖွင့်မည်",
    chatStudySessionsRemove = "ဖယ်ရှားမည်",
    chatStudySessionsRename = "အမည်ပြောင်းမည်",
    chatStudySessionsNamePlaceholder = "လေ့လာမှုအပိုင်း အမည်",
    chatStudySessionsSaveName = "အမည် သိမ်းဆည်းမည်",
    chatStudyAnotherLanguage = "ဤသင်ခန်းစာကို အခြားဘာသာဖြင့် လေ့လာမည်",
    chatStudyAnotherLanguagePlaceholder = "ဘာသာစကား ရိုက်ထည့်ပါ",
    chatStudyAnotherLanguageConfirm = "သင်ခန်းစာ ဖန်တီးမည်",
    chatStudySessionsError = "လေ့လာမှုအပိုင်းများကို အပ်ဒိတ်လုပ်၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatSaveMessage = "စာတို သိမ်းဆည်းမည်",
    chatUnsaveMessage = "သိမ်းဆည်းထားသော စာတိုကို ဖယ်ရှားမည်",
    chatSavedError = "သိမ်းဆည်းထားသော စာတိုများကို အပ်ဒိတ်လုပ်၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatStudyMessage = "စာတိုကို လေ့လာမည်",
    chatStudyTitle = "လေ့လာမှုအပိုင်း",
    chatWatchTab = "ကြည့်ရှုရန်",
    chatWatchPlay = "ဖွင့်မည်",
    chatWatchPause = "ခေတ္တရပ်မည်",
    chatWatchPreparing = "အသံ ပြင်ဆင်နေသည်…",
    chatWatchSeekLabel = "လေ့လာမှုအပိုင်းတစ်လျှောက် ရွှေ့ပါ",
    chatWatchFullscreen = "မျက်နှာပြင်ပြည့်",
    chatWatchExitFullscreen = "မျက်နှာပြင်ပြည့်မှ ထွက်မည်",
    chatStoryTab = "ပုံပြင်",
    chatStoryContinue = "ဆက်လုပ်မည်",
    chatStoryBack = "နောက်သို့",
    chatStudyTab = "လေ့လာရန်",
    chatWordsTab = "ဝေါဟာရ",
    chatWordsWord = "စကားလုံး",
    chatWordsMeaning = "အဓိပ္ပာယ်",
    chatListenTab = "နားထောင်ရန်",
    chatReadTab = "ဖတ်ရှုရန်",
    chatWriteTab = "ရေးသားရန်",
    chatExpressTab = "ပြောဆိုရန်",
    chatPlayTab = "သရုပ်ဆောင်ရန်",
    chatTest = StudyTestStrings(
        tab = "စမ်းသပ်မှု",
        sceneLoading = "စမ်းသပ်မှုအခြေအနေကို ဖန်တီးနေသည်…",
        sceneError = "စမ်းသပ်မှုအခြေအနေကို ဖန်တီး၍မရပါ။",
        audioError = "စကားကို ဖွင့်၍မရပါ။ ပြန်စမ်းပါ။",
        retry = "ပြန်စမ်းပါ",
        listenHint = { name -> "$name ပြောခဲ့တာကို သင့်ဘာသာစကားဖြင့် ရှင်းပြပါ။" },
        speakHint = "လေ့လာနေသော ဘာသာစကားဖြင့် အခြေအနေနှင့်ကိုက်ညီအောင် ပြန်ဖြေပါ။",
        playLine = "ပြန်ဖွင့်",
        beatProgress = { current, total -> "အကြိမ် $current / $total" },
        outlineTitle = "သင်လုပ်ရမည့်အရာများ",
        start = "စတင်ရန်",
        inputPlaceholder = "ရိုက်ထည့်ပါ သို့မဟုတ် ဖိထားပြီး ပြောပါ…",
        next = "နောက်တစ်ခု",
        finish = "စမ်းသပ်မှု ပြီးရန်",
        scoring = "အဖြေများကို အမှတ်ပေးနေသည်…",
        scoreError = "စမ်းသပ်မှုကို အမှတ်မပေးနိုင်ပါ။ ပြန်စမ်းပါ။",
        resultTitle = "စမ်းသပ်မှုရလဒ်",
        gradeLabel = "စုစုပေါင်းအဆင့်",
        listeningLabel = "နားထောင်နားလည်မှု",
        speakingLabel = "စကားပြော",
        retake = "ပြန်လည်ဖြေဆိုရန်",
        levelBeginner = "အစပြုသူ",
        levelElementary = "အခြေခံ",
        levelIntermediate = "အလယ်အလတ်",
        levelAdvanced = "အဆင့်မြင့်",
        levelNative = "မိခင်ဘာသာစကားပြောသူ",
        resultCheer = { level ->
            when (level) {
                5 -> "ကြားပြီး အခြေအနေထဲမှာ သုံးနိုင်ခဲ့တယ်။ တကယ့်စကားဝိုင်းပါ။"
                4 -> "နားထောင်မှု ထက်မြက်ပြီး အဖြေတွေ အသုံးဝင်တယ်။ ထိပ်ဆုံးနီးပါး။"
                3 -> "စကားဝိုင်းကို ထိန်းထားနိုင်ခဲ့တယ်။ တကယ့်အလယ်အလတ်ပါ။"
                2 -> "တစ်စိတ်တစ်ပိုင်း ဖမ်းမိခဲ့တယ်။ ပြန်ဖြေရင် ပိုခိုင်မယ်။"
                else -> "စကားဝိုင်းထဲ ရောက်နေပြီ။ နောက်အကြိမ်မှာ ပိုရှင်းမယ်။"
            }
        },
    ),
    chatAllTab = "အားလုံး",
    chatReadTapHint = "စာသားထဲရှိ ကိုက်ညီသောစကားလုံးအားလုံးကို အသားပေးပြရန် စကားလုံးတစ်လုံးကို နှိပ်ပါ",
    chatReadPlayText = "စာသားတစ်ခုလုံးကို အသံဖွင့်မည်",
    chatReadPauseText = "စာသားအသံကို ခေတ္တရပ်မည်",
    chatWriteHint = "ပြထားသည့်အတိုင်း ဝါကျတစ်ခုစီကို အတိအကျ ရိုက်ထည့်ပါ သို့မဟုတ် အသံဖြင့် ပြောပါ",
    chatWritePlaceholder = "ဝါကျကို ရိုက်ထည့်ပါ…",
    chatWriteCorrect = "မှန်ကန်ပါသည်!",
    chatWriteChecking = "စစ်ဆေးနေသည်…",
    chatWriteFeedbackError = "ထိုကြိုးပမ်းမှုကို ရှင်းပြ၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatExpressHint = "သင်လေ့လာနေသော ဘာသာစကားဖြင့် အဓိပ္ပာယ်ကို ဖော်ပြပါ",
    chatListenHint = "ဝါကျတစ်ခုစီကို နားထောင်ပြီး သင့်ဘာသာစကားဖြင့် အဓိပ္ပာယ်ကို ရိုက်ထည့်ပါ",
    chatListenPlaceholder = "ဝါကျ၏ အဓိပ္ပာယ်ကို ရိုက်ထည့်ပါ…",
    chatListenChecking = "စစ်ဆေးနေသည်…",
    chatExpressPlaceholder = "သင့်ဝါကျကို ရိုက်ထည့်ပါ…",
    chatExpressCorrect = "တော်လိုက်တာ!",
    chatExpressChecking = "စစ်ဆေးနေသည်…",
    chatExpressFeedbackError = "ထိုကြိုးပမ်းမှုကို စစ်ဆေး၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatExpressLoading = "လေ့ကျင့်ရန် ဝါကျများ စုစည်းနေသည်…",
    chatExpressGenerating = "နောက်ထပ် ဝါကျများ ဖန်တီးနေသည်…",
    chatExpressEmpty = "လေ့ကျင့်ရန် ဝါကျများ မရှိသေးပါ။ အဓိပ္ပာယ်များ ပေါ်လာသည်အထိ စောင့်ပါ။",
    chatPlayHint = "မြင်ကွင်းတစ်ခု ရွေးချယ်ပြီး စကားပြောခန်းတိုတစ်ခု လေ့ကျင့်ပါ",
    chatPlayLoading = "ဇာတ်လမ်း မြင်ကွင်းများ ဖန်တီးနေသည်…",
    chatPlayError = "သရုပ်ဆောင် မြင်ကွင်းများကို ဖွင့်၍မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatPlayRetry = "ထပ်မံကြိုးစားမည်",
    chatPlayPlaceholder = "လေ့လာနေသော ဘာသာစကားဖြင့် ပြန်လည်ဖြေကြားပါ…",
    chatPlayYouLabel = "သင်",
    chatPlayOtherLabel = "သူတို့",
    chatPlaySending = "စဉ်းစားနေပါသည်…",
    chatStudyLoading = "သင့်လေ့လာမှုအပိုင်းကို ပြင်ဆင်နေသည်…",
    chatStudyCreationTitle = "သင့်လေ့လာမှုအပိုင်းကို ဖန်တီးနေသည်",
    chatStudyCreationText = "လေ့လာရန် စာသား",
    chatStudyCreationStructure = "အပိုင်း ဖွဲ့စည်းပုံ",
    chatStudyCreationDissections = "စကားလုံး အဓိပ္ပာယ်များ",
    chatStudyCreationImages = "ရုပ်ပုံများ",
    chatStudyCreationTest = "စမ်းသပ်မှု",
    chatStudyCreationCount = { done, total -> "$done / $total" },
    chatStudyError = "လေ့လာမှုအပိုင်းကို စတင်၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatStudyLiteral = "တိုက်ရိုက်အဓိပ္ပာယ်",
    chatStudyRegister = "အသုံးအနှုန်း အဆင့်",
    chatStudyContext = "ဤအခြေအနေတွင်",
    chatStudySynonyms = "အဓိပ္ပာယ်တူ စကားလုံးများ",
    chatStudyExamples = "ဥပမာ ဝါကျများ",
    chatStudyPlaySentence = "ဝါကျကို အသံဖွင့်မည်",
    chatStudyPauseSentence = "ဝါကျအသံကို ခေတ္တရပ်မည်",
    chatStudyTapHint = "စကားလုံး၏ အဓိပ္ပာယ်ကို ခွဲခြမ်းစိတ်ဖြာရန် နှိပ်ပါ",
    chatStudyPreparingMeanings = "စကားလုံး အဓိပ္ပာယ်များကို ဖွင့်နေသည်…",
    chatStudySentenceError = "ဤဝါကျအတွက် အဓိပ္ပာယ်များကို ဖွင့်၍မရပါ။",
    chatStudyRetrySentence = "ထပ်မံကြိုးစားမည်",
    chatStudyMeaningLoading = "အဓိပ္ပာယ် ဖွင့်နေသည်…",
    chatStudyPlayground = "ကိုယ်တိုင် စမ်းသပ်ကြည့်ပါ",
    chatStudyPlaygroundHint = "ဤစကားလုံးသည် သဘာဝကျမှု ရှိမရှိ စမ်းသပ်ရန် ၎င်းကို သုံးပြီး ဝါကျတစ်ခု ရေးပါ။",
    chatStudyPlaygroundPlaceholder = "ဤစကားလုံးပါသော ဝါကျတစ်ခု ရိုက်ထည့်ပါ…",
    chatStudyPlaygroundSubmit = "ဝါကျ စစ်ဆေးမည်",
    chatStudyPlaygroundNatural = "သဘာဝကျပါသည်",
    chatStudyPlaygroundUnnatural = "သဘာဝမကျပါ",
    chatStudyPlaygroundError = "ထိုဝါကျကို စစ်ဆေး၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    chatStudyPlaygroundImageLoading = "ကာတွန်းမြင်ကွင်း ရေးဆွဲနေသည်…",
    chatStudyPlaygroundImageError = "ကာတွန်းရုပ်ပုံ မထုတ်လုပ်နိုင်ပါ။",
    chatStudyPlaygroundImageAlt = "ဤစကားလုံးအတွက် ကာတွန်းမြင်ကွင်း",
    chatStudySentenceImageLoading = "မြင်ကွင်းတစ်ခု ဖန်တီးနေသည်…",
    chatStudySentenceImageError = "မြင်ကွင်းရုပ်ပုံ မဖန်တီးနိုင်ပါ။",
    chatStudySentenceImageAlt = "ဤဝါကျအတွက် သရုပ်ဖော်ပုံ",
    chatStudyWordImageLoading = "စကားလုံးမြင်ကွင်း ဖန်တီးနေသည်…",
    chatStudyWordImageError = "စကားလုံးရုပ်ပုံ မဖန်တီးနိုင်ပါ။",
    chatStudyWordImageAlt = "ဤစကားလုံးအတွက် သရုပ်ဖော်ပုံ",
    challengesTitle = "စိန်ခေါ်မှုများ",
    challengesPrompt = "အိမ်နီးချင်းများနှင့် ပျော်စရာ စိန်ခေါ်မှုများ!",
    challengesStartNew = "စိန်ခေါ်မှုအသစ် စတင်မည်",
    challengesLoading = "စိန်ခေါ်မှုများကို ဖွင့်နေသည်…",
    challengesEmpty = "အများပြည်သူ စိန်ခေါ်မှုများ မရှိသေးပါ။ ရပ်ကွက်အတွက် တစ်ခု စတင်လိုက်ပါ။",
    challengesInReview = "စိစစ်နေဆဲ",
    challengesProcessing = "သင့်ဗီဒီယိုကို လုပ်ဆောင်နေသည်…",
    challengesFailed = "လုပ်ဆောင်မှု မအောင်မြင်ပါ",
    challengesYourName = "သင့်အမည်",
    challengesYourNamePlaceholder = "အိမ်နီးချင်းများ သင့်ကို ဘယ်လိုခေါ်ရမလဲ။",
    challengesPickVideo = "ဗီဒီယိုတစ်ခု ရွေးပါ (၅ မိနစ်အထိ)",
    challengesRulesTitle = "စိန်ခေါ်မှု စည်းမျဉ်းများ",
    challengesRulePlace = "သင့်ဗီဒီယိုသည် One Verandah တွင် ရိုက်ကူးထားခြင်း ဖြစ်ရမည်",
    challengesRuleFamily = "မိသားစုနှင့် သင့်တော်သော ဗီဒီယို ဖြစ်သင့်သည်",
    challengesRuleLength = "ဗီဒီယိုကြာချိန်သည် ၂–၄ မိနစ် ရှည်သင့်သည်",
    challengesSubmit = "ဗီဒီယို ပေးပို့မည်",
    challengesUploading = "လုပ်ဆောင်ပြီး တင်နေသည်",
    challengesVote = "မဲပေးမည်",
    challengesVoted = "မဲပေးပြီးပါပြီ",
    challengesVotes = "မဲများ",
    challengesUntitled = "စိန်ခေါ်မှုအသစ်",
    challengesEnter = "ဒီစိန်ခေါ်မှုတွင် ပါဝင်မည်",
    challengesNext = "နောက်ဗီဒီယို",
    challengesPrevious = "ယခင်ဗီဒီယို",
    challengesSubmitError = "ထိုဗီဒီယိုကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    challengesVoteError = "မဲကို သိမ်းဆည်း၍ မရပါ။",
    chatExploreChallenges = "စိန်ခေါ်မှုများ",
    chatExploreChallengesSub = "ရပ်ကွက်အတွင်း ထိပ်ဆုံးနေရာအတွက် ယှဉ်ပြိုင်ပါ",
    chatExploreMeets = "တွေ့ဆုံမှုများ",
    chatExploreMeetsSub = "ကော်ဖီသောက် စကားပြောခြင်းနှင့် အိမ်နီးချင်းအသစ်များ",
    adminChallengesTab = "စိန်ခေါ်မှုများ",
    adminPendingSubmissions = "တင်သွင်းချက်အသစ်များ",
    adminPublishSubmission = "ထုတ်ဝေမည်",
    adminRenameChallenge = "အမည်ပြောင်းမည်",
    adminDeleteChallenge = "စိန်ခေါ်မှု ဖျက်မည်",
    adminDeleteSubmission = "တင်သွင်းချက် ဖျက်မည်",
    adminNoChallenges = "စိန်ခေါ်မှု မရှိသေးပါ။",
    adminNoPending = "စိစစ်ရန် မရှိပါ။",
    meetsTitle = "တွေ့ဆုံမှုများ",
    meetsPrompt = "ကော်ဖီသောက် စကားပြောခြင်း၊ ပူးပေါင်းဆောင်ရွက်ခြင်းနှင့် အိမ်နီးချင်းအသစ်များ။ သင်တွေ့ဆုံလိုသူထံ ကမ်းလှမ်းချက် ပေးပို့ပါ။",
    meetsStartNew = "တွေ့ဆုံမှုတစ်ခု တင်မည်",
    meetsLoading = "တွေ့ဆုံမှုများကို ဖွင့်နေသည်…",
    meetsEmpty = "တွေ့ဆုံမှု မရှိသေးပါ။ ပထမဆုံး နှုတ်ဆက်သူ ဖြစ်လာပါစေ။",
    meetsInReview = "အတည်ပြုချက် စောင့်ဆိုင်းနေသည်",
    meetsYourName = "သင့်အမည်",
    meetsYourNamePlaceholder = "လူများ သင့်ကို ဘယ်လိုခေါ်ရမလဲ။",
    meetsYourPhoto = "သင့်အကောင်းဆုံး ဓာတ်ပုံ",
    meetsPickPhoto = "ဓာတ်ပုံတစ်ခု ရွေးပါ",
    meetsZaloHint = "တွေ့ဆုံမှု အသက်ဝင်နေစဉ် လူတိုင်း မြင်တွေ့နိုင်ပါသည်",
    meetsPitchLimit = "သင့်ထံ လူမည်မျှ ကမ်းလှမ်းနိုင်သလဲ။",
    meetsPitchLimitHint = "ပြီးမြောက်သော တွေ့ဆုံမှုတိုင်းသည် တစ်ခု အသုံးပြုပါသည်",
    meetsPitchLabel = "သင့်ကမ်းလှမ်းချက်",
    meetsPitchPlaceholder = "ဘာအကြောင်း တွေ့ဆုံစကားပြောချင်ပါသလဲ သို့မဟုတ် လုပ်ချင်ပါသလဲ။",
    meetsSubmit = "တွေ့ဆုံမှု တင်မည်",
    meetsUploading = "တင်နေသည်",
    meetsRemaining = { count -> "ကမ်းလှမ်းချက် $count ခု ကျန်ရှိပါသည်" },
    meetsPitchPerson = { name -> "$name ထံ ကမ်းလှမ်းမည်" },
    meetsPitchReplyLabel = "သင့်ကမ်းလှမ်းချက်",
    meetsPitchReplyPlaceholder = "ဘာကြောင့် တွေ့ဆုံသင့်သလဲ။",
    meetsSendPitch = "ကမ်းလှမ်းချက် ပေးပို့မည်",
    meetsPitchSent = "ကမ်းလှမ်းချက် ပေးပို့ပြီးပါပြီ။ ကိုက်ညီပါက သင့် Zalo ကို လွှဲပြောင်းပေးပါမည်။",
    meetsSubmitError = "တွေ့ဆုံမှုကို တင်၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    meetsPitchError = "ကမ်းလှမ်းချက်ကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    meetsNoRemaining = "ဤတွေ့ဆုံမှု ပြည့်သွားပါပြီ",
    meetsUntitled = "တွေ့ဆုံမှုအသစ်",
    meetsCannotPitchOwn = "ဒါက သင့်တွေ့ဆုံမှု ဖြစ်ပါသည်",
    meetsAlreadyPitched = "သင် ကမ်းလှမ်းချက် ပေးပို့ထားပြီးဖြစ်သည်",
    adminMeetsTab = "တွေ့ဆုံမှုများ",
    adminPendingMeets = "တွေ့ဆုံမှုအသစ်များ",
    adminPendingPitches = "ကမ်းလှမ်းချက်အသစ်များ",
    adminApproveMeet = "အတည်ပြုမည်",
    adminCompletePitch = "ပြီးမြောက်ကြောင်း မှတ်သားမည်",
    adminResetRemaining = "ကျန်ရှိအရေအတွက် ပြန်လည်သတ်မှတ်မည်",
    adminResetRemainingHint = "ကမ်းလှမ်းချက် မည်မျှ ကျန်ရှိသင့်သလဲ။",
    adminDeleteMeet = "တွေ့ဆုံမှု ဖျက်မည်",
    adminDeletePitch = "ကမ်းလှမ်းချက် ဖျက်မည်",
    adminNoMeets = "တွေ့ဆုံမှု မရှိသေးပါ။",
    adminNoPendingMeets = "တွေ့ဆုံမှုအသစ် မရှိပါ။",
    adminNoPendingPitches = "ကမ်းလှမ်းချက်အသစ် မရှိပါ။",
    adminMeetZalo = "Zalo",
    adminMeetPosterZalo = "တင်သူ၏ Zalo",
    adminPitchReplyZalo = "ကမ်းလှမ်းသူ၏ Zalo",
    locationsTitle = "နေရာများ",
    locationsPrompt = "အနီးနားရှိ နေရာများကို ရှာဖွေပါ — ကော်ဖီဆိုင်များ၊ စတူဒီယိုများနှင့် စတင်တည်ထောင်နေဆဲ နေရာများ။",
    locationsSearchPlaceholder = "နေရာများ ရှာဖွေရန်",
    locationsAllCategories = "အားလုံး",
    locationsLoading = "နေရာများကို ဖွင့်နေသည်…",
    locationsEmpty = "နေရာများ မရှိသေးပါ။ မကြာမီ ပြန်လာကြည့်ပါ။",
    locationsStatusPlanning = "စီစဉ်နေဆဲ",
    locationsStatusComingSoon = "မကြာမီ လာမည်",
    locationsStatusOpen = "ဖွင့်လှစ်ထားသည်",
    locationsStatusClosed = "ပိတ်ထားသည်",
    locationsClosesAt = { time -> "$time တွင် ပိတ်မည်" },
    locationsOpensAt = { time -> "$time တွင် ဖွင့်မည်" },
    locationsOpensDayAt = { day, time -> "$day $time တွင် ဖွင့်မည်" },
    locationsCommitFunds = "ရန်ပုံငွေ ထည့်ဝင်မည်",
    locationsContact = "ဆက်သွယ်ရန်",
    locationsDirections = "လမ်းညွှန်",
    locationsAmountLabel = "ပမာဏ (VND)",
    locationsAmountPlaceholder = "50.000",
    locationsMessageLabel = "စာတို",
    locationsMessagePlaceholder = "တာဝန်ခံထံ စာတိုပေးပို့ရန်",
    locationsCommitSubmit = "ထည့်ဝင်မည်",
    locationsCommitSent = "ကျေးဇူးတင်ပါသည် — ဤအရာကို လွှဲပြောင်းပေးပါမည်။",
    locationsCommitError = "ထိုထည့်ဝင်မှုကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    locationsContactSubmit = "ပေးပို့မည်",
    locationsContactSent = "ပေးပို့ပြီးပါပြီ။ တာဝန်ခံက Zalo မှတစ်ဆင့် ဆက်သွယ်ပါမည်။",
    locationsContactError = "ထိုစာတိုကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    locationsContactPlaceholder = "နှုတ်ဆက်စကား ပြောပါ",
    locationsLikeError = "ထိုသဘောကျမှုကို သိမ်းဆည်း၍ မရပါ။",
    locationsSuggestTitle = "နေရာတစ်ခု အကြံပြုရန်",
    locationsSuggestHint = "ကျွန်ုပ်တို့ ထည့်သွင်းသင့်သည့် ကော်ဖီဆိုင်၊ စတူဒီယို သို့မဟုတ် ရပ်ကွက်နေရာကို သိပါသလား။",
    locationsSuggestName = "နေရာအမည်",
    locationsSuggestType = "မည်သည့်နေရာအမျိုးအစားလဲ",
    locationsSuggestAddress = "လိပ်စာ သို့မဟုတ် ရှာဖွေရမည့်နည်း",
    locationsSuggestWhy = "အိမ်နီးချင်းများ ဘာကြောင့် သိသင့်သလဲ",
    locationsSuggestSubmit = "ပေးပို့မည်",
    locationsSuggestSuccess = "ပေးပို့ပြီးပါပြီ! ကျွန်ုပ်တို့ လေ့လာကြည့်ရှုပါမည်။",
    locationsSuggestError = "ယခု ပေးပို့၍ မရသေးပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    locationsUntitled = "နေရာအသစ်",
    locationsDayMon = "တနင်္လာ",
    locationsDayTue = "အင်္ဂါ",
    locationsDayWed = "ဗုဒ္ဓဟူး",
    locationsDayThu = "ကြာသပတေး",
    locationsDayFri = "သောကြာ",
    locationsDaySat = "စနေ",
    locationsDaySun = "တနင်္ဂနွေ",
    chatExploreLocations = "နေရာများ",
    chatExploreLocationsSub = "အနီးနားရှိ နေရာများ",
    adminLocationsTab = "နေရာများ",
    adminAddLocation = "နေရာ ထည့်မည်",
    adminEditLocation = "ပြင်ဆင်မည်",
    adminDeleteLocation = "နေရာ ဖျက်မည်",
    adminNoLocations = "နေရာ မရှိသေးပါ။",
    adminLocationName = "အမည်",
    adminLocationType = "အမျိုးအစား",
    adminLocationCategory = "ကဏ္ဍ",
    adminLocationAddress = "လိပ်စာ",
    adminLocationStatus = "အခြေအနေ",
    adminLocationHostZalo = "တာဝန်ခံ Zalo",
    adminLocationMapsUrl = "Maps URL",
    adminLocationHighlights = "ပေါ်လွင်ချက်များ",
    adminLocationPhotos = "ဓာတ်ပုံများ",
    adminLocationAddPhoto = "ဓာတ်ပုံများ ထည့်မည်",
    adminLocationSchedule = "အပတ်စဉ် ဖွင့်ချိန် (UTC+7)",
    adminLocationClosedDay = "ပိတ်သည်",
    adminLocationApplyBelow = "အောက်တွင် လျှောက်ထားပါ",
    adminLocationContent = "အကြောင်းအရာ",
    adminLocationAddText = "စာသား ထည့်မည်",
    adminLocationAddImage = "ရုပ်ပုံ ထည့်မည်",
    adminLocationCommitments = "ရန်ပုံငွေ ကတိကဝတ်များ",
    adminLocationContacts = "စာတိုများ",
    clubsTitle = "ကလပ်များ",
    clubsPrompt = "ကလပ်များကို ရှာဖွေပါ — တစ်ခုခုတွင် ပါဝင်ပါ၊ သို့မဟုတ် ကိုယ်ပိုင်ကလပ် စတင်ပါ။",
    clubsSearchPlaceholder = "ကလပ်များ ရှာဖွေရန်",
    clubsAllCategories = "အားလုံး",
    clubsLoading = "ကလပ်များကို ဖွင့်နေသည်…",
    clubsEmpty = "ကလပ်များ မရှိသေးပါ။ မကြာမီ ပြန်လာကြည့်ပါ။",
    clubsUntitled = "ကလပ်အသစ်",
    clubsCreateTitle = "ကလပ်တစ်ခု ဖွဲ့စည်းရန်",
    clubsCreateHint = "အမည်၊ ဆောင်ပုဒ်၊ ဖော်ပြချက်နှင့် သင့်ထံ ဆက်သွယ်ရမည့်နည်းလမ်းကို ရိုက်ထည့်ပါ။ အခြားအသေးစိတ်များကို ကလပ်ဖွဲ့စည်းပြီးနောက် ထည့်သွင်းပါမည်။",
    clubsCreateName = "ကလပ်အမည်",
    clubsCreateTagline = "ဆောင်ပုဒ်",
    clubsCreateDescription = "ဖော်ပြချက်",
    clubsCreateHostName = "သင့်အမည်",
    clubsCreateSubmit = "ကလပ်တစ်ခု ဖွဲ့စည်းမည်",
    clubsCreateSuccess = "ကလပ်တောင်းဆိုချက် ပေးပို့ပြီးပါပြီ။ မကြာမီ ဆက်သွယ်ပါမည်။",
    clubsCreateError = "ကလပ်တောင်းဆိုချက်ကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    clubsJoin = "ကလပ်တွင် ပါဝင်မည်",
    clubsJoinSubmit = "ကလပ်တွင် ပါဝင်မည်",
    clubsJoinSent = "ကျေးဇူးတင်ပါသည်! တာဝန်ခံက Zalo မှတစ်ဆင့် ဆက်သွယ်ပါမည်!",
    clubsJoinError = "ပါဝင်ရန် တောင်းဆိုချက်ကို ပေးပို့၍ မရပါ။ ထပ်မံကြိုးစားကြည့်ပါ။",
    clubsYourName = "သင့်အမည်",
    clubsYourMessage = "သင့်စာတို",
    clubsYourMessagePlaceholder = "တာဝန်ခံကို သိစေလိုသည့် မည်သည့်အရာမဆို",
    clubsHostedBy = "ဦးဆောင်သူ -",
    clubsDetails = "အသေးစိတ်",
    adminClubsTab = "ကလပ်များ",
    adminAddClub = "ကလပ် ထည့်မည်",
    adminEditClub = "ကလပ် ပြင်ဆင်မည်",
    adminDeleteClub = "ကလပ် ဖျက်မည်",
    adminNoClubs = "ကလပ် မရှိသေးပါ။",
    adminClubName = "အမည်",
    adminClubTagline = "ဆောင်ပုဒ်",
    adminClubDescription = "ဖော်ပြချက်",
    adminClubCategory = "ကဏ္ဍ",
    adminClubActivities = "လှုပ်ရှားမှုများ",
    adminClubHostName = "ဦးဆောင်သူ အမည်",
    adminClubHostZalo = "ဦးဆောင်သူ Zalo",
    adminClubPhotos = "ဓာတ်ပုံများ",
    adminClubAddPhoto = "ဓာတ်ပုံများ ထည့်မည်",
    adminClubContent = "အကြောင်းအရာ",
    adminClubAddText = "စာသား ထည့်မည်",
    adminClubAddImage = "ရုပ်ပုံ ထည့်မည်",
    adminClubJoinQuestions = "ပါဝင်ခွင့် မေးခွန်းများ",
    adminClubCreateRequests = "ကလပ်ဖွဲ့စည်းရန် တောင်းဆိုချက်များ",
    adminClubJoinRequests = "ကလပ်ဝင်ရန် တောင်းဆိုချက်များ",
    adminNoClubCreateRequests = "ကလပ်ဖွဲ့စည်းရန် တောင်းဆိုချက် မရှိပါ။",
    adminNoClubJoinRequests = "ကလပ်ဝင်ရန် တောင်းဆိုချက် မရှိပါ။",
    adminDismissClubRequest = "ပယ်ဖျက်မည်",
    adminDismissClubJoin = "ပယ်ဖျက်မည်",
    adminStatsTab = "စာရင်းအင်း",
    adminStatsHint = "ဗီယက်နမ်စံတော်ချိန် (UTC+7) အရ နေ့အလိုက် လာရောက်သူများ။ ဒေါင်လိုက်တိုင်များသည် လာရောက်သူများဖြစ်ပြီး မျဉ်းကွေးသည် လည်ပတ်မှုများ ဖြစ်သည်။",
    adminStatsLoading = "စာရင်းအင်းများကို ဖွင့်နေသည်…",
    adminStats7Days = "၇ ရက်",
    adminStats30Days = "ရက် ၃၀",
    adminStatsVisitors = "လာရောက်သူများ",
    adminStatsVisits = "လည်ပတ်မှုများ",
    adminStatsNewVisitors = "လာရောက်သူအသစ်များ",
    adminStatsToday = "ယနေ့",
    adminStatsLast7Days = "လွန်ခဲ့သော ၇ ရက်",
    adminStatsLast30Days = "လွန်ခဲ့သော ရက် ၃၀",
    adminStatsAllTime = "အချိန်အားလုံး",
    adminStatsTopScreens = "လူကြည့်အများဆုံး မျက်နှာပြင်များ",
    adminStatsLanguages = "ဘာသာစကားများ",
    adminStatsNoData = "လည်ပတ်မှု မှတ်တမ်း မရှိသေးပါ။",
    adminStatsTimezone = "အချိန်သည် UTC+7 ဖြစ်သည်",
    adminStatsSelected = "ရွေးချယ်ထားသော နေ့",
)

fun stringsFor(language: Language): UiStrings = when (language) {
    Language.ENGLISH -> English
    Language.VIETNAMESE -> Vietnamese
    Language.CHINESE -> Chinese
    Language.JAPANESE -> Japanese
    Language.KOREAN -> Korean
    Language.RUSSIAN -> Russian
    Language.SPANISH -> Spanish
    Language.GERMAN -> German
    Language.BURMESE -> Burmese
}

/** Returns the flag emoji used to represent [language] in the UI. */
fun flagFor(language: Language): String = when (language) {
    Language.ENGLISH -> "\uD83C\uDDFA\uD83C\uDDF8" // 🇬🇧
    Language.VIETNAMESE -> "\uD83C\uDDFB\uD83C\uDDF3" // 🇻🇳
    Language.CHINESE -> "\uD83C\uDDE8\uD83C\uDDF3" // 🇨🇳
    Language.JAPANESE -> "\uD83C\uDDEF\uD83C\uDDF5" // 🇯🇵
    Language.KOREAN -> "\uD83C\uDDF0\uD83C\uDDF7" // 🇰🇷
    Language.RUSSIAN -> "\uD83C\uDDF7\uD83C\uDDFA" // 🇷🇺
    Language.SPANISH -> "\uD83C\uDDEA\uD83C\uDDF8" // 🇪🇸
    Language.GERMAN -> "\uD83C\uDDE9\uD83C\uDDEA" // 🇩🇪
    Language.BURMESE -> "\uD83C\uDDF2\uD83C\uDDF2" // 🇲🇲
}

/**
 * Holds the currently selected [Language] as Compose state so the whole UI
 * recomposes when it changes.
 *
 * The initial value is derived from the browser: if the user's preferred
 * language matches a supported language we use it, otherwise we fall back
 * to English.
 */
object Localization {
    var language: Language by mutableStateOf(detectBrowserLanguage())
        private set

    val strings: UiStrings get() = stringsFor(language)

    fun setLanguage(newLanguage: Language) {
        language = newLanguage
    }
}

/**
 * Inspects the browser's preferred languages and returns the first matching
 * supported language, otherwise English.
 *
 * `navigator.languages` is checked first (ordered by user preference), then we
 * fall back to the single `navigator.language` value.
 */
private fun detectBrowserLanguage(): Language {
    val navigator = window.navigator
    val candidates: List<String> = buildList {
        navigator.languages.forEach { add(it) }
        navigator.language.let { if (it.isNotBlank()) add(it) }
    }
    for (tag in candidates) {
        val lang = Language.fromTag(tag)
        if (lang != Language.DEFAULT) return lang
    }
    return Language.DEFAULT
}
